/**
 * Shared messages for the customer frontend — the panel and the Agent module.
 *
 * Every panel string is a tuple `[en, zh]`, which is the whole point: the
 * compiler requires both, so a string cannot be added in one language and
 * forgotten in the other. A framework with per-locale JSON files makes that
 * failure silent and only visible to whoever happens to browse in the
 * neglected language.
 *
 * `t` reads two tables, because the two trees name their strings differently.
 * The panel invents a key — `t("members.invite")`. The Agent passes the English
 * itself — `t("Invite")` — because ~3,300 literals were written that way before
 * the Agent was Ondit's, and giving each one a key would rewrite all of them for
 * no reader's benefit. `AGENT_COPY` is that second table; see the file for what
 * the shape costs.
 *
 * Those two are the only tables. A screen does not keep one of its own, and
 * text the server already sends in both languages — a plan's name, a platform
 * field's label — is chosen with `pick` rather than an inline `locale === "zh"`.
 *
 * Which table an argument goes to is decided by *membership*, not by looking
 * at the string: the keyed table first, the source-string table second, and on
 * a miss in both the argument comes back unchanged. That ordering is what
 * keeps the panel exactly as it was — a panel key with no entry still warns in
 * development and still renders as the key — while an Agent string with no
 * translation renders in English, which is what an untranslated Agent screen
 * does today. `KEY_SHAPE` is consulted only to decide which of those two
 * failures a miss was, so that 3,300 untranslated English strings do not
 * drown the one real missing key in the console.
 */

import { type ReactNode, useContext, useEffect, useMemo } from "react";
import { AGENT_COPY } from "~/core/i18n/agent-copy";
import { Context, type I18n } from "~/core/i18n/context";
import { pick } from "~/core/i18n/pick";
import { registryCopy, useRegistryCopyVersion } from "~/core/i18n/registry-copy";

export const LOCALES = ["en", "zh"] as const;
export type Locale = (typeof LOCALES)[number];
export const LOCALE_COOKIE = "ONDIT_LOCALE";

type Vars = Record<string, string | number>;

const T: Record<string, [string, string]> = {
	"distribution.method.browser": ["Browser assistant", "浏览器辅助"],
	"distribution.method.api": ["API", "API"],
	"distribution.method.manual": ["Manual transfer", "手动转移"],

	// -- the browser assistant and WordPress tasks, one article to one platform
	"publishFlow.recorded": ["Publishing result saved", "发布结果已保存"],
	"publishFlow.resume": ["Continue", "继续发布"],
	"publishFlow.intro": [
		"Choose an article and a platform. Check the content here, then publish in your own account.",
		"选择文章和平台，在这里检查内容，再用你自己的账号发布。",
	],
	"publishFlow.steps": ["Publishing steps", "发布步骤"],
	"publishFlow.choose": ["Choose content", "选择内容"],
	"publishFlow.check": ["Review content", "检查内容"],
	"publishFlow.publish": ["Publish and save link", "发布并保存链接"],
	"publishFlow.chooseHeading": ["What would you like to publish, and where?", "你想把哪篇文章发布到哪里？"],
	"publishFlow.article": ["Article to share", "要发布的文章"],
	"publishFlow.destination": ["Publish to", "发布到"],
	"publishFlow.selectPlatform": ["Choose a platform", "选择一个平台"],
	"publishFlow.noArticle": [
		"Publish an article on Ondit first. It will appear here for sharing.",
		"先在 Ondit 发布一篇文章，即可在这里选择并分发。",
	],
	"publishFlow.continue": ["Next: review content", "下一步：检查内容"],
	"publishFlow.changeSelection": ["Change article or platform", "更换文章或平台"],
	"publishFlow.recommend": ["Help me choose a platform", "帮我选平台"],
	"publishFlow.checkFields": [
		"Complete the required fields and fix the marked content before continuing.",
		"请补全必填项，并检查标出的内容后继续。",
	],
	"publishFlow.review": ["Check your {platform} post", "检查要发布到 {platform} 的内容"],
	"publishFlow.finish": ["Finish publishing on {platform}", "在 {platform} 完成发布"],
	"publishFlow.reviewHelp": [
		"The text is prepared from your article. Edit it as needed; your original article stays unchanged.",
		"已根据原文准备好内容。你可以修改这份平台副本，不会改动原文。",
	],
	"publishFlow.apiMethod": [
		"Connect an account to send from Ondit. The platform's own answer becomes the receipt.",
		"连接账号后可直接从 Ondit 发送，平台自己的回答就是回执。",
	],
	"publishFlow.bothMethods": [
		"Connect an account to send from Ondit. The browser assistant stays available for what the platform's API does not carry.",
		"连接账号后可直接从 Ondit 发送；平台接口带不了的部分，仍可用浏览器助手。",
	],
	"publishFlow.browserMethod": [
		"The browser assistant brings your text to this platform. You check it and click Publish there.",
		"浏览器助手会帮你把内容带到这个平台，你核对后在平台点击发布。",
	],
	"publishFlow.manualMethod": [
		"This platform uses manual publishing. Copy the prepared content, open the platform, and save the published link here.",
		"这个平台需要手动发布。复制准备好的内容，打开平台发布后，在这里保存链接。",
	],
	"publishFlow.tooLong": [
		"Over the limit by {count}. Shorten this text to continue.",
		"超出平台字数限制 {count}，请精简后继续。",
	],
	"publishFlow.fieldRequired": ["This field is required.", "请填写此必填项。"],
	"publishFlow.extension.checking": ["Checking your publishing assistant…", "正在检查发布助手…"],
	"publishFlow.extension.connected": ["Publishing assistant connected", "发布助手已连接"],
	"publishFlow.extension.outdated": ["Update your publishing assistant", "发布助手有新版本"],
	"publishFlow.upgradeHelp": [
		"Install version {version}, then reload this page.",
		"请安装 {version} 版本，然后刷新此页面。",
	],
	"publishFlow.schedule": ["Schedule automatic publishing", "定时自动发布"],
	"publishFlow.scheduleAt": ["Publish at (your local time)", "发布时间（当前时区）"],
	"publishFlow.scheduleLimits": [
		"The assistant verifies the signed-in account, then fills and publishes at the scheduled time; a delay over 15 minutes stops for your review. Media up to 20 MB total; YouTube public videos; X and LinkedIn images; DEV Markdown with embedded images. Options needing manual input cannot be scheduled.",
		"助手会先识别平台的登录账号，到时间后自动填入并发布；延迟超过 15 分钟会停下等你处理。素材合计不超过 20 MB；YouTube 公开视频；X、LinkedIn 图片；DEV 可在 Markdown 中插图。需要手动填写的平台选项暂不支持定时。",
	],
	"publishFlow.scheduleFields": ["These fields need manual handling: {fields}", "以下内容需要手动处理：{fields}"],
	"publishFlow.scheduleHelp": [
		"Keep this browser open and the computer awake until then.",
		"到时间前请保持此浏览器运行、电脑唤醒。",
	],
	"publishFlow.schedulePending": [
		"Continue in the assistant to verify the account and save your schedule.",
		"请在发布助手中完成账号检查；检查通过后才会保存定时任务。",
	],
	"publishFlow.scheduleSaved": ["Automatic publishing scheduled: {time}", "已安排自动发布：{time}"],
	"publishFlow.scheduleCancel": ["Cancel automatic publishing", "取消定时发布"],
	"publishFlow.scheduleCancelled": ["Automatic publishing cancelled", "已取消定时发布"],
	"publishFlow.scheduleAttention": [
		"Automatic publishing stopped. Check the platform before creating a new task.",
		"自动发布已停止，请先到平台核对结果，再创建新任务。",
	],
	"publishCalendar.scheduled": ["Automatic", "定时自动发布"],

	"publishFlow.extension.missing": ["Connect your browser first", "先连接你的浏览器"],
	"publishFlow.connectedHelp": [
		"The next button opens this post in the assistant, ready to continue.",
		"点击下方发布按钮，即可打开助手并继续处理这篇内容。",
	],
	"publishFlow.installHelp": [
		"Chrome or Firefox with the Ondit assistant installed; reload this page after installing it.",
		"需在装有 Ondit 助手的 Chrome 或 Firefox 中打开；安装后请刷新此页。",
	],
	"publishFlow.recheck": ["Check connection again", "重新检查连接"],
	"publishFlow.connectionLost": [
		"The assistant did not respond. Your content is saved. Check the extension, reload this page, and try again.",
		"助手没有响应，内容已保存。请检查插件并刷新页面，再试一次。",
	],
	"publishFlow.go": ["Open {platform} and fill in", "打开 {platform} 并填写"],
	"publishFlow.openEditor": ["Open full publishing page", "打开完整发布页"],
	"publishFlow.otherMethods": ["Other ways to publish", "其他发布方式"],
	"publishFlow.reopen": ["Reopen publishing assistant", "重新打开发布助手"],
	"publishFlow.working": ["Preparing…", "正在准备…"],
	"publishFlow.inAssistant": ["Sign in to the platform in the assistant", "在助手中登录平台账号"],
	"publishFlow.onPlatform": ["Fill your content and publish there", "填入内容并在平台发布"],
	"publishFlow.returnLink": ["Paste the published link back in the assistant", "把发布链接粘贴回助手"],
	"publishFlow.linkLabel": ["Already published? Save the link", "已经发布了？保存链接"],
	"publishFlow.linkHelp": [
		"Paste the public link here, or let the assistant bring it back. A saved link records your confirmation; it is not an API verification.",
		"粘贴发布后的公开链接。保存后会标记为你已确认发布，Ondit 尚未核验公开页面。",
	],
	"publishFlow.openResult": ["View published content", "查看发布内容"],
	"publishFlow.edit": ["Back to editing", "返回编辑"],
	"publishFlow.preview": ["Show preview", "查看预览"],
	"publishFlow.optional": ["Images and more settings (optional)", "图片与更多设置（可选）"],
	"publishFlow.mediaSettings": ["Required media and platform settings", "必需素材与平台设置"],
	"publishFlow.loadFailed": [
		"Could not load your saved publishing progress. Refresh before continuing.",
		"未能加载已保存的发布进度，请刷新后继续。",
	],
	"publishFlow.manualRecords": ["Manually recorded posts", "手动记录的发布"],
	"publishFlow.tab.prepare": ["Publish content", "发布内容"],
	"publishFlow.tab.history": ["Publishing progress", "发布进度"],
	"publishFlow.tab.platforms": ["Platform reference", "平台资料"],
	"publishing.platform.alt": ["Alternative text", "替代文本"],
	"publishing.platform.methods": ["Publishing method", "发布方式"],
	"publishing.platform.preview": ["Platform preview", "平台预览"],
	"publishing.platform.previewHelp": [
		"Preview follows this platform’s field structure. Final rendering and eligibility are determined by the platform.",
		"预览对应平台字段；最终排版和可发布条件以平台为准。",
	],
	"publishing.platform.handoff": ["Send to browser extension", "发送到浏览器插件"],
	"publishing.platform.install": [
		"Open the Ondit extension to select a task and fill the platform editor. Keep this tab open to sync the result.",
		"打开 Ondit 插件选择任务并填充平台编辑器；保留此页面以同步结果。",
	],
	"publishing.platform.missing": [
		"The extension did not respond. Install or enable it, then send the saved task again.",
		"插件未响应，请安装或启用插件后重新发送已保存的任务。",
	],
	"publishing.platform.guided": ["Choose this setting on the platform", "请在平台确认此设置"],
	"publishing.platform.automatic": [
		"Automatic field matching; select a field if matching fails",
		"自动匹配字段；匹配失败时可点选目标字段",
	],
	"publishing.platform.media": ["Media URLs (one HTTPS URL per line)", "素材网址（每行一个 HTTPS 网址）"],
	"publishing.platform.mediaHelp": [
		"Media is included in the handoff. Upload files and choose cover/crop settings in the platform editor.",
		"素材随任务传递；请在平台编辑器上传文件并选择封面与裁剪。",
	],
	"publishing.platform.mediaRequired": ["Image or video required", "需要图片或视频"],
	"publishing.platform.required": ["Required", "必填"],
	"publishing.platform.save": ["Save draft", "保存草稿"],
	"publishing.platform.saved": ["Task saved", "任务已保存"],
	"publishing.platform.savedToExtension": [
		"Task sent. Open the extension to continue.",
		"任务已发送，请打开插件继续。",
	],
	"publishing.platform.publish": ["Send to {platform}", "提交到 {platform}"],
	"publishing.platform.publishConfirm": [
		"Send the displayed version to {platform} with the settings shown? Ondit cannot take it back.",
		"按当前设置把这一版提交到 {platform}？Ondit 无法撤回。",
	],
	"publishing.platform.account": ["Account", "账号"],
	"publishing.platform.connectAccount": ["Connect an account", "连接账号"],
	"publishing.platform.history": ["Publishing tasks", "发布任务"],
	"publishing.platform.noneTitle": ["No publishing tasks yet", "还没有发布任务"],
	"publishing.platform.none": [
		"Select an article and a platform above to prepare one.",
		"在上方选择文章和平台即可准备一个。",
	],
	"publishing.platform.verify": ["Confirm publication link", "确认发布链接"],
	"publishing.platform.result": ["Published link", "发布链接"],
	"publishing.platform.invalid": [
		"Complete required fields and fix length / URL errors.",
		"请补齐必填字段，并修正长度或网址错误。",
	],
	"publishing.platform.invalidField": ["Check this field", "请检查此字段"],
	"publishing.platform.tooShort": ["Use at least {count} characters.", "至少需要 {count} 个字符。"],
	"publishing.platform.mediaMin": ["At least {count} files", "至少 {count} 个文件"],
	"publishing.platform.mediaVideoAlone": ["a video must be the only file", "视频只能单独发布"],
	"publishing.platform.mediaNoVideo": ["images only", "仅支持图片"],
	"publishing.platform.compatibility": [
		"Automatic matching needs verification against your account’s current editor; guided field selection remains available.",
		"自动匹配需在你的账号当前编辑器中验证；也可使用点选字段填充。",
	],
	"publishing.platform.assets": ["Media", "素材"],
	"publishing.platform.confirm": ["Confirm", "确认"],
	"publishing.platform.state.ready": ["Draft saved; ready to continue", "草稿已保存，待继续发布"],
	"publishing.platform.state.filled": ["Content filled; publish on the platform", "内容已填入，请在平台发布"],
	"publishing.platform.state.user_reported": ["Published link saved", "发布链接已保存"],
	"publishing.platform.state.sending": ["Submitting; do not repeat", "提交中，请勿重复"],
	"publishing.platform.state.published": ["Platform confirmed publication", "平台已确认发布"],
	"publishing.platform.state.remote_draft": ["Saved remotely; not confirmed public", "已保存到平台，未确认公开"],
	"publishing.platform.state.unknown": [
		"Result uncertain; check the platform before another submission",
		"结果不确定，请先核查平台，勿重复提交",
	],
	"publishing.platform.state.failed": ["Action failed", "操作失败"],
	"publishing.platform.state.cancelled": ["Cancelled", "已取消"],

	// -- installing the browser assistant
	"publishFlow.tab.calendar": ["Publishing calendar", "发布日历"],
	"publishCalendar.title": ["Publishing calendar", "发布日历"],
	"publishCalendar.linkUrl": ["Public post URL", "平台上的公开链接"],
	"publishCalendar.result": ["Published link", "发布链接"],
	"publishCalendar.new": ["Plan a post", "安排发布"],
	"publishCalendar.setTime": ["Set publishing time", "设置计划时间"],
	"publishCalendar.plannedTime": ["Planned publishing time", "计划发布时间"],
	"publishCalendar.removeTime": ["Remove from calendar", "取消排期"],
	"publishCalendar.details": ["Post details", "发布详情"],
	"publishCalendar.lockedContent": [
		"This publication snapshot is locked. Cancel a waiting automatic schedule before editing its content; completed and running publications remain read-only.",
		"此发布快照已锁定。修改内容前请先取消等待中的自动任务；已发布或执行中的内容仅可查看。",
	],
	"publishCalendar.allPlatforms": ["All platforms", "全部平台"],
	"publishCalendar.previous": ["Previous period", "上一段时间"],
	"publishCalendar.next": ["Next period", "下一段时间"],
	"publishCalendar.today": ["Today", "今天"],
	"publishCalendar.day": ["Day", "日"],
	"publishCalendar.week": ["Week", "周"],
	"publishCalendar.month": ["Month", "月"],
	"publishCalendar.view": ["Calendar view", "日历视图"],
	"publishCalendar.timezone": ["Times shown in {zone}", "时间按 {zone} 显示"],
	"publishCalendar.help": [
		"A planned time is a reminder — you still publish it yourself. Automatic schedules run in your connected browser.",
		"手动计划只是提醒，仍需你自己发布；定时任务由连接的浏览器执行。",
	],
	"publishCalendar.prepareHelp": [
		"Open the article to prepare the content for this platform.",
		"打开文章，为这个平台准备发布内容。",
	],
	"publishCalendar.empty": [
		"No posts in this period. Click a time slot to plan a post.",
		"这段时间还没有发布安排，点击时间格即可排期。",
	],
	"publishCalendar.unscheduled": ["Not scheduled", "未排期"],
	"publishCalendar.noDrafts": ["All saved posts have a date.", "没有未排期的内容。"],
	"publishCalendar.dragHelp": ["Drag a post onto a slot to schedule it.", "把内容拖到日历的时间格即可排期。"],
	"publishCalendar.planned": ["Unconfirmed", "待确认"],
	"publishCalendar.awaiting": ["{count} planned — none sent until you confirm", "已排期 {count} 篇，确认后才会发出"],
	"publishCalendar.confirm": ["Confirm and publish", "去确认发布"],
	"publishCalendar.willNotSend": [
		"This is a plan, not a schedule: the day is a reminder and nothing leaves on its own.",
		"这只是计划，不是定时任务：日期是提醒，不会自动发出。",
	],
	"publishCalendar.draft": ["Draft", "草稿"],
	"publishCalendar.recorded": ["Published result recorded", "已记录发布结果"],
	"publishCalendar.addOn": ["Plan a post on {day}", "在 {day} 安排发布"],
	"publishing.install.return": ["Return to publishing", "返回发布"],
	"publishing.install.title": ["Install publishing assistant", "安装发布助手"],
	"publishing.install.intro": [
		"Install once in the browser where you use your publishing accounts. Your platform sign-in stays in that browser.",
		"请安装到日常使用发布账号的浏览器。平台登录状态保留在该浏览器中。",
	],
	"publishing.install.chrome": ["Download Chrome extension", "下载 Chrome 插件"],
	"publishing.install.chromeHelp": [
		"Extract the ZIP. Open chrome://extensions, enable Developer mode, choose Load unpacked and select the extracted folder.",
		"解压 ZIP，在 chrome://extensions 开启开发者模式，选择“加载已解压的扩展程序”，选中解压目录。",
	],
	"publishing.install.firefox": ["Download Firefox extension", "下载 Firefox 插件"],
	"publishing.install.firefoxHelp": [
		"Firefox 140+: extract the ZIP, open about:debugging#/runtime/this-firefox, choose Load Temporary Add-on and select manifest.json. This development install lasts until Firefox restarts.",
		"Firefox 140 及以上：解压后打开 about:debugging#/runtime/this-firefox，点击“临时载入附加组件”，选择 manifest.json。开发版临时安装在重启后失效。",
	],
	"publishing.install.use": ["Reload this page once it is installed.", "安装完成后请刷新本页。"],

	// -- written into a post in the article's language, not the reader's
	"distribution.originalArticle": ["Original article", "原文"],
	"distribution.narrationDraft": ["Narration draft (recording and media required)", "口播脚本草稿（需录制与制作素材）"],

	"errors.conflict.publishing_in_flight": [
		"This brand has submissions awaiting remote confirmation. Reconcile them before deleting the brand.",
		"此品牌还有等待远端确认的投递，请先核对后再删除品牌。",
	],
	"errors.publishing_not_connected": ["Connect the publishing organization first.", "请先连接发布组织。"],
	"errors.publishing_not_configured": ["The operator must configure the publishing service.", "请先配置发布服务。"],
	"errors.publishing_invalid": [
		"Check the required fields, source and publication date.",
		"请检查必填字段、原文和发布时间。",
	],
	"errors.publishing_source_required": [
		"Keep the exact original article URL in the content.",
		"文案必须保留准确的原文链接。",
	],
	"errors.publishing_contract_unavailable": [
		"Sync the channel requirements before preparing a delivery.",
		"请先同步渠道要求。",
	],
	"errors.publishing_too_long": ["This post exceeds the channel length limit.", "文案超过渠道字数限制。"],
	"errors.publishing_settings_invalid": [
		"Complete the required channel settings with valid values.",
		"请正确填写渠道的必需设置。",
	],
	"errors.publishing_media_unknown_size": [
		"The media address must state its file size. Re-upload it or use a direct link.",
		"素材地址未声明文件大小，请重新上传或改用直链。",
	],
	"errors.publishing_media_too_large": [
		"This media file is too large for the platform's upload.",
		"素材文件超过该平台上传限制。",
	],
	"errors.publishing_remote_required": ["Enter the exact remote post identifier.", "请输入准确的远端帖子编号。"],
	"errors.publishing_remote_not_found": [
		"No matching post was found for this account and period.",
		"未在此账号和时间范围内找到匹配帖子。",
	],

	"errors.publishing_media_invalid": [
		"Choose a PNG, JPEG, WebP or MP4 file smaller than 50 MB.",
		"请选择不超过 50 MB 的 PNG、JPEG、WebP 或 MP4 文件。",
	],
	"errors.publishing_media_expired": [
		"An attachment belongs to an old connection. Create a new package with current assets.",
		"素材所属渠道连接已更换，请使用当前连接的素材创建新的内容包。",
	],
	"publishing.legacy.title": ["Archived deliveries", "历史投递记录"],
	"publishing.legacy.help": [
		"The previous delivery service has been retired. These records are read-only. Verify unknown or submitted results on the destination platform before publishing again.",
		"旧投递服务已停用，以下记录仅供查阅。结果未知或已提交的内容，请先到目标平台核对，避免重复发布。",
	],
	"publishing.legacy.remote": ["Remote post ID", "远端帖子编号"],
	"publishing.title": ["Publishing workspace", "发布工作台"],
	"publishing.empty": ["Choose a published article to prepare a platform copy.", "选择已发布文章，准备平台副本。"],
	"publishing.confirmed": ["confirmed", "已确认发布"],
	"errors.conflict.publishing_changed": [
		"The source, account, schedule or approval changed. Reload and prepare a new reviewed version.",
		"原文、账号、排期或审批状态已变化，请刷新并准备新的审阅版本。",
	],
	"publishing.state.draft": ["Draft", "草稿"],
	"publishing.state.approved": ["Approved", "已批准"],
	"publishing.state.queued": ["Scheduled", "已排期"],
	"publishing.state.sending": ["Submitting", "正在提交"],
	"publishing.state.submitted": ["Submitted, awaiting confirmation", "已提交，待确认"],
	"publishing.state.published": ["Published", "已发布"],
	"publishing.state.failed": ["Failed", "失败"],
	"publishing.state.unknown": ["Result unknown", "结果未知"],
	"publishing.state.paused": ["Needs attention", "需要处理"],
	"publishing.state.cancelled": ["Cancelled", "已取消"],

	"nav.plans": ["Content plans", "内容计划"],
	"jobs.studio.generate": ["Generate media", "生成素材"],
	"jobs.studio.render": ["Render content", "合成素材"],
	"errors.conflict.page_revision_stale": [
		"This content changed. Reload before saving.",
		"内容已更新，请重新载入后保存。",
	],
	"reading.studio": ["Content studio", "素材制作"],
	"reading.plans": ["Content plans", "内容计划"],
	"plans.lede": [
		"Turn a goal into content, publishing and a clear next step.",
		"围绕一个目标，连接内容创作、发布与结果观察。",
	],
	"plans.prepareRetry": ["Prepare another generation attempt", "重新准备生成"],
	"plans.runAt": ["Run at", "运行时间"],
	"plans.runAtHint": [
		"Local time. Leave empty to start when the brand is available.",
		"本地时间。留空则在品牌可运行时开始。",
	],
	"plans.generationScheduled": ["Generation scheduled", "已安排生成"],
	"plans.execution": ["Execution", "执行状态"],
	"plans.articleLoadFailed": [
		"Articles could not be loaded. Reopen this action to retry.",
		"文章加载失败，请重新打开此行动重试。",
	],
	"plans.new": ["New content plan", "创建内容计划"],
	"plans.empty": ["What would you like to improve?", "你希望改善什么？"],
	"plans.emptyBody": [
		"Start with a goal, or create a plan from an observed gap. Keep the evidence, content and outcomes together.",
		"从一个目标开始，也可以从检测到的缺口创建计划。证据、内容和结果会保留在同一个工作空间。",
	],
	"plans.title": ["Plan name", "计划名称"],
	"plans.objective": ["What should this achieve?", "这次希望解决什么问题？"],
	"plans.audience": ["Who is this for?", "目标受众"],
	"plans.language": ["Content language", "内容语言"],
	"plans.titlePlaceholder": ["Explain our product for small teams", "让小团队了解我们的产品"],
	"plans.objectivePlaceholder": [
		"Which question should our content answer, or what needs to change?",
		"内容需要回答哪个问题，或改善什么现状？",
	],
	"plans.audiencePlaceholder": ["For example, support leads at small businesses", "例如：小企业的客服负责人"],
	"plans.createHelp": ["Creating a plan does not run AI or publish anything.", "创建计划不会调用模型或发布内容。"],
	"plans.search": ["Search plans", "搜索计划"],
	"plans.all": ["All plans", "全部计划"],
	"plans.active": ["In progress", "进行中"],
	"plans.paused": ["Paused", "已暂停"],
	"plans.completed": ["Completed", "已完成"],
	"plans.progress": ["{done} of {total} actions recorded", "已记录 {done} / {total} 项行动"],
	"plans.back": ["All content plans", "全部内容计划"],
	"plans.next": ["Your next step", "接下来做什么"],
	"plans.actions": ["Plan actions", "计划行动"],
	"plans.evidence": ["Why this plan?", "这个计划从何而来？"],
	"plans.noEvidence": [
		"This plan starts with your goal. Add observations before drawing conclusions about improvement.",
		"此计划从你设定的目标出发。判断改善效果前，需要检测证据与基线。",
	],
	"plans.history": ["Activity", "操作记录"],
	"plans.noResults": ["No outcome recorded yet.", "还没有记录结果。"],
	"plans.outcome": ["Outcome or next step", "结果或下一步"],
	"plans.outcomeHint": [
		"Record what you checked, learned or completed. A note is not proof of publishing or improved visibility.",
		"记录检查了什么、发现了什么或完成了什么。备注不代表已验证发布或可见度改善。",
	],
	"plans.saveNote": ["Save note", "保存备注"],
	"plans.finish": ["Record as complete", "记录为已完成"],
	"plans.reopen": ["Reopen", "重新处理"],
	"plans.pause": ["Pause plan", "暂停计划"],
	"plans.resume": ["Resume plan", "恢复计划"],
	"plans.close": ["Complete plan", "完成计划"],
	"plans.add": ["Add action", "添加行动"],
	"plans.kind": ["Action type", "行动类型"],
	"plans.destination": ["Publishing destination", "发布位置"],
	"plans.cancel": ["Cancel", "取消"],
	"plans.saved": ["Plan updated", "计划已更新"],
	"plans.open": ["Open workspace", "打开工作空间"],
	"plans.facts": ["Confirm facts", "补充事实"],
	"plans.research": ["Review evidence", "查看检测证据"],
	"plans.create": ["Create content", "创作内容"],
	"plans.update": ["Update content", "更新内容"],
	"plans.distribute": ["Arrange publishing", "安排发布"],
	"plans.observe": ["Observe results", "观察结果"],
	"plans.pending": ["To do", "待处理"],
	"plans.running": ["Working", "处理中"],
	"plans.review": ["Needs review", "待审阅"],
	"plans.done": ["Recorded complete", "已记录完成"],
	"plans.blocked": ["Needs attention", "需要处理"],
	"plans.cancelled": ["Cancelled", "已取消"],
	"plans.ondit": ["Ondit directory", "Ondit 目录"],
	"plans.website": ["Brand website", "品牌官网"],
	"plans.community": ["Community", "社区"],
	"plans.social": ["Social channel", "社交渠道"],
	"plans.selectAction": ["Choose an action to review its details.", "选择行动查看详情。"],
	"plans.linkArticle": ["Link an existing article", "关联已有文章"],
	"plans.selectArticle": ["Select an article", "选择文章"],
	"plans.link": ["Link article", "关联文章"],
	"plans.pauseHelp": [
		"The plan is paused. Resume it before adding or completing actions.",
		"计划已暂停。恢复后可以添加或完成行动。",
	],
	"plans.actionsReady": [
		"All actions have a recorded outcome. Review the results before completing the plan.",
		"全部行动已有结果记录。完成计划前，请检查观察结论。",
	],
	"plans.created": ["Created", "已创建"],
	"plans.note": ["Note saved", "已保存备注"],
	"plans.linked": ["Article linked", "已关联文章"],
	"errors.conflict.org_has_members": [
		"This account still has other people in it. Remove them, or hand the account over, before deleting it.",
		"这个账户里还有其他成员。请先移除他们或把账户转交出去，再删除账号。",
	],
	"errors.member_transfer_self": ["You already own this account.", "你已经是这个账户的所有者。"],
	"errors.conflict.plan_busy": [
		"This action is running. Wait for the result or cancel it first.",
		"行动正在执行，请等待结果或先取消行动。",
	],
	"errors.conflict.plan_changed": ["This plan changed. Reload it before saving again.", "计划已变化，请刷新后再保存。"],
	"errors.conflict.plan_paused": ["Resume this plan before continuing.", "请先恢复计划再继续。"],
	"errors.conflict.plan_unfinished": ["Finish or cancel the remaining actions first.", "请先完成或取消剩余行动。"],
	"manual.taskPrompts": ["Sampled prompts ({n})", "本次采样提示词（{n}）"],
	"manual.snapshotHelp": [
		"Wording saved when this manual task was submitted.",
		"以下为手动提交任务时保存的提示词内容。",
	],
	"errors.conflict.sampling_active": [
		"Sampling is already queued or running for this brand and language. Check Runs and try again when it finishes.",
		"该品牌当前语言已有采样任务排队或运行中。请在运行页面查看，完成后再试。",
	],
	"manual.actions": ["Manual actions", "手动操作"],
	"manual.promptAction": ["Edit and sample prompts", "编辑与采样提示词"],
	"manual.writeAction": ["Write an article", "撰写文章"],
	"manual.distributeAction": ["Distribute an article", "转发文章"],
	"manual.addPrompt": ["Add prompt", "新增提示词"],
	"manual.editPrompt": ["Edit", "编辑"],
	"manual.editHelp": [
		"Changing the wording creates a new prompt and stops future sampling of the previous wording. Historical answers remain unchanged.",
		"修改问题内容后将创建新版本，并停止旧版本的后续采样；历史回答保留原始问题。",
	],
	"manual.sampleQueued": ["Sampling queued. Follow progress in Runs.", "采样已排队，可在运行页面查看进度。"],
	// An estimate from what the run will ask, not a price: the balance is what
	// a customer watches, and a run that stops for credits can be continued.
	"manual.sampleEstimate": [
		"Sampling queued, about {n} credits. Follow progress in Runs.",
		"采样已排队，预计消耗约 {n} 积分，可在运行页面查看进度。",
	],
	// A manual run costs credits and a sample taken hours ago has mostly not
	// moved, so a second one inside a day is asked about rather than refused —
	// the answer is sometimes yes, after changing a prompt or an engine.
	"manual.repeatTitle": ["Sample again?", "重复采样？"],
	"manual.repeatBody": [
		"This brand was sampled {when}. Running again collects a new set of answers and uses credits; engines rarely answer differently within a day.",
		"该品牌{when}采样过。再次运行会重新采集一轮回答并消耗积分，同一天内引擎的回答通常不会改变。",
	],
	"manual.repeatConfirm": ["Sample anyway", "仍然采样"],
	"manual.viewTasks": ["View tasks", "查看任务"],
	"manual.sampleHelp": [
		"Select your prompts to sample again using the brand’s configured engines. Each run uses credits and adds new measurements; existing answers are retained.",
		"选择自己的提示词，使用品牌配置的引擎重新采样。每次采样消耗积分并新增测量结果，已有回答会保留。",
	],
	"manual.sampleSelected": ["Sample selected ({n})", "采样所选（{n}）"],
	"manual.selectPrompts": ["Select visible private prompts", "选择当前列表中的自有提示词"],
	"manual.selectPrompt": ["Select: {text}", "选择：{text}"],
	"manual.addGap": ["Add content gap", "新增内容缺口"],
	"manual.gapQuestion": ["Question this content should answer", "这篇内容需要回答的问题"],
	"manual.gapHelp": [
		"Record a question to clarify, then review its writing brief and generate a draft. This is a manual idea, not a detected finding. It does not automatically enable paid sampling.",
		"记录需要澄清的问题，随后可查看写作依据并生成草稿。该条目标记为手动添加，不代表已检测到的问题，也不会自动开启付费采样。",
	],
	"manual.gapSource": ["Manually added", "手动添加"],
	// -- shell
	"nav.skipContent": ["Skip to content", "跳转到主要内容"],
	"nav.overview": ["Overview", "概览"],
	"nav.agentMode": ["Agents", "智能体"],

	"nav.analysisMode": ["Analytics", "分析"],
	"nav.runs": ["Runs", "运行"],
	"nav.search": ["Search", "搜索"],
	"nav.settings": ["Settings", "设置"],
	"nav.mode": ["Workspace mode", "工作区模式"],
	"nav.billing": ["Billing", "账单"],
	"nav.account": ["Account", "账户"],
	"nav.signOut": ["Sign out", "退出登录"],
	"nav.language": ["Language", "语言"],
	"nav.collapse": ["Collapse the navigation", "收起导航"],
	"nav.resize": ["Resize the navigation", "调整导航宽度"],
	"nav.expand": ["Expand the navigation", "展开导航"],
	"nav.directory": ["Open the brands site", "打开品牌站"],

	// -- the account row, and the settings sections about the person
	"account.open": ["Account and settings", "账户与设置"],
	"account.menu": ["More account actions", "更多账户操作"],
	"account.subscription": ["Subscription", "订阅"],
	"account.name": ["Name", "姓名"],
	"account.nameHint": ["How colleagues see you in the members list.", "同事在成员列表里看到的名字。"],
	"account.nameSaved": ["Name updated.", "姓名已更新。"],
	"account.save": ["Save", "保存"],
	"account.org": ["Organisation", "组织"],
	"account.role": ["Role", "角色"],
	"account.joined": ["Joined", "加入日期"],
	"account.password": ["Password", "密码"],
	"account.passwordHint": ["We send a link to {email}.", "我们会把重置链接发到 {email}。"],
	"account.passwordSend": ["Send a reset link", "发送重置链接"],
	"account.passwordSent": ["Link sent. Check your inbox.", "链接已发送，请查收邮箱。"],
	"account.planHint": ["{plan} · {credits} credits left", "{plan} · 剩余 {credits} 积分"],
	"account.directory": ["Brands site", "品牌站"],
	"account.directoryHint": [
		"The public directory, where a published page lands.",
		"公开目录，发布出去的页面就在那里。",
	],
	"account.openLink": ["Open", "打开"],
	"account.close": ["Delete account", "删除账号"],
	"account.closeHint": [
		"Deletes the accounts you own, with their brands and everything measured for them. There is no undo.",
		"删除你作为所有者的账户，连同其中的品牌和所有测量数据，无法撤销。",
	],
	"account.closeTitle": ["Delete this account?", "删除这个账号？"],
	"account.closeBody": [
		"Every brand you own is removed, its pages and measurements go with it, and a paid plan is cancelled. Accounts you were only invited to are left alone. This cannot be undone.",
		"你名下的每个品牌都会被删除，页面和测量数据一并消失，付费套餐会被取消。别人邀请你加入的账户不受影响。此操作无法撤销。",
	],
	"account.closed": ["Your account is gone. Signing out.", "账号已删除，正在退出。"],
	"account.signOutHint": ["Ends this session in this browser.", "退出这台浏览器上的登录状态。"],
	"account.languageHint": [
		"The panel and the brands site, both. It follows you between them.",
		"面板和品牌站共用一个设置，两边一致。",
	],
	"account.themeHint": ["Follows the machine unless you say otherwise.", "默认跟随系统，也可以自己定。"],
	"account.densityHint": [
		"Compact takes the padding out of the rows. Useful once a table is longer than a screen.",
		"紧凑模式会压缩行距，表格超过一屏时更好用。",
	],

	// -- the loop, as five numbered stages
	//
	// The sidebar is the diagram of the product: measure, read the measurement,
	// write the answer, publish it, measure again. Anything that is not a stage
	// of that loop — the overview, the account — sits outside the numbers.
	"nav.stage.detect": ["Detect", "检测"],
	"nav.stage.analyse": ["Analyse", "分析"],
	"nav.stage.generate": ["Generate", "生成"],
	"nav.stage.publish": ["Publish", "发布"],
	"nav.stage.reflow": ["Reflow", "回流"],
	"nav.stage.n": ["Step {n}", "第 {n} 步"],

	// The five counts on the overview, one per stage. Nouns, not verbs: this
	// row says where the brand is, not what to do — the to-do list above it is
	// where the instructions live.
	"loop.title": ["Where this brand is around the loop", "这个品牌在闭环中的位置"],
	"loop.answers": ["answers collected", "已收集回答"],
	"loop.gaps": ["gaps open", "待处理缺口"],
	"loop.waiting": ["drafts waiting", "待审草稿"],
	"loop.published": ["pages live", "已上线页面"],
	"loop.concluded": ["conclusions reached", "已有结论"],
	// What a zero at that step is waiting for. A count of nothing says the
	// step is empty; only these say whether that is a problem or a queue.
	"loop.pending.answers": ["after the first pass", "首轮采集后出现"],
	"loop.pending.gaps": ["once answers are read", "回答分析后出现"],
	"loop.pending.waiting": ["once a page is drafted", "生成草稿后出现"],
	"loop.pending.published": ["once a draft is approved", "草稿通过后出现"],
	"loop.pending.concluded": ["after two samples", "两轮采集后出现"],

	// -- the loop map, folded into the right-hand column beside the assistant
	//
	// The sidebar names the same five steps and stays the navigation; these are
	// the words for the thing a column of links cannot say — what the machine is
	// doing, and where it has stopped and is waiting for a person.

	// -- appearance
	"theme.label": ["Appearance", "外观"],
	"theme.light": ["Light", "浅色"],
	"theme.dark": ["Dark", "深色"],
	"theme.system": ["System", "跟随系统"],
	"density.label": ["Density", "密度"],
	"density.comfortable": ["Comfortable", "标准"],
	"density.compact": ["Compact", "紧凑"],

	// -- the credit ledger. Every reason the server writes; an unknown one is
	// shown as stored rather than as a key.
	"ledger.subscription.start": ["Plan started", "订阅开始"],
	"ledger.subscription.renew": ["This month's credits", "本月积分"],
	"ledger.subscription.canceled": ["Plan cancelled", "订阅已取消"],
	"ledger.usage": ["Models run for you", "为你运行的模型"],
	"ledger.seed": ["Starting balance", "初始额度"],
	"ledger.migration": ["Balance carried over", "结转余额"],
	"ledger.credit_pack": ["Credits bought", "充值积分"],
	"ledger.credits.expired": ["Plan credits expired", "套餐积分到期"],
	"ledger.admin.grant": ["Credits granted by Ondit", "Ondit 赠送的积分"],
	"ledger.admin.revoke": ["Credits revoked by Ondit", "Ondit 收回的积分"],
	"ledger.refund.succeeded": ["Refunded", "已退款"],
	"ledger.dispute.lost": ["Payment disputed", "支付被拒付"],
	// What a metered call outside a job was bought as: the ledger line says
	// which of these ran, not only that models did.
	"meter.sample": ["Collecting AI answers", "采集 AI 回答"],
	"meter.factcheck": ["Checking answer claims", "核查回答事实"],
	"meter.generate": ["Generating a page", "生成页面"],
	"meter.opportunities": ["Analysing opportunities", "分析优化机会"],
	"meter.onboarding": ["Setting the brand up", "品牌初始化"],
	"meter.agent": ["Agent", "智能体"],

	// -- brands
	"brand.switch": ["Switch brand", "切换品牌"],
	"brand.yours": ["Your brands", "你的品牌"],
	"brand.none": ["No brand yet", "还没有品牌"],
	"brand.add": ["Add a brand", "添加品牌"],
	"brand.full": ["Your plan's brand limit is {n}.", "当前套餐的品牌上限是 {n} 个。"],
	"brand.upgrade": ["Upgrade to add a brand", "升级以添加品牌"],

	// The name of the mark that carries a screen's explanation.
	"hint.label": ["What this means", "这是什么意思"],

	// -- global filters
	"filter.range": ["Range", "时间范围"],
	"filter.range7": ["7 days", "7 天"],
	"filter.range30": ["30 days", "30 天"],
	"filter.range90": ["90 days", "90 天"],
	"filter.engine": ["Engine", "引擎"],
	"filter.allEngines": ["All engines", "全部引擎"],
	"filter.locale": ["Content language", "内容语言"],
	"locale.en": ["English", "英语"],
	"locale.zh": ["Simplified Chinese", "简体中文"],

	// -- overview
	"overview.title": ["Overview", "概览"],
	"overview.statQuestions": ["Tracked questions", "追踪的问题"],
	"overview.statCited": ["{cited} cited", "其中 {cited} 个被引用"],
	"overview.statOndit": ["Ondit pages cited", "Ondit 页面被引用"],
	"overview.statOwn": ["Your own site cited", "你的官网被引用"],
	"overview.shareTitle": ["Citation share", "引用份额"],
	"overview.shareLede": [
		"How often AI answers cite you, and who they cite instead.",
		"AI 回答引用你的频率，以及它们转而引用了谁。",
	],
	"overview.noTrend": ["Two weeks of samples make the first line.", "两周的采样才画得出第一条线。"],
	"overview.noData": ["Nothing measured yet", "还没有测量数据"],
	"overview.noDataBody": [
		"Add a question and run the first sample. The first useful comparison needs two weeks of data.",
		"先添加一个问题并跑一次采样。第一次有意义的对比需要两周的数据。",
	],
	"overview.delta": ["{delta} points vs. two weeks ago", "较两周前 {delta} 个百分点"],
	// The sentence that keeps this product honest.
	"overview.noConclusion": ["No conclusion yet — not enough samples", "暂无结论——样本不足"],
	"overview.noConclusionWhy": [
		"A change is only reported when both windows have at least five usable samples and the move is at least 20 points. Anything smaller is noise.",
		"只有当前后两个窗口各有至少五个可用样本、且变化不小于 20 个百分点时才会报告变化。更小的差异是噪声。",
	],
	"overview.nothingQueued": ["Nothing queued", "没有排队的任务"],
	"overview.trend": ["Citation share by week", "按周的引用份额"],
	"overview.competitors": ["Who is cited instead", "被引用的是谁"],
	"overview.lastSampled": ["Last sampled", "最近采样"],
	"overview.never": ["never", "从未"],
	"overview.nextSample": ["next {when}", "下次 {when}"],
	"overview.sampleRunning": ["A run is already going", "已经有一次采集在跑了"],
	"overview.sampleNow": ["Sample now", "立即采样"],

	// -- what each number actually is
	//
	// One sentence per figure, on the figure. Every number here is one somebody
	// may quote in a meeting, and a definition that lives in the documentation is
	// a definition nobody reads before quoting it.
	"hint.trackedQuestions": [
		"Distinct questions answered in the latest weekly run. A question counts as cited when more than half of its replications named you.",
		"最近一次周度采样中被回答过的不同问题数。当一个问题超过半数的重复采样点到了你，才算被引用。",
	],
	"hint.onditPages": [
		"Times an answer cited one of your pages on the Ondit directory, in this range.",
		"该时间范围内，AI 回答引用你在 Ondit 品牌库页面的次数。",
	],
	"hint.ownSite": [
		"Times an answer cited a domain you own, in this range. Add domains in Settings and they count here.",
		"该时间范围内，AI 回答引用你自己域名的次数。在「设置」里补充域名，它们就会计入这里。",
	],
	"hint.lastSampled": [
		"When an engine last answered one of your questions. The scheduled run is weekly.",
		"引擎最近一次回答你的问题的时间。定时任务是每周一次。",
	],
	"hint.share": [
		"Within one question a domain counts as cited when it appeared in more than half its replications; the share is the fraction of questions where that held, averaged over engines.",
		"在一个问题内，某域名出现在超过半数的重复采样中才算被引用；份额是满足这一条件的问题占比，再按引擎求平均。",
	],
	"hint.balance": [
		"Credits are the sum of an append-only ledger. Every charge is listed under Billing.",
		"积分是一本只追加的流水账的总和。每一笔扣减都在「账单」里列出。",
	],
	"hint.crawlerHits": [
		"Fetches of your pages by an AI crawler, counted per page per day. Not visits by people.",
		"AI 抓取器对你页面的抓取次数，按页面按天统计。不是真人访问。",
	],
	"hint.crawlerErrors": [
		"Fetches answered with something other than a page. A crawler that cannot read a page cannot cite it.",
		"没有返回页面的抓取。读不到页面的抓取器不可能引用它。",
	],
	"hint.perceptionNamed": [
		"Answers in this window that named the brand, out of every answer to its tracked questions.",
		"这段时间里提到该品牌的回答数，分母是它所追踪问题的全部回答。",
	],
	"hint.perceptionFirst": [
		"Of the answers that name you, the share where you are named before anybody else.",
		"在提到你的回答里，你被排在所有品牌最前面的比例。",
	],
	"hint.perceptionPosition": [
		"Your average place among the brands an answer names. Lower is better; 1 is first.",
		"在一条回答提到的品牌中，你的平均位次。越小越好，1 表示第一个。",
	],
	"hint.perceptionContradictions": [
		"Claims an assistant made that this brand's own sourced facts contradict. Listed on FactCheck.",
		"助手说过、但与该品牌自己有出处的事实相矛盾的说法。详见「事实核查」。",
	],
	"hint.spend24h": [
		"What this account has been billed for model calls in the last 24 hours, at published prices.",
		"最近 24 小时该账户在模型调用上的计费金额，按官方标价计算。",
	],
	"hint.spendMonth": ["The same, since the first of this month.", "同上口径，从本月 1 日算起。"],
	"hint.oppsFresh": [
		"A plan is kept for six days and served as it stands; reading it costs nothing. Only writing a new one spends.",
		"一份计划保存六天，期间直接读取已有结果，不产生费用。只有重新生成才会扣积分。",
	],
	"hint.openGaps": [
		"Questions where a competitor was cited in the majority of replications and you were not.",
		"竞品在多数重复采样中被引用、而你没有被引用的问题。",
	],

	// -- what to do next
	//
	// One card per thing worth doing, ordered by whether it is blocking. Every
	// body says the consequence, not the instruction: a customer who knows why a
	// thing matters decides for themselves whether to do it, and one who is only
	// told to click does it once.
	"todo.title": ["What to do next", "接下来做什么"],
	"todo.lede": [
		"Everything below is computed from your own numbers. Nothing here is a guess.",
		"下面每一条都是用你自己的数据算出来的，没有一条是猜的。",
	],
	"todo.attention": ["Needs attention", "需要处理"],
	"todo.opportunity": ["Opportunity", "可以做"],
	"todo.empty": ["Nothing needs you right now.", "目前没有需要你处理的事。"],
	"todo.emptyBody": [
		"The loop is running on its own. The next weekly sample will say whether it worked.",
		"闭环在自己跑。下一次周度采样会告诉你有没有用。",
	],
	"todo.hide": ["Hide", "隐藏"],
	"todo.hidden": ["{n} hidden", "已隐藏 {n} 条"],
	"todo.showHidden": ["Show them", "重新显示"],

	"todo.no_questions.title": ["No questions are being asked yet", "还没有在问任何问题"],
	"todo.no_questions.body": [
		"Add prompts to start measuring how engines answer questions about your brand.",
		"添加提示词，开始测量各引擎如何回答与你的品牌相关的问题。",
	],
	"todo.no_questions.cta": ["Add questions", "添加问题"],

	"todo.never_sampled.title": ["{n} questions, no answers yet", "{n} 个问题，还没有采样"],
	"todo.never_sampled.body": [
		"The weekly run has not reached this brand. You can start one now rather than wait for it.",
		"周度任务还没跑到这个品牌。你可以现在就发起一次，不用等。",
	],
	"todo.never_sampled.cta": ["Sample now", "立即采样"],

	"todo.sampling_now.title": ["The first pass is running", "首轮采集正在进行"],
	"todo.sampling_now.body": [
		"Each of the {n} questions is being put to every engine you track. It usually takes a few minutes; the figures on this screen fill in as the answers land.",
		"正在把 {n} 个问题逐一问给你追踪的每个引擎。通常几分钟完成，回答陆续返回后，这个页面上的数字会随之出现。",
	],
	"todo.sampling_now.cta": ["Watch it run", "查看进度"],

	// The minute between paying and the first question existing. Everything the
	// other cards would ask for — questions, facts, a weekly clock — is being
	// written right now, so this replaces all three rather than joining them.
	"todo.setup_preparing.title": ["Setting your brand up", "正在准备这个品牌"],
	"todo.setup_preparing.body": [
		"Your questions, your facts and the weekly schedule are being written now. The cards and figures on this screen fill in as each lands.",
		"问题、事实和每周的定时任务正在写入。写好一样，这个页面上的卡片和数字就出现一样。",
	],
	"todo.setup_preparing.cta": ["Watch it run", "查看进度"],

	// Named after the workflow the customer can see on their own canvas, not
	// after "the first pass": the card and the thing it is about have to be the
	// same object, or the button lands somewhere they do not recognise.
	"todo.setup_running.title": ["Initial Setup is running", "初始化正在运行"],
	"todo.setup_running.body": [
		"It is putting each of your {n} questions to every engine you track, then drafting your facts page. The figures on this screen fill in as it goes.",
		"它正在把 {n} 个问题逐一问给你追踪的每个引擎，然后起草你的事实页。它跑到哪一步，这个页面上的数字就出现到哪一步。",
	],
	"todo.setup_running.cta": ["Watch it run", "查看进度"],

	// The task list is where a failure says which step broke and offers the
	// retry; the card's job is to say a failure happened at all.
	"todo.tasks_failed.title": ["{n} task(s) stopped on an error", "有 {n} 个任务出错中断了"],
	"todo.tasks_failed.body": [
		"Whatever they were writing or measuring did not land, so the figures they feed are the last good ones. Open the task to see which step failed, then run it again.",
		"它们要写的或要测的东西没做完，相关数字停在上一次的结果上。打开任务看是哪一步失败的，处理之后重新跑一次。",
	],
	"todo.tasks_failed.cta": ["Open the tasks", "查看任务"],

	"todo.setup_failed.title": ["Initial Setup stopped on an error", "初始化中断了"],
	"todo.setup_failed.body": [
		"The first pass did not finish, so nothing has been measured yet. Open the workflow to see which step failed, then run it again.",
		"首轮没有跑完，所以还什么都没测到。打开工作流看是哪一步失败的，处理之后重新运行一次。",
	],
	"todo.setup_failed.cta": ["Open the workflow", "打开工作流"],

	"todo.loop_failed.title": ["Weekly Optimization stopped on an error", "每周优化中断了"],
	"todo.loop_failed.body": [
		"Its last run did not finish, so this week has nothing to compare against and no page was drafted from it.",
		"上一次运行没有跑完，所以这一周没有可对比的基准，也没有从中起草任何页面。",
	],
	"todo.loop_failed.cta": ["Open the workflow", "打开工作流"],

	"todo.loop_stopped.title": ["Nothing will measure this brand next week", "下周不会再测量这个品牌"],
	"todo.loop_stopped.body": [
		"Weekly Optimization has no clock — it was switched off, or the workflow was deleted. Every number here stays where it is until something runs again.",
		"每周优化没有时钟了——要么被关掉了，要么工作流被删了。在有东西重新跑起来之前，这里的每个数字都会停在原地。",
	],
	"todo.loop_stopped.cta": ["Open the Agent", "去智能体"],

	"todo.not_cited.title": ["{n} answers, with no links to your domains", "{n} 条回答，没有引用你的域名"],
	"todo.not_cited.body": [
		"The collected answers did not link to a recognised brand domain. Check domain matching, then review the cited sources and content gaps to understand what the answers used instead.",
		"已采集答案没有链接到已识别的品牌域名。请先检查域名匹配，再查看引用来源与内容缺口，确定答案采用了哪些其他证据。",
	],
	"todo.not_cited.cta": ["Check names and domains", "检查名称与域名"],

	"todo.factcheck_high.title": ["{n} things AI said about you are wrong", "AI 有 {n} 处关于你的说法是错的"],
	"todo.factcheck_high.body": [
		"Each one contradicts a fact you have already verified. That is the material a clarification page is written from.",
		"每一条都和你已核实的事实相矛盾。澄清页就是用这些材料写出来的。",
	],
	"todo.factcheck_high.cta": ["Read them", "去看看"],

	"todo.gaps_open.title": ["{n} questions a competitor wins and you do not", "有 {n} 个问题被竞品拿下，你没有"],
	"todo.gaps_open.body": [
		"Review the cited sources and suggested page type, then choose which gap to address.",
		"查看被引用的来源和建议页面类型，选择要优先补齐的内容。",
	],
	"todo.gaps_open.cta": ["Open the gaps", "查看缺口"],

	"todo.few_datapoints.title": ["Only {n} checkable facts on your listing", "你的条目上只有 {n} 条可核实的事实"],
	// Two floors, and they are different rules: three facts to have a page at
	// all, and two distinct source URLs among them for the sources block. A
	// listing can clear the first and still be refused by the second, so the
	// string has to name both.
	"todo.few_datapoints.body": [
		"Three facts is the floor, and two of them need to carry a source URL — under either, every generator refuses, because a page without original data is one an engine has no reason to cite.",
		"三条事实是底线，其中至少两条要带来源链接——任何一条不满足，所有生成都会被拒绝：没有原始数据的页面，引擎没有理由引用它。",
	],
	"todo.few_datapoints.cta": ["Add facts", "补充事实"],

	"todo.low_credits.title": ["{n} credits left, and pages waiting to be written", "只剩 {n} 积分，还有页面等着写"],
	"todo.low_credits.body": [
		"A generation costs more than the balance covers, so the gap list cannot be worked through.",
		"一次生成的花费超过了当前余额，缺口清单没法继续做下去。",
	],
	"todo.low_credits.cta": ["Top up", "去充值"],

	"todo.publishing_review.title": ["{n} channel drafts await review", "有 {n} 项渠道草稿待审核"],
	"todo.publishing_review.body": [
		"Review the platform, content and source version before publishing.",
		"发布前请核对目标平台、内容和原文版本。",
	],
	"todo.publishing_review.cta": ["Review deliveries", "审核发布"],
	"todo.publishing_attention.title": ["{n} deliveries need attention", "有 {n} 项投递需要处理"],
	"todo.publishing_attention.body": [
		"Inspect failed or unknown results. Unknown writes are never retried automatically.",
		"检查失败或结果未知的发布任务。结果未知时不会自动重发。",
	],
	"todo.publishing_attention.cta": ["Resolve deliveries", "处理投递"],
	"todo.pages_review.title": ["{n} drafts are waiting for you", "有 {n} 篇草稿在等你"],
	"todo.pages_review.body": [
		"Review the draft’s facts, sources and wording, then approve it when it is ready.",
		"检查草稿的事实、来源和表述，确认内容准确后再审批发布。",
	],
	"todo.pages_review.cta": ["Review them", "去审核"],

	"todo.badge_missing.title": ["The link back from your site is missing", "你站点上的回链还没找到"],
	"todo.badge_missing.body": [
		"{n} of your pages are live here. The badge on your own site is what tells an engine that this listing and your product are the same thing.",
		"你已经有 {n} 个页面在这里上线。你自己站点上的徽章，是告诉引擎「这个条目和你的产品是同一个东西」的方式。",
	],
	"todo.badge_missing.cta": ["Get the badge", "获取徽章"],

	// -- questions
	"questions.title": ["Questions", "问题集"],
	"questions.lede": [
		"The prompts used to sample answers from each engine for your brand.",
		"用于向各引擎采样、测量品牌表现的提示词。",
	],
	"questions.add": ["Add a question", "添加问题"],
	"questions.placeholder": ["What would a buyer type into ChatGPT?", "买家会在 ChatGPT 里怎么问？"],
	"questions.private": ["Yours", "你的"],
	"questions.used": ["{used} of {max} used", "已用 {used} / {max}"],
	"questions.lastSampled": ["Last sampled", "最近采样"],
	"questions.cited": ["Cited", "被引用"],
	"questions.notCited": ["Not cited", "未被引用"],
	"questions.removeTitle": ["Remove this question?", "移除这个问题？"],
	"questions.removeBody": [
		"It stops being sampled from next week. The answers already recorded for it stay in your history.",
		"下周起不再采样。已记录的回答会保留在历史里。",
	],
	"questions.remove": ["Remove", "删除"],
	"questions.empty": ["No questions yet.", "还没有问题。"],
	"questions.allIntents": ["Every intent", "全部意图"],
	"questions.intent": ["Intent", "意图"],
	"questions.intent.informational": ["Informational", "信息型"],
	"questions.intent.comparison": ["Comparison", "对比型"],
	"questions.intent.alternatives": ["Alternatives", "替代品型"],
	"questions.intent.commercial": ["Commercial", "商业型"],
	"questions.intent.transactional": ["Transactional", "交易型"],

	// -- visibility
	"visibility.title": ["Visibility", "可见度"],
	"visibility.lede": [
		"Every question, on every engine, with how many of its replications cited you.",
		"每个问题在每个引擎上，有多少次重复采样引用了你。",
	],
	"visibility.question": ["Question", "问题"],
	"visibility.empty": ["Nothing sampled in this range.", "该时间范围内没有采样。"],
	"visibility.overall": ["All engines", "全部引擎"],

	// -- citations
	"citations.title": ["Citations", "引用"],
	"citations.lede": [
		"The sources AI answers actually used. Your own pages and ours are marked.",
		"AI 回答实际用到的来源。你自己的页面和我们的页面都有标记。",
	],
	"citations.byDomain": ["By domain", "按域名"],
	"citations.byUrl": ["By page", "按页面"],
	"citations.count": ["Times cited", "被引次数"],
	"citations.yours": ["yours", "你的"],
	"citations.ondit": ["Ondit", "Ondit"],
	"citations.empty": ["No citations in this range.", "该时间范围内没有引用。"],
	// What kind of source the engines leaned on. The useful question is not
	// "which fifty domains" but "what kind of thing does the model trust here",
	// because the answer decides what to do about it.
	"citations.kind": ["Kind", "来源类型"],
	"citations.kind.yours": ["Yours", "你的"],
	"citations.kind.ondit": ["Ondit", "Ondit"],
	"citations.kind.directory": ["Directory", "目录站"],
	"citations.kind.community": ["Community", "社区"],
	"citations.kind.other": ["Other", "其他"],
	"citations.mix": ["What the engines leaned on", "引擎主要依赖什么"],
	"citations.mixLede": [
		"A category answered mostly out of directories is one where being listed works. One answered mostly out of forums is one where a page nobody discusses will not be cited whatever it says.",
		"如果一个分类的回答主要来自目录站，那么「被收录」就有用；如果主要来自论坛，那么没人讨论的页面，写得再好也不会被引用。",
	],

	// -- competitors
	"competitors.rivals": ["Products", "竞品"],
	"competitors.rivalsHint": [
		"Products cited in the answers, plus competitors you added. Compare their sources and coverage.",
		"答案引用的产品，以及你添加的竞品。查看它们的引用来源和内容覆盖情况。",
	],
	"competitors.sources": ["Sources", "来源"],
	"competitors.sourcesHint": [
		"Cited forums, reviews and documentation. Review which sources could include relevant information about your brand.",
		"被引用的论坛、测评和文档。查看哪些来源适合补充与你的品牌相关的信息。",
	],
	"competitors.noRivals": ["No product was cited instead of you.", "没有产品被引用来替代你。"],
	"competitors.noSources": ["No other page won a citation.", "没有其他页面赢下引用。"],
	"competitors.watch": ["Watch", "关注"],
	"competitors.watched": ["Named", "已点名"],
	"competitors.watchedHint": [
		"You named this one, so it stays on the list even in a week it was never cited.",
		"你点名的对手,即使这一周一次都没被引用,也会留在列表上。",
	],
	"competitors.watched.done": ["{domain} stays on the list from now on.", "{domain} 之后会一直留在列表上。"],
	"competitors.unwatch": ["Stop watching", "取消关注"],
	"competitors.unwatched": ["{domain} is no longer pinned.", "{domain} 不再固定显示。"],
	"competitors.lede": [
		"Who was cited instead of you, ranked by how many questions they win — products in one list, the pages that beat you to it in the other. Derived from the answers, not a list you maintain; name one yourself and it stays on the list whatever the week did.",
		"引用了谁而不是你,按赢下的问题数排序 —— 产品一张表,抢在你前面的页面另一张。这是从回答里算出来的,不是你维护的名单;你也可以自己点名一个,不管这周有没有被引用它都会留在表上。",
	],
	"competitors.share": ["Share", "份额"],
	"competitors.questions": ["Questions won", "赢下的问题"],
	"competitors.empty": ["Nothing to compare yet.", "还没有可对比的数据。"],
	"competitors.emptyBody": [
		"A rival appears here once an answer in this range names another product. Collect samples, or widen the period and the engines above.",
		"当这个范围内的回答提到别的产品时，竞品才会出现在这里。可以先采样，或把上方的时间范围和引擎放宽。",
	],
	// Derived rivals are the right idea and produce one recurring complaint: the
	// top of the list is GitHub, Reddit and the customer's own docs host. The
	// fix belongs on the row, not buried three screens away in settings.
	"competitors.exclude": ["Not a rival", "不是竞品"],
	"competitors.excluded": ["{domain} is no longer counted as a rival.", "{domain} 不再算作竞品。"],
	"competitors.excludedHint": [
		"It stays in your citations and in the source mix — only the ranking and the gap list stop treating it as somebody you lost to.",
		"它仍会出现在引用列表和来源构成里——只是排名和缺口清单不再把它当成「赢了你的人」。",
	],
	"settings.rivals": ["Rivals you name", "你点名的竞品"],
	"settings.rivalsHint": [
		"Always on the competitors list, at zero in a week they were not cited. One per line. Added to what the engines actually cited, never instead of it.",
		"始终显示在竞品表上,这一周没被引用就是 0%。每行一个。它们是加在引擎实际引用的结果之上,不会替换掉那些结果。",
	],
	"settings.excluded": ["Domains that are not rivals", "不算竞品的域名"],
	"settings.excludedHint": [
		"Excluded from the competitor ranking and the gap list. One per line. Their citations are still counted everywhere else.",
		"这些域名不计入竞品排名和缺口清单。每行一个。它们的引用在其他地方照常统计。",
	],

	// -- gaps
	"gaps.lede": [
		"Questions where a competitor is cited and you are not. Each one names the page that would answer it.",
		"竞品被引用而你没有被引用的问题。每一条都指明了应该写哪个页面。",
	],
	"gaps.suggested": ["Suggested", "建议"],
	"gaps.competitors": ["Cited instead", "被引用的是"],
	"gaps.generate": ["Generate", "生成"],
	"gaps.balance": ["{balance} credits left", "剩余 {balance} 积分"],
	"gaps.empty": ["No open gaps. That is the good outcome.", "没有待处理的缺口。这是好结果。"],
	"gaps.became": ["Became", "写成了"],
	"gaps.draft": ["View article", "查看文章"],
	"gaps.queued": ["Queued", "已排队"],
	"gaps.stalled": ["Stopped", "已中断"],
	"gaps.release": ["Put back", "重新排队"],
	"gaps.released": ["Back on the list", "已放回列表"],
	"gaps.balanceLabel": ["Credits left", "剩余积分"],
	"gaps.openLabel": ["Open gaps", "待处理缺口"],
	// Working a gap list one row at a time is the difference between a tool
	// somebody uses on Monday and one they mean to get to. The total cost is on
	// the button before the click, exactly as it is for a single page.
	"gaps.selected": ["{n} selected", "已选 {n} 个"],
	"gaps.generateSelected": ["Generate {n} pages · {credits} credits", "生成 {n} 个页面 · {credits} 积分"],
	"gaps.selectAll": ["Select every gap shown", "全选当前列表"],
	"gaps.select": ["Select this gap", "选择这一条"],
	"gaps.batchDone": ["{done} queued.", "已排队 {done} 个。"],
	"gaps.batchPartial": [
		"{done} queued, {failed} refused. The refusals are listed above.",
		"已排队 {done} 个，{failed} 个被拒绝。拒绝原因见上方。",
	],
	"gaps.noCredits": ["No credits left.", "积分已用完。"],

	// -- crawlers
	//
	// The step before everything else this product measures: whether an engine
	// ever came to read. The only failure in the chain nobody notices on their
	// own — a bot rule answering 403 to GPTBot looks like a working site.
	"crawlers.lede": [
		"Which AI systems came to read your pages, and whether they came to train on them, index them, or answer a question being asked right now.",
		"哪些 AI 系统来读过你的页面，以及它们是来训练、建索引，还是正在回答某个人此刻提出的问题。",
	],
	"crawlers.hits": ["Fetches", "抓取次数"],
	"crawlers.errors": ["Refused", "被拒绝"],
	"crawlers.vendors": ["Vendors", "厂商数"],
	"crawlers.daily": ["Day by day", "逐日"],
	"crawlers.dailyHint": [
		"One column a day, stacked by why they came. A gap is a day nothing read these pages.",
		"每天一列，按来意分层。空白的一天表示当天没有任何抓取。",
	],
	"crawlers.bot": ["Crawler", "抓取器"],
	"crawlers.vendor": ["Vendor", "厂商"],
	"crawlers.pagesRead": ["Pages read", "读过的页面"],
	"crawlers.path": ["Page", "页面"],
	"crawlers.thePage": ["the page", "该页面"],
	"crawlers.pages": ["Most read", "被读得最多的"],
	"crawlers.pagesHint": [
		"A page with refusals against it is a page the engines tried to read and could not.",
		"如果某个页面有被拒记录，说明引擎试图读它但没读到。",
	],
	"crawlers.purpose.live": ["Answering a question now", "正在回答问题"],
	"crawlers.purpose.search": ["Building an index", "在建索引"],
	"crawlers.purpose.training": ["Collecting to train on", "在收集训练数据"],
	"crawlers.purpose.preview": ["Unfurling a link", "在展开链接预览"],
	"crawlers.purposeHint.live": [
		"Somebody asked an assistant something and it came here to answer. The fetch closest to a citation.",
		"有人向助手提了问题，它来这里找答案。这是最接近一次引用的抓取。",
	],
	"crawlers.purposeHint.search": [
		"Building the index the assistant searches later. Today's fetch, next month's answer.",
		"在建助手之后会检索的索引。今天来抓，下个月才出现在回答里。",
	],
	"crawlers.purposeHint.training": [
		"Taking the page for a future model. A bet on next year rather than this week.",
		"把页面收进未来的模型。这是押注明年，不是这周。",
	],
	"crawlers.purposeHint.preview": ["Turning a link into a card in a chat.", "把链接变成聊天里的卡片。"],
	"crawlers.emptyTitle": ["Nothing has read these pages yet", "还没有任何抓取记录"],
	"crawlers.empty": [
		"Fetches of your pages on {host} are counted automatically — there is nothing to install.",
		"{host} 上你的页面被抓取时会自动计数，不需要安装任何东西。",
	],
	"crawlers.ownSite": ["Your own site", "你自己的站点"],
	"crawlers.ownSiteHint": [
		"The same picture for your own domain. Optional: everything above is counted here without it.",
		"给你自己的域名做同样的统计。可选：上面的数据不需要它也照常统计。",
	],
	"crawlers.ownSiteLive": ["Your own site is reporting.", "你的站点正在上报数据。"],
	"crawlers.ownSiteOff": ["Not connected yet.", "尚未接入。"],
	"crawlers.connect": ["Get the snippet", "获取代码"],
	"crawlers.rotate": ["New token", "更换令牌"],
	"crawlers.rotateTitle": ["Replace the token?", "更换令牌？"],
	"crawlers.rotateBody": [
		"The token in your deployed snippet stops working the moment a new one is issued. Update the snippet on your site, or its crawler counts stop here.",
		"新令牌签发的那一刻，已部署代码里的旧令牌即失效。请更新站点上的代码，否则这里将不再收到它的爬虫计数。",
	],
	"crawlers.tokenMinted": [
		"New token. The old one stops working now, so replace it wherever it is deployed.",
		"已生成新令牌。旧的立即失效，请在部署的地方替换掉。",
	],

	// -- perception
	"perception.rank": ["place {n}", "第 {n} 位"],
	"perception.title": ["Perception", "印象"],
	"perception.lede": [
		"What happens when an assistant does name you: where in the answer you land, what words come with you, and who you share the answer with.",
		"助手提到你时到底说了什么：你出现在回答的什么位置、被贴上哪些词、和谁一起被提到。",
	],
	"perception.empty": [
		"No answer in this window named this brand, so there is nothing to read yet.",
		"这段时间里没有回答提到这个品牌，暂时无从读起。",
	],
	"perception.named": ["Answers naming you", "提到你的回答"],
	"perception.ofAnswers": ["of {n} answers", "共 {n} 条回答"],
	"perception.first": ["Named first", "被首个提到"],
	"perception.position": ["Average place", "平均位次"],
	"perception.contradictions": ["Contradicted facts", "与事实矛盾"],
	"perception.mood.positive": ["Recommended", "被推荐"],
	"perception.mood.neutral": ["Listed", "被列出"],
	"perception.mood.negative": ["Warned against", "被劝退"],
	"perception.mood.unread": ["Not read yet", "尚未解读"],
	"perception.alongside": ["Who shares the answer", "和谁同框"],
	"perception.alongsideHint": [
		"The comparison you are actually in, decided by the assistant rather than by anybody's marketing.",
		"你真正被放进的那组对比——由助手决定，而不是任何人的市场部。",
	],
	"perception.brand": ["Brand", "品牌"],
	"perception.together": ["In the same answer", "同一条回答"],
	"perception.ahead": ["Named before you", "排在你前面"],
	"perception.sentiment": ["How you are treated", "被怎样对待"],
	"perception.sentimentHint": [
		"A brand merely listed is neutral, not positive — being named is not the same as being recommended.",
		"只是被列出来算中性，不算正面：被提到和被推荐是两回事。",
	],
	"perception.themes": ["The words that come with you", "跟着你出现的词"],
	"perception.themesHint": [
		"Said once, it is one model's phrasing. Said twenty times, it is what the market has decided you are.",
		"说一次只是某个模型的措辞；说二十次，就是市场认定的你。",
	],
	"perception.quotes": ["What was said", "都说了什么"],
	"perception.quotesHint": [
		"One line per answer, paraphrasing how it positioned you.",
		"每条回答一句话，概括它是怎么定位你的。",
	],

	// -- tasks
	"jobs.onboarding.prepare": ["Prepare brand monitoring", "准备品牌监测"],
	"jobs.questions.generate": ["Generate candidate questions", "生成候选问题"],
	"jobs.samples.collect": ["Collect AI answer batches", "采集 AI 回答批次"],
	"jobs.sample.batch": ["Collect AI answers", "采集 AI 回答"],
	"jobs.claims.extract": ["Check answer claims", "核查回答事实"],
	"jobs.generate.page": ["Generate page", "生成页面"],
	"jobs.opportunities.report": ["Analyse opportunities", "分析优化机会"],
	"jobs.publish.page": ["Publish page", "发布页面"],
	"jobs.reflow.page": ["Measure page reflow", "测量页面回流"],
	"jobs.reflow.compare": ["Compare publication cohorts", "对比发布前后批次"],
	"jobs.knowledge.index": ["Index a knowledge document", "索引知识库文档"],
	"jobs.agent.workflow": ["Run the agent", "智能体运行中"],
	"jobs.agent.schedule": ["Scheduled agent run", "智能体定时运行"],

	"tasks.totalLabel": ["Tasks", "任务数"],
	"tasks.summaryHelp": [
		"Pending items include reviews you can handle and workflow requests assigned to you across this account. Tasks and agent runs belong to this brand and include all dates. Scheduled counts enabled schedules, not future occurrences. These counts are independent of the overview filters.",
		"我的待办汇总账户内你有权限审核的事项及指派给你的工作流请求；任务和智能体运行统计当前品牌，不限日期。已计划统计启用中的计划，不是未来执行次数。这些数量不受概览筛选影响。",
	],
	"tasks.needsAttention": ["To do", "待处理"],
	"tasks.enabledSchedules": ["Enabled schedules", "启用中的计划"],
	"tasks.active": ["In progress", "进行中"],
	"errors.gated.task_retired": [
		"This legacy task was retired. Use a publication cohort comparison to measure reflow.",
		"此旧版任务已停用。请通过发布前后批次对比来测量回流。",
	],
	"tasks.scheduledFor": ["Scheduled for {time}", "计划于 {time}"],
	"tasks.inbox": ["My pending tasks", "我的待办"],
	"tasks.inboxEmptyTitle": ["Nothing needs your attention", "暂时没有待办"],
	"tasks.inboxEmpty": [
		"Article reviews, publishing reviews, content plan reviews and workflow requests you can handle appear here.",
		"你可处理的文章审核、渠道发布审核、内容计划审阅和工作流请求，会显示在这里。",
	],
	"tasks.inboxTime": ["Due / scheduled", "截止或计划时间"],
	"tasks.deadline": ["Respond before", "处理截止时间"],
	"tasks.review": ["Review", "查看并处理"],
	"tasks.approval": ["Approval needed", "待审批"],
	"tasks.kind.page_review": ["Article review", "文章审核"],
	"tasks.kind.publishing_review": ["Publishing review", "发布审核"],
	"tasks.kind.publishing_plan": ["Publishing confirmation", "发布确认"],
	"tasks.kind.plan_review": ["Plan action review", "计划行动审阅"],
	"tasks.inputNeeded": ["Input needed", "待补充输入"],
	"tasks.openWorkflow": ["Open workflow", "打开工作流"],
	"tasks.approve": ["Approve", "批准"],
	"tasks.reject": ["Reject", "拒绝"],
	"tasks.submit": ["Submit", "提交"],
	"tasks.approvalHint": ["The workflow continues according to your decision.", "工作流会按你的决定继续。"],
	"tasks.inputHint": ["The workflow continues with what you submit here.", "工作流会使用你在此提交的内容继续。"],
	"tasks.chooseValue": ["Choose a value", "请选择"],
	"tasks.jsonHint": ["Enter JSON.", "请输入 JSON。"],
	"tasks.responded": ["Response sent. The workflow continues.", "已提交，工作流将继续执行。"],
	"tasks.execution": ["Execution history", "执行记录"],
	"tasks.title": ["Tasks", "任务"],
	"tasks.tabLoop": ["Tasks", "任务"],
	"tasks.tabScheduled": ["Scheduled", "已计划"],
	"tasks.agenda.timezone": ["Display timezone", "显示时区"],
	"tasks.agenda.help": [
		"Schedules for this brand, grouped by the display timezone. Today, tomorrow, the rest of this week (ending Sunday), then the remaining days in the next 30 days do not overlap. Recurrences use each schedule’s saved timezone. Open an agent and use Schedule to set its run times.",
		"显示当前品牌的定时运行。按显示时区依次分为今天、明天、本周剩余时间（截至周日）及未来 30 天中的其余日期，各组不重复。实际执行使用计划保存的时区。打开智能体，点击“定时”设置运行时间。",
	],
	"tasks.agenda.setup": ["Set up a schedule", "设置定时"],
	"tasks.agenda.pause": ["Pause schedule", "暂停计划"],
	"tasks.agenda.resume": ["Resume schedule", "恢复计划"],
	"tasks.agenda.paused": ["Paused schedules", "已暂停的计划"],
	"tasks.agenda.history": ["Run history · Last 90 days", "运行记录 · 最近 90 天"],
	"tasks.agenda.today": ["Today", "今天"],
	"tasks.agenda.tomorrow": ["Tomorrow", "明天"],
	"tasks.agenda.week": ["This week", "本周"],
	"tasks.agenda.month": ["Upcoming 30 days", "未来 30 天"],
	"tasks.agenda.noHistory": ["No run history in the last 90 days.", "最近 90 天内没有运行记录。"],
	"tasks.agenda.empty": ["No scheduled runs.", "没有安排运行。"],
	"tasks.agenda.time": ["Scheduled time", "计划运行时间"],
	"tasks.agenda.more": ["Load more", "加载更多"],
	"tasks.agenda.viewRun": ["View run", "查看运行"],
	"tasks.agenda.waiting": ["Awaiting response", "等待回应"],
	"tasks.agenda.cancelled": ["Cancelled", "已取消"],
	"tasks.agenda.unknown": ["Unknown status", "未知状态"],
	"tasks.tabAgent": ["Agent runs", "智能体运行"],
	"tasks.runs": ["Loop runs", "闭环运行"],
	"tasks.scope": [
		"All tasks for brands you can access in this account. Active tasks are included regardless of the selected period.",
		"查看此账户内你有权限访问的所有品牌任务；进行中的任务不受时间范围限制。",
	],
	"tasks.search": ["Search task name, brand or ID", "搜索任务名称、品牌或 ID"],
	"tasks.type": ["Type", "类型"],
	"tasks.allBrands": ["All brands", "全部品牌"],
	"tasks.allTypes": ["All types", "全部类型"],
	"tasks.period": ["Period", "时间范围"],
	"tasks.days": ["Last {n} days", "最近 {n} 天"],
	"tasks.allTime": ["All time", "全部时间"],
	"tasks.running": ["Running", "运行中"],
	"tasks.queued": ["Queued", "排队中"],
	"tasks.failed": ["Failed", "失败"],
	"tasks.done": ["Completed", "已完成"],
	"tasks.skipped": ["Skipped", "已跳过"],
	"tasks.all": ["All tasks", "全部任务"],
	// Not a status the server keeps: a paused task is queued with a mark on it,
	// and one that was running is still settling the answers it had already
	// bought when somebody pressed stop.
	"tasks.paused": ["Paused", "已暂停"],
	"tasks.pausing": ["Stopping", "正在停止"],
	"tasks.pause": ["Pause", "暂停"],
	"tasks.resume": ["Resume", "继续"],
	"tasks.retry": ["Try again", "重试"],
	"tasks.name": ["Task", "任务名称"],
	"tasks.started": ["Started", "开始时间"],
	"tasks.finished": ["Finished", "结束时间"],
	"tasks.created": ["Queued", "创建时间"],
	"tasks.scheduled": ["Scheduled after", "计划执行时间"],
	"tasks.progress": ["Progress", "进度"],
	"tasks.attempts": ["Attempts / limit", "已尝试 / 最多次数"],
	"tasks.details": ["Task details", "任务详情"],
	"tasks.id": ["Task ID", "任务 ID"],
	"tasks.total": ["{n} tasks", "共 {n} 个任务"],
	"tasks.page": ["Page {n} of {total}", "第 {n} / {total} 页"],
	"tasks.previous": ["Previous", "上一页"],
	"tasks.next": ["Next", "下一页"],
	"tasks.empty": ["No tasks match these filters.", "没有符合筛选条件的任务。"],
	"tasks.accountTask": ["Account task", "账户任务"],
	"tasks.noStart": [
		"Not started, or the start time was not recorded for this older task.",
		"尚未开始，或该历史任务未记录开始时间。",
	],
	"tasks.reason": ["Failure or skip reason", "失败或跳过原因"],
	"tasks.workerLost": ["The worker stopped before this task finished.", "执行进程中断，任务未能完成。"],
	"tasks.result": ["View result", "查看结果"],
	"tasks.openResult": ["Open result page", "打开结果页面"],
	"tasks.timeline": ["Execution history", "执行记录"],
	"tasks.attemptStarted": ["Attempt {n} started", "第 {n} 次执行开始"],
	"tasks.viewRunTasks": ["View all {n} tasks in this run", "查看本次运行的全部 {n} 个任务"],
	"runs.brand": ["Brand", "品牌"],

	// -- opportunities
	//
	// The one screen that says what to do rather than what happened, and the
	// only one whose advice is mostly about places Ondit does not publish.
	"opps.lede": [
		"What to do next about your measurements — including the moves Ondit cannot make for you.",
		"针对你的测量结果，接下来该做什么——包括 Ondit 无法替你完成的那些动作。",
	],
	"opps.summary": ["The picture", "整体判断"],
	"opps.risks": ["Where this is hard", "难做的地方"],
	"opps.risksHint": [
		"Named on purpose. A plan with no hard parts in it is a plan nobody checked.",
		"特意列出来。一份没有难点的计划，是没人核对过的计划。",
	],
	"opps.category.listing": ["A page on Ondit", "Ondit 上的页面"],
	"opps.category.owned": ["Your own site", "你自己的站点"],
	"opps.category.outreach": ["Somebody else's page", "别人的页面"],
	"opps.category.community": ["Where people are talking", "人们讨论的地方"],
	"opps.categoryHint.listing": [
		"Already detected as a gap. One click and one credit each, on the Gaps screen.",
		"这些已经在缺口里检测出来了。在「缺口」页面点一下，每个消耗一份积分。",
	],
	"opps.categoryHint.owned": [
		"Something to publish or change on a domain you own.",
		"在你自己拥有的域名上发布或修改的东西。",
	],
	"opps.categoryHint.outreach": [
		"Assistants lean on independent sources far more than on a brand's own site, so this is usually the largest part of a plan.",
		"AI 助手更依赖独立的第三方来源，而不是品牌自己的站点，所以这通常是计划里最大的一块。",
	],
	"opps.categoryHint.community": [
		"Forums, Q&A and video — the conversations assistants quote.",
		"论坛、问答和视频——AI 助手会引用的那些讨论。",
	],
	"opps.format": ["What to make", "要做的东西"],
	"opps.mustCover": ["It has to answer", "必须回答"],
	"opps.proof": ["The proof that would win it", "能拿下它的证据"],
	"opps.nMoves": ["{n} to do", "{n} 项"],
	"opps.questions": ["Questions this helps", "对应的问题"],
	"opps.rivalPages": ["What is cited instead", "目前被引用的页面"],
	"opps.yourPages": ["Your pages already cited", "你已被引用的页面"],
	"opps.copy": ["Copy brief", "复制简报"],
	"opps.copied": ["Copied", "已复制"],
	"opps.copyFailed": [
		"The browser refused the clipboard. The card is selectable.",
		"浏览器拒绝了剪贴板。卡片内容可以直接选中复制。",
	],
	"opps.source": ["From Ondit · {when}", "来自 Ondit · {when}"],
	"opps.sourceUndated": ["From Ondit", "来自 Ondit"],
	"opps.generate": ["Write the plan", "生成计划"],
	"opps.refresh": ["Write a new plan", "重新生成计划"],
	"opps.working": ["Reading a month of answers…", "正在读取一个月的回答……"],
	"opps.done": ["Done", "已完成"],
	"opps.blocked": [
		"Not enough to advise on yet: {answers} answers in the last {days} days, {needed} needed.",
		"暂时没有可给的建议：近 {days} 天有 {answers} 条回答，至少需要 {needed} 条。",
	],
	"opps.failed": ["The plan could not be written. Your credits were returned.", "计划没能写出来，积分已退回。"],
	"opps.emptyTitle": ["No plan yet", "还没有计划"],
	"opps.empty": [
		"One call over the last month of your answers; it is stored, so reading it again is free.",
		"它会基于你最近一个月的回答做一次分析；结果会保存下来，之后再看不再花费。",
	],
	"opps.generatedLabel": ["Written", "生成时间"],
	"opps.windowLabel": ["Read over", "分析范围"],
	"opps.windowDays": ["the last {n} days", "最近 {n} 天"],

	// -- pages
	"pages.title": ["Pages", "页面"],
	"pages.lede": [
		"What we have published for you, and whether it moved anything.",
		"我们为你发布的内容，以及它有没有带来变化。",
	],
	"pages.status.draft": ["Draft", "草稿"],
	"pages.previousLive": ["The approved version remains live", "已审批版本仍在线"],
	"pages.status.review": ["Needs review", "待审核"],
	"pages.publishPending": [
		"Publication is queued. Its result will appear when processing completes.",
		"发布已排队，处理完成后将显示结果。",
	],
	"pages.publishFailed": [
		"Publication did not complete. Check the operation details and current page revision.",
		"发布未完成，请查看操作详情和当前页面版本。",
	],
	"pages.status.published": ["Published", "已发布"],
	"pages.status.unpublished": ["Unpublished", "已下架"],
	// Two headings, and the difference is the point. On the published list the
	// count is taken over the window in the bar above it, so it names none.
	// The editor has no bar, so its figure names its own week.
	"pages.cited": ["Cited", "被引用"],
	// The step before it. Two columns and not one, because "never fetched" and
	// "fetched and never cited" are different problems with different fixes.
	"pages.crawled": ["Fetched", "被抓取"],
	"pages.closesGap": ["Closes: {question}", "补的缺口：{question}"],
	"pages.citedThisWeek": ["Cited {count}× this week", "本周被引 {count} 次"],
	"pages.approve": ["Approve and publish", "批准并发布"],
	"pages.view": ["View", "查看"],
	"pages.empty": ["Nothing generated yet.", "还没有生成内容。"],
	"pages.revision": ["Revision {n}", "第 {n} 版"],
	// The two halves of the same table. Generating and publishing are separate
	// steps of the loop and the customer is asked to do a different thing in
	// each, so they are separate screens.
	"pages.draftsLede": [
		"Written from your gaps, waiting for you. Nothing goes on the directory until you approve it.",
		"根据你的缺口写出来的，等你过目。你不批准，就不会出现在品牌库里。",
	],
	"pages.generationStarted": ["Started {time}", "{time}开始"],
	"pages.viewArticle": ["View article", "查看文章"],
	"pages.generations": ["Article generation", "文章生成进度"],
	"pages.generationStatus.done": ["Generated", "已生成"],
	"pages.generation.done": [
		"Your article is ready. Open it to review its content and publishing status.",
		"文章已生成，可以打开查看内容和发布状态。",
	],
	"pages.generationStatus.queued": ["Queued", "排队中"],
	"pages.generationStatus.running": ["Generating", "生成中"],
	"pages.generationStatus.failed": ["Generation failed", "生成失败"],
	"pages.generationStatus.skipped": ["Generation skipped", "已跳过"],
	"pages.generation.queued": [
		"Waiting to start. The result will appear here automatically.",
		"正在等待开始，完成后结果会自动显示在这里。",
	],
	"pages.generation.running": [
		"Your article is being written. You can leave this page and return later.",
		"文章正在生成，你可以离开此页面，稍后回来查看。",
	],
	"pages.generation.failed": [
		"The article could not be generated. Please contact support if this persists.",
		"文章未能生成，若持续失败，请联系支持。",
	],
	"pages.generation.skipped": [
		"Generation did not proceed. Check Runs for the blocking condition.",
		"本次生成未执行，请前往运行记录查看限制条件。",
	],
	"pages.draftsEmpty": ["Nothing waiting for review.", "没有待审核的内容。"],
	"pages.publishedLede": [
		"Live on the directory, with how often each one was cited this week.",
		"已经在品牌库上线，以及每个页面本周被引用的次数。",
	],
	"pages.publishedEmpty": ["Nothing published yet.", "还没有发布任何内容。"],
	"pages.toGaps": ["See open gaps", "查看待处理缺口"],
	"pages.unpublish": ["Take down", "下架"],
	"pages.unpublishTitle": ["Take this page down?", "把这个页面下架？"],
	"pages.unpublishBody": [
		"The URL starts answering 410 and engines drop it from their index. A URL an answer engine has already learned is hard to get back.",
		"该链接会开始返回 410，引擎会把它从索引里删掉。回答引擎已经学会的链接，很难再拿回来。",
	],
	"pages.publishedAt": ["Published", "发布时间"],

	// -- the editor
	//
	// A page nobody can read before approving is a page nobody reads. The whole
	// screen is built on one rule: a human editing this text is under exactly the
	// same constraint as the model that wrote it — only the facts in the table.
	"editor.requestRevision": ["Request a revision", "退回并自动修订"],
	"editor.revisionFeedback": ["What needs to change?", "需要修改什么？"],
	"editor.revisionHint": [
		"Unsaved edits are saved first. Revision uses credits and the existing sourced facts. The result returns for review before publication.",
		"未保存的修改会先保存。修订将消耗积分，并使用已有来源事实；完成后仍需审核才能发布。",
	],
	"editor.revisionQueued": ["Revision queued for review", "修订任务已排队，完成后等待审核"],
	"settings.missingFacts": ["Other facts to complete ({n})", "待补充的其他事实（{n}）"],
	"settings.collectFacts": ["Collect missing facts", "自动补充缺失事实"],
	"settings.collectingFacts": ["Reading the website…", "正在读取官网…"],
	"settings.collectedFacts": ["Added {n} sourced facts", "已补充 {n} 条有来源的事实"],
	"settings.collectFactsHint": [
		"Reads the brand website and the public pages named for it, using AI. Uses credits and preserves existing facts.",
		"使用 AI 读取品牌官网和填写的公开页面，消耗积分，保留已有事实。",
	],

	"editor.mode": ["Article view", "文章视图"],
	"editor.read": ["Read article", "阅读全文"],
	"editor.edit": ["Edit blocks", "编辑内容"],
	"editor.revising": [
		"Revision is running. This page updates automatically when it is ready for review.",
		"正在返修，完成后本页会自动展示待审结果。",
	],
	"editor.revisionFailed": [
		"Revision failed. Your previous article is preserved. Adjust the feedback before requesting another revision.",
		"返修失败，原文已保留。请调整反馈后再次请求修订。",
	],

	"editor.liveRevisionHint": [
		"The approved version remains live. Saving or requesting a revision creates a draft; approve it to replace the public article.",
		"已审核版本继续在线。保存或自动修订只创建草稿；再次审核发布后才会替换公开文章。",
	],
	"editor.title": ["Edit article", "编辑文章"],
	"editor.back": ["All drafts", "返回草稿列表"],
	"editor.meta": ["Title and summary", "标题与摘要"],
	"editor.pageTitle": ["Title", "标题"],
	"editor.description": ["Summary", "摘要"],
	"editor.descriptionHint": [
		"What a search engine and an assistant show under the title. One sentence.",
		"搜索引擎和助手会显示在标题下面的那句话。一句就好。",
	],
	"editor.blocks": ["The page", "页面内容"],
	"editor.save": ["Save", "保存"],
	"editor.saving": ["Saving…", "保存中…"],
	"notebook.question": ["Question", "问题"],
	"notebook.answer": ["Answer", "回答"],
	"notebook.sources": ["Sources", "来源"],
	"notebook.source": ["Source", "来源"],
	"notebook.url": ["URL", "链接"],
	"notebook.metric": ["Metric", "指标"],
	"notebook.value": ["Value", "数值"],
	"notebook.unit": ["Unit", "单位"],
	"notebook.series": ["Series", "数据系列"],
	"notebook.notes": ["Notes", "笔记"],
	"editor.notebookTools": ["AI assistant & references", "AI 助手与参考资料"],
	"editor.titleRequired": ["The title cannot be empty.", "文章标题不能为空。"],
	"editor.sourceInvalid": [
		"Every source needs a valid HTTP or HTTPS URL.",
		"每个来源都需要有效的 HTTP 或 HTTPS 链接。",
	],
	"editor.documentError": [
		"Check the document title and table columns. This edit cannot be saved without losing content.",
		"请检查文章标题和表格列，当前编辑无法完整保存。",
	],
	"editor.saved": ["Saved as revision {n}", "已保存为第 {n} 版"],
	"editor.unsaved": ["Unsaved changes", "有未保存的修改"],
	"editor.preview": ["Open the live page", "打开线上页面"],
	"editor.articleInfo": ["Article information", "文章信息"],
	"editor.addAfter": ["Insert a block below", "在下方添加内容块"],
	"editor.add": ["Add a block", "添加内容块"],
	"editor.moveUp": ["Move up", "上移"],
	"editor.moveDown": ["Move down", "下移"],
	"editor.remove": ["Remove this block", "删除这个块"],
	"editor.words": ["{n} of {max} words", "{n} / {max} 词"],
	"editor.answerLong": [
		"Over the cap. An answer that long is one an assistant summarises instead of quoting.",
		"超过上限。这么长的段落，助手会自己改写而不是原样引用。",
	],

	"block.answer": ["Direct answer", "直接回答"],
	"block.answerHint": [
		"The paragraph an assistant can lift whole. Exactly one, first, within the count shown below it — a page without it can be read but not quoted.",
		"助手可以整段照搬的那一段。有且只有一段，放在最前面，长度不超过下方显示的上限——没有它的页面能被读，但不能被引用。",
	],
	"block.section": ["Section", "小节"],
	"block.sectionHeading": ["Heading", "标题"],
	"block.sectionBody": ["Body", "正文"],
	"block.sectionHint": ["Markdown: emphasis, links and lists.", "支持 Markdown：强调、链接、列表。"],
	"block.table": ["Table", "表格"],
	"block.tableCaption": ["Caption", "表格说明"],
	"block.addRow": ["Add a row", "添加一行"],
	"block.addColumn": ["Add a column", "添加一列"],
	"block.removeRow": ["Remove this row", "删除这一行"],
	"block.faq": ["Questions and answers", "问答"],
	"block.faqQ": ["Question", "问题"],
	"block.faqA": ["Answer", "回答"],
	"block.addFaq": ["Add a question", "添加一个问题"],
	"block.sources": ["Sources", "来源"],
	"block.sourceLabel": ["Label", "名称"],
	"block.sourceUrl": ["Link", "链接"],
	"block.addSource": ["Add a source", "添加来源"],
	"block.stat": ["Figure", "数据"],
	"block.statLabel": ["Label", "名称"],
	"block.statValue": ["Value", "数值"],
	"block.statUnit": ["Unit", "单位"],
	"block.chart": ["Chart", "图表"],
	"block.chartLocked": [
		"A chart's data comes from a measurement, so it is not edited here. Remove it if it does not belong.",
		"图表的数据来自测量，不在这里编辑。如果不该出现，删掉它。",
	],
	"block.remove": ["Remove", "删除"],

	// The client-side copies of the server's rules. Same words, said before the
	// save rather than after it.
	"editor.rule.oneAnswer": [
		"A generated page needs exactly one direct answer, and it has to come first.",
		"生成的页面需要且只需要一段直接回答，而且必须放在最前面。",
	],
	"editor.rule.tableWidth": ["Every row has to be as wide as the columns.", "每一行的单元格数量必须和列数一致。"],
	"editor.rule.empty": ["Every block needs something in it.", "每个块都不能是空的。"],

	"editor.facts": ["Facts this page may use", "这个页面可以使用的事实"],
	"editor.factsHint": [
		"Everything the page is allowed to assert, with its source. A sentence that is not supported here is the sentence the checker flags next time.",
		"页面能够断言的全部内容，以及它们的来源。写在这里之外的句子，没有依据。",
	],
	"editor.factsEmpty": ["No sourced facts on this brand yet.", "这个品牌还没有带来源的事实。"],
	"editor.revisionLabel": ["Revision", "版本号"],
	"editor.citationsLabel": ["Citations this week", "本周被引次数"],
	"editor.archivedBeforeApproval": ["Saved before approval replacement", "审批替换前留档"],
	"editor.revisions": ["Revisions", "版本"],
	"editor.reflow": ["Two weeks later", "两周后"],
	"editor.status": ["Status", "状态"],

	// -- new content from a template
	"compose.templatePlaceholder": ["Choose a writing template", "选择写作模板"],
	"compose.title": ["Write something", "写点东西"],
	"compose.lede": [
		"Choose a format based on the question you want to answer. Content uses your sourced facts; model usage is charged as it runs.",
		"根据要回答的问题选择内容类型。文章使用品牌已有的事实与来源，模型费用按实际用量扣除积分。",
	],
	"compose.balance": ["Credits left", "剩余积分"],
	"compose.choose": ["What to write", "写什么"],
	"compose.target": ["Compare with", "和谁对比"],
	"compose.targetHint": [
		"A comparison needs three facts the two of you both publish. A table where one side is blank is not a comparison.",
		"一次对比需要三条你们双方都公开的事实。一边空着的表格，不叫对比。",
	],
	"compose.shared": ["{n} shared facts", "{n} 条共同事实"],
	"compose.noTargetsTitle": ["Nothing to compare against yet", "还没有可对比的对象"],
	"compose.noTargets": [
		"Nobody in your category has published enough facts to compare against.",
		"你的分类里，还没有哪个品牌公开了足够多的事实可以拿来对比。",
	],
	"compose.quality": ["How much to spend on the writing", "在写作上花多少"],
	"compose.quality.standard": ["Standard", "标准"],
	"compose.quality.standardWhy": [
		"A fast model drafts, a better one rewrites. Most pages are a table and four paragraphs off a fact list, and this writes those indistinguishably.",
		"用快的模型起草，用更好的模型重写。多数页面无非是一张表加四段话，这一档写出来看不出区别。",
	],
	"compose.quality.deep": ["Deep", "深度"],
	"compose.quality.deepWhy": [
		"Both passes run a tier up. Worth it for the page you are about to stake a comparison on; wasted on a correction FAQ.",
		"两遍都往上升一档。要拿去做对比的那个页面值得；用在澄清问答上就是浪费。",
	],
	"compose.start": ["Write it", "开始写"],
	"compose.queued": [
		"Queued. It takes a few minutes, and lands in Drafts.",
		"已排队。需要几分钟，写完会出现在「草稿」里。",
	],
	"compose.blocked": ["Cannot run yet", "暂时不能运行"],
	"compose.recipe.entity.enrich": ["Your own listing", "你自己的条目页"],
	"compose.recipe.entity.enrichWhy": [
		"The page an assistant lands on when somebody asks what you are. Built from your facts, with the answer paragraph first.",
		"有人问「你们是什么」时，助手会读到的那个页面。用你的事实写成，直接回答放在最前面。",
	],
	"compose.recipe.faq.clarify": ["A correction", "一条澄清"],
	"compose.recipe.faq.clarifyWhy": [
		"A short question-and-answer page that puts a checkable fact where an assistant said something wrong.",
		"一个简短的问答页面，在助手说错的地方放上一条可核实的事实。",
	],
	"compose.recipe.vs.generate": ["A comparison", "一个对比页"],
	"compose.recipe.vs.generateWhy": [
		"You against one named rival, on the facts you both publish. The question people actually type.",
		"你和一个指定竞品，就双方都公开的事实做对比。这是人们真正会输入的问题。",
	],
	"compose.recipe.alternatives.generate": ["Alternatives to you", "「你的替代品」页"],
	"compose.recipe.alternatives.generateWhy": [
		'The page that answers "what else is there" — and is the page a rival would otherwise own.',
		"回答「还有什么别的选择」的那个页面——否则这个页面就归竞品所有了。",
	],
	"compose.recipe.best.generate": ["A best-of list", "一个「最佳」榜单页"],
	"compose.recipe.answer.rewrite": ["A rewritten answer", "一条重写的回答"],

	// -- factcheck
	"factcheck.lede": [
		"Things assistants said about you that your own verified facts contradict. Your datapoints are the ground truth here.",
		"助手说过的、与你自己已核实事实相矛盾的内容。这里以你的数据点为准。",
	],
	"factcheck.claim": ["What AI said", "AI 说的"],
	"factcheck.truth": ["What is true", "事实"],
	"factcheck.severity": ["Severity", "严重度"],
	"factcheck.severity.high": ["High", "高"],
	"factcheck.severity.medium": ["Medium", "中"],
	"factcheck.severity.low": ["Low", "低"],
	"factcheck.emptyTitle": ["No contradictions recorded", "没有矛盾记录"],
	"factcheck.empty": [
		"Nothing in this scope contradicts your facts. This does not confirm that every statement is accurate.",
		"当前范围内没有与你的事实相矛盾的表述。这不代表所有表述都已验证准确。",
	],

	// -- reflow
	"reflow.lede": [
		"Two weeks after each page went live, the same questions again. This is the only number that says whether the work worked.",
		"每个页面上线两周后，重问同一批问题。这是唯一能说明这份工作有没有用的数字。",
	],
	"reflow.before": ["Before", "之前"],
	"reflow.after": ["After", "之后"],
	"reflow.delta": ["Change", "变化"],
	"reflow.significant": ["Significant", "显著"],
	"reflow.notSignificant": ["No conclusion", "暂无结论"],
	"reflow.emptyTitle": ["No reports yet", "还没有报告"],
	"reflow.empty": [
		"The first arrives two weeks after your first page goes live, once there are answers from both sides of it.",
		"第一份会在你的第一个页面上线两周后出现，那时页面前后两侧才都有回答可比。",
	],

	// -- badge
	"badge.lede": [
		"Put this on your site. It shows how often AI cited you this week, and the link back is what tells engines the two are the same product.",
		"把它放到你的站点上。它显示本周 AI 引用你的次数，而回链是告诉引擎“这两个是同一个产品”的方式。",
	],
	"writing.bold": ["Bold", "加粗"],
	"writing.italic": ["Italic", "斜体"],
	"writing.bullets": ["Bullet list", "无序列表"],
	"writing.numbered": ["Numbered list", "有序列表"],
	"writing.undo": ["Undo", "撤销"],
	"writing.redo": ["Redo", "重做"],
	"writing.assistant": ["AI writing assistant", "AI 写作助手"],
	"writing.clarify": ["Make clearer", "润色表达"],
	"writing.shorten": ["Shorten", "精简内容"],
	"writing.expand": ["Add detail", "补充细节"],
	"writing.outline": ["Improve structure", "优化结构"],
	"writing.clarifyPrompt": [
		"Make the wording clearer while preserving meaning, verified facts and sources.",
		"润色表达，使语言清晰易懂，保留原意、已验证事实与来源。",
	],
	"writing.shortenPrompt": [
		"Remove repetition and shorten the article while preserving essential facts and sources.",
		"删除重复、精简文章，保留关键事实与来源。",
	],
	"writing.expandPrompt": [
		"Add useful explanation using only the available verified facts. Do not invent examples, claims or numbers.",
		"仅使用已有已验证事实补充有用的解释，不编造案例、主张或数字。",
	],
	"writing.outlinePrompt": [
		"Improve the sequence of sections and headings around the reader's questions; keep the answer first and preserve sources.",
		"围绕读者问题优化段落顺序与标题，保持直接回答在首位并保留来源。",
	],
	"nav.templates": ["Writing templates", "写作模板"],
	"templates.title": ["Writing templates", "写作模板"],
	"templates.lede": [
		"Choose an angle. Every template uses your verified facts and sources.",
		"选择写作角度，所有模板都使用已验证的事实和来源。",
	],
	"templates.search": ["Search templates", "搜索模板"],
	"templates.use": ["Use template", "使用模板"],
	"nav.distribution": ["Distribution", "分发与排期"],
	"reading.distribution": ["Distribution", "分发与排期"],
	"reading.templates": ["Writing templates", "写作模板"],
	"distribution.lede": [
		"Adapt published originals for other platforms, then record the links you publish.",
		"将已发布原文适配到其他平台，并记录转发链接。",
	],
	"distribution.prepare": ["Prepare a post", "准备转发"],
	"distribution.platforms": ["Platform library", "平台库"],
	"distribution.history": ["Distribution records", "转发记录"],
	"distribution.search": ["Search platforms", "搜索平台"],
	"distribution.rulesHint": [
		"Capabilities cover publishing on the platform, not automatic posting from Ondit. Conditions and research sources are listed per platform; unresolved rules remain marked Check.",
		"支持情况指平台的发布能力，不代表 Ondit 自动发布能力。各平台列出适用条件及检索来源，缺少可靠依据的规则仍标为待确认。",
	],
	"distribution.conditions": ["Conditions & sources", "适用条件与来源"],
	"distribution.sources": ["Sources", "核查来源"],
	"distribution.logoSource": ["Logo source", "Logo 来源"],
	"distribution.reviewed": ["Reviewed {date}", "核查于 {date}"],
	"distribution.platform": ["Platform", "平台"],
	"distribution.format": ["Format", "内容形式"],
	"distribution.images": ["Images", "图片"],
	"distribution.video": ["Video / script", "视频 / 脚本"],
	"distribution.links": ["External links", "外部链接"],
	"distribution.article": ["Article", "文章"],
	"distribution.social": ["Short post", "短帖"],
	"distribution.yes": ["Supported", "支持"],
	"distribution.no": ["Not supported", "不支持"],
	"distribution.restricted": ["Conditional", "有条件支持"],
	"distribution.check": ["Check with platform", "待确认"],
	"distribution.status": ["Status", "状态"],
	"distribution.recorded": ["Repost recorded", "已记录转发"],
	"distribution.draft": ["Prepared draft", "适配草稿"],
	"distribution.empty": ["No distribution records yet.", "暂无转发记录。"],
	"distribution.chooseArticle": ["Choose a published article", "选择已发布文章"],
	"distribution.otherPlatforms": ["Choose another platform", "选择其他平台"],
	"distribution.platformSearchHint": [
		"Search other platforms by name or domain, then select one to prepare your post.",
		"按名称或域名搜索其他平台，选择后准备转发内容。",
	],
	"distribution.choosePlatform": ["Choose a platform", "选择转发平台"],
	"distribution.recommending": ["AI is recommending…", "AI 推荐中…"],
	"distribution.recommend": ["Recommend with AI", "AI 推荐平台"],
	"distribution.recommendHint": [
		"Uses the article and 90 days of citation sources. Model usage is billed to credits.",
		"结合文章与近 90 天引用来源，模型用量计入积分。",
	],
	"distribution.evidence": ["Observed in {n} answers over 90 days", "近 90 天出现在 {n} 条回答中"],
	"distribution.original": ["Ondit original", "Ondit 原文"],
	"distribution.rules": ["Platform rules / site", "平台规则 / 网站"],
	"distribution.stale": [
		"The original has a newer version. Review and update this adaptation before saving.",
		"原文已有新版本，请核对并更新适配稿后再保存。",
	],
	"distribution.previewHint": [
		"Content preview only; the platform controls the final layout. Video formats require recording and media production.",
		"此处为内容预览，最终版式以平台为准；视频内容仍需录制和制作素材。",
	],
	"distribution.missingSource": [
		"The original link is missing. Keep source attribution wherever the platform allows it.",
		"适配稿缺少原文链接，请在平台允许的位置保留原文出处。",
	],
	"distribution.length": ["{n} characters", "{n} 个字符"],
	"distribution.tooLong": ["Over the reference limit; shorten before posting.", "超过参考上限，请精简后再转发。"],
	"distribution.publishedUrl": ["Published third-party URL (optional)", "第三方转发链接（可稍后录入）"],
	"distribution.recordHint": [
		"Pasting a URL records your repost; it does not mean a backlink or citation has been verified.",
		"录入链接用于追溯转发，不代表已验证回链或检测到引用。",
	],
	"distribution.save": ["Save record", "保存记录"],
	"distribution.saved": ["Distribution record saved", "转发信息已保存"],
	"distribution.copy": ["Copy text / Markdown", "复制文本 / Markdown"],
	"distribution.copyRich": ["Copy rich text", "复制富文本"],
	"distribution.copyFailed": [
		"Copy unavailable. Select the content or export it.",
		"暂时无法复制，请选择内容复制或导出。",
	],
	"distribution.export": ["Export Markdown", "导出 Markdown"],
	"distribution.open": ["Open platform", "前往平台"],
	"distribution.preview": ["Adaptation preview", "适配预览"],
	"distribution.add": ["Add platform", "添加平台"],
	"distribution.limit": ["Character limit (optional)", "字数上限（可选）"],
	"badge.preview": ["Ondit citation badge", "Ondit 引用徽章"],
	"badge.html": ["HTML", "HTML"],
	"badge.markdown": ["Markdown", "Markdown"],
	"badge.backlinkOk": ["Backlink found", "已找到回链"],
	"badge.backlinkMissing": ["Backlink not found", "未找到回链"],
	"badge.backlinkUnchecked": ["Not checked yet", "尚未检查"],
	"badge.copy": ["Copy", "复制"],
	"badge.copied": ["Copied", "已复制"],

	// -- settings: the page's navigation, one row per section of the catalogue
	"settings.nav": ["Settings navigation", "设置导航"],
	"settings.group.personal": ["Personal", "个人"],
	"settings.group.account": ["Account", "账户"],
	"settings.group.workspace": ["Workspace", "工作区"],
	"settings.section.profile": ["Profile", "个人资料"],
	"settings.section.profileHint": ["Manage your name, password and sign-in.", "管理你的姓名、密码和登录方式。"],
	"settings.section.appearance": ["Appearance", "外观"],
	"settings.section.appearanceHint": ["Choose light, dark or the system theme.", "选择浅色、深色或跟随系统。"],
	"settings.section.language": ["Language", "语言"],
	"settings.section.languageHint": ["Choose the language this workspace reads in.", "选择这个工作区的界面语言。"],
	"settings.section.density": ["Density", "密度"],
	"settings.section.densityHint": ["Set how much of a table fits on one screen.", "决定一屏能显示多少表格内容。"],
	"settings.section.members": ["Members", "成员"],
	"settings.section.membersHint": [
		"Invite colleagues and set what each may edit.",
		"邀请同事，并决定各自可以编辑什么。",
	],
	"settings.section.subscription": ["Subscription", "订阅"],
	"settings.section.subscriptionHint": ["Change the plan, its seats and its brands.", "更换套餐，调整席位与品牌数量。"],
	"settings.section.usage": ["Usage and billing", "用量与账单"],
	"settings.section.usageHint": ["Read what credits and model calls were spent.", "查看积分与模型调用的花费明细。"],
	"settings.section.identity": ["Brand identity", "品牌身份"],
	"settings.section.identityHint": ["Name the brand, its site and its other names.", "填写品牌名称、官网和其他名称。"],
	"settings.section.facts": ["Brand facts", "品牌事实"],
	"settings.section.factsHint": [
		"Record the sourced facts every page is built from.",
		"记录带来源的事实，页面据此生成。",
	],
	"settings.section.measurement": ["Measurement", "测量"],
	"settings.section.measurementHint": ["Choose the engines, rivals and analysis model.", "选择引擎、竞品和分析模型。"],
	"settings.section.filing": ["Filing", "口径"],
	"settings.section.filingHint": [
		"Decide how citations, names and rivals are counted.",
		"决定引用、名称和竞品如何计入。",
	],
	"settings.section.voice": ["Voice", "语气"],
	"settings.section.voiceHint": ["Set the words a page under this brand may use.", "规定这个品牌页面可以使用的措辞。"],
	"settings.integrationNotFound": ["Integration not found", "未找到此集成"],
	"settings.section.integrations": ["Integrations", "集成"],
	"settings.section.integrationsHint": [
		"Connect services and manage workspace connections.",
		"连接外部服务，管理工作区的集成账号。",
	],
	"settings.section.secrets": ["Secrets", "密钥"],
	"settings.section.secretsHint": [
		"Store the keys workflows and tools authenticate with.",
		"保存工作流和工具用于鉴权的密钥。",
	],

	// -- the brand's own settings
	"settings.brandName": ["Primary brand name", "主品牌名称"],
	"settings.brandNameHint": [
		"The previous name remains an alias. Published article URLs stay unchanged; revise article text separately.",
		"原名称会保留为别名。已发布文章的链接不变，正文需要单独修订。",
	],
	"settings.categories": ["Categories", "类目"],
	"settings.categoriesHint": [
		"What the brand sells, from Shopify's product taxonomy. Up to five, at any level: Apparel & Accessories for a brand that sells all of it, Athletic Shoes for one that makes only those. Peers and questions are drawn from these.",
		"品牌卖什么，取自 Shopify 标准产品分类。最多 5 个，哪一层都可以：全品类服饰品牌选「服饰与配饰」，只做运动鞋的选「运动鞋」。同类对比和生成的问题都以此为准。",
	],
	"category.placeholder": ["Choose categories", "选择类目"],
	"category.search": ["Search categories", "搜索类目"],
	"category.empty": ["No category matches that", "没有匹配的类目"],
	"category.loading": ["Loading categories…", "正在加载类目…"],
	"category.back": ["Back", "返回上一级"],
	"category.whole": ["All of {name}", "选择整个「{name}」"],
	"category.full": ["Up to {max} categories. Remove one to add another.", "最多 {max} 个类目，先移除一个再添加。"],
	"settings.logo": ["Brand logo", "品牌 Logo"],
	"settings.logoHint": [
		"PNG, JPEG, WebP or GIF, up to 5 MB. Resized proportionally to at most 64 × 64 pixels when saved.",
		"支持 PNG、JPEG、WebP、GIF，最大 5 MB。保存时等比例缩小至不超过 64 × 64 像素。",
	],
	"settings.logoInvalid": ["Choose a valid image up to 5 MB.", "请选择不超过 5 MB 的有效图片。"],
	"settings.website": ["Official website", "官网地址"],
	"settings.sourcePages": ["Public pages about the brand", "介绍品牌的公开页面"],
	"settings.sourcePagesHint": [
		"For a brand with no website, or one whose site says little: a marketplace store, a business registry entry, a certificate lookup. One address per line, up to five. Collecting facts reads these pages beside the website and cites them.",
		"适合没有官网或官网信息很少的品牌：电商店铺、企业信用信息公示、认证证书查询等。每行一个地址，最多五个。补充事实时会连同官网一起读取这些页面，并以它们为来源。",
	],
	"settings.websiteHint": [
		"Use an HTTP or HTTPS URL. Its domain is added to matching; existing domains and source links are retained.",
		"填写 HTTP 或 HTTPS 地址。新域名会加入匹配，已有域名和事实来源链接保留。",
	],
	"settings.aliases": ["Other names", "其他名称"],
	"settings.aliasesHint": [
		"Names an assistant might use instead of the official one. One per line. Saving removes duplicates and the primary name, ignoring case and surrounding spaces.",
		"助手可能用来代替正式名称的叫法。每行一个。保存时自动去除与主名称相同或重复的名称，忽略大小写和首尾空格。",
	],
	"settings.domains": ["Domains you own", "你拥有的域名"],
	"settings.domainsHint": [
		"A citation of any of these counts as a citation of you. One per line.",
		"引用这些域名中的任何一个都算作引用了你。每行一个。",
	],
	"settings.taglineEn": ["Tagline (English)", "一句话介绍（英文）"],
	"settings.taglineZh": ["Tagline (Chinese)", "一句话介绍（中文）"],
	"settings.pause": ["Pause tracking for this brand", "暂停该品牌的追踪"],
	"settings.pauseHint": [
		"Stops the spending on this brand at the next call, including work already queued. Its questions and its history stay, published pages stay up, and the other brands in this account keep running. The account-wide switch is on Runs.",
		"从下一次调用开始停止该品牌的花费，包括已排队的任务。它的问题和历史都保留，已发布的页面保持在线，账户里的其他品牌照常运行。账户级开关在「运行」页面。",
	],
	"settings.researchModelChoose": ["Choose analysis model", "选择分析模型"],
	"settings.researchModelProvider": ["Provider", "提供商"],
	"settings.researchModelOther": ["Other", "其他"],
	"settings.researchModel": ["Analysis model", "分析模型"],
	"settings.researchModelHint": [
		"Which model reads your measurements and writes your opportunities. Nothing it writes is published, so this is yours to pick — and you are billed at the model's own list price.",
		"用哪个模型读取你的测量结果、生成机会报告。它写的内容不会被发布，所以这个由你来选——按该模型的官方标价计费。",
	],
	"settings.researchModelDefault": ["Default ({model})", "默认（{model}）"],
	"settings.researchModelSearch": ["Search by name", "按名称搜索"],
	"settings.researchModelProviderAll": ["All providers", "全部提供商"],
	"settings.researchModelNone": [
		"No model matches this search. Clear it to see the whole list.",
		"没有模型匹配当前筛选。清空后可看到完整列表。",
	],
	"settings.researchModelPrice": [
		"{input} in / {output} out per 1M tokens, USD",
		"每百万 token：输入 {input} / 输出 {output} 美元",
	],
	"settings.identity": ["Who this brand is", "这个品牌是谁"],
	"settings.identityHint": [
		"What an assistant might call it, and which domains count as its own.",
		"助手可能会怎么称呼它，以及哪些域名算是它自己的。",
	],
	"settings.measurement": ["How it is measured", "怎么测量"],
	"settings.measurementHint": [
		"Which assistants are asked, who does not count as a rival, and what reads the answers.",
		"去问哪些助手、谁不算竞品，以及用什么模型读这些答案。",
	],
	"settings.taglineCount": ["{n} of 160", "{n} / 160"],
	"settings.datapointsEnough": [
		"{n} facts and {sources} distinct sources, as recommended. Review each brief for facts that support its specific question.",
		"已有 {n} 条事实和 {sources} 个不同来源，达到建议数量；仍需检查每份简报的事实是否足以支持对应问题。",
	],
	"settings.datapointsShort": [
		"{n} facts and {sources} distinct sources. Pages can still be generated, but they can only state what these facts say; {min} facts and {minSources} sources are recommended.",
		"已有 {n} 条事实和 {sources} 个不同来源。仍可生成内容，但页面只能写这些事实里有的内容；建议至少 {min} 条事实和 {minSources} 个来源。",
	],
	"settings.datapointsNone": [
		"This brand's industry defines no facts yet, so there is nothing to fill in here.",
		"这个品牌所在的行业还没有定义事实字段，这里暂时没有可填的内容。",
	],
	"settings.enginesChosen": ["{n} of {max} chosen", "已选 {n} / {max}"],
	"settings.valueUnknown": ["Not stated", "未填写"],
	"settings.valueYes": ["Yes", "是"],
	"settings.valueNo": ["No", "否"],
	"settings.unsaved": ["Unsaved changes", "有未保存的改动"],
	"settings.allSaved": ["Everything is saved", "改动都已保存"],
	"settings.revert": ["Discard them", "放弃改动"],
	"settings.save": ["Save", "保存"],
	"settings.saved": ["Saved", "已保存"],

	// -- plans
	// The pricing page on the directory renders the same cards from its own
	// copy of these lines (site/src/messages). Two surfaces, one price list:
	// a wording changed on one side and not the other is how a customer ends
	// up quoting a plan back that nobody sells.
	"plan.monthly": ["Monthly", "按月"],
	"plan.yearly": ["Yearly", "按年"],
	"plan.twoMonthsFree": ["2 months free", "省 2 个月"],
	"plan.perMonth": ["month", "月"],
	"plan.billedYearly": ["Billed yearly", "按年计费"],
	"plan.custom": ["Custom", "定制"],
	"plan.popular": ["Popular", "最常选"],
	"plan.subscribe": ["Subscribe", "订阅"],
	"plan.upgrade": ["Upgrade", "升级"],
	"plan.current": ["Current plan", "当前套餐"],
	"plan.contact": ["Talk to us", "联系我们"],
	"plan.engineOne": ["1 answer engine tracked", "追踪 1 个回答引擎"],
	"plan.engines": ["{n} answer engines tracked", "追踪 {n} 个回答引擎"],
	"plan.enginesAll": ["Every engine we track", "我们追踪的全部引擎"],
	"plan.questionsCustom": ["Tailored question tracking", "定制的问题追踪"],
	"plan.credits": ["{n} credits a month", "每月 {n} 积分"],
	"banner.trialEnding": [
		"Your trial month ends in {n} days. It does not renew — move up to a plan to keep measuring.",
		"体验月还有 {n} 天结束。它不会自动续费，升级套餐才能继续测量。",
	],
	"banner.trialToday": [
		"Your trial month ends today. It does not renew — move up to a plan to keep measuring.",
		"体验月今天结束。它不会自动续费，升级套餐才能继续测量。",
	],
	"banner.trialUpgrade": ["See pricing", "查看价格"],
	"plan.trialOnce": ["One month to try it. It does not renew.", "一个月体验期，不会自动续费。"],
	"plan.questions": ["{n} tracked questions a brand", "每个品牌追踪 {n} 个问题"],
	"plan.pages": ["{n} generated pages a month", "每月生成 {n} 个页面"],
	"plan.workflows": ["{n} custom workflows per account", "每账户 {n} 个自定义工作流"],
	"plan.workflowsUnlimited": ["Unlimited custom workflows", "自定义工作流不限数量"],
	"plan.builtinWorkflows": ["Built-in workflows do not count toward the limit", "内置工作流不占数量额度"],
	"workflow.quota": ["Custom workflows: {used} / {limit}", "自定义工作流：{used} / {limit}"],
	"workflow.quotaHint": [
		"Shared across this account. Built-in workflows are excluded; changing their definition counts as a custom workflow. Archive a custom workflow or change plans to add more.",
		"额度由账户下所有品牌共享。内置工作流不计入，修改其定义后按自定义计数。可归档自定义工作流或更换套餐以增加容量。",
	],
	"errors.gated.workflow_quota": [
		"Your custom workflow limit has been reached. Archive a custom workflow or change plans. Built-in workflows are not counted.",
		"自定义工作流数量已达套餐上限。请归档自定义工作流或更换套餐；内置工作流不占额度。",
	],
	"plan.agent": ["The Agent: workflows and automation", "Agent：工作流与自动化"],
	"plan.advanced": ["Query fan-outs and the opportunities report", "查询分支与机会报告"],
	"plan.brandOne": ["One brand", "一个品牌"],
	"plan.brands": ["{n} brands", "{n} 个品牌"],
	"plan.brandsMany": ["Many brands", "多个品牌"],
	"plan.seatOne": ["1 seat", "1 个席位"],
	"plan.seats": ["{n} seats", "{n} 个席位"],
	"plan.sso": ["SSO and a contract", "SSO 与合同"],
	"plan.supportDedicated": ["Dedicated support", "专属支持"],
	"plan.blurb.starter": [
		"For a brand that wants to know whether ChatGPT names it.",
		"想知道 ChatGPT 会不会提到自己的品牌。",
	],
	"plan.blurb.standard": [
		"For a team that wants to be recommended across the engines people use, and the pages to get there.",
		"想在人们常用的几个引擎里被推荐，并且拿到达成这一点的页面。",
	],
	"plan.blurb.pro": [
		"For a company with several brands, or one brand it takes seriously: five engines, five replications, the Agent and the readings beside it.",
		"有多个品牌，或者认真对待一个品牌：五个引擎、五次重复、Agent 和它旁边的高级读数。",
	],
	"plan.blurb.enterprise": [
		"For agencies and large teams running AEO across many brands, with a contract to match.",
		"代理机构和大团队，跨多个品牌运营 AEO，用合同来定。",
	],

	// -- billing
	"billing.plan": ["Plan", "套餐"],
	"billing.credits": ["Credits", "积分"],
	"billing.balance": ["Balance", "余额"],
	"billing.history": ["Every charge", "每一笔"],
	"billing.noCheckout": ["Payments are not set up on this deployment.", "这个部署还没有配置支付。"],
	"billing.runRequiresPlan": ["Subscribe to start conversations and run agents.", "订阅后即可发起对话和运行智能体。"],
	"billing.subscribeAction": ["View subscription plans", "查看订阅套餐"],
	"billing.checkoutUnavailable": ["Checkout is unavailable. Please try again.", "暂时无法打开结账，请重试。"],
	"billing.confirming": [
		"Payment confirmation is still pending. Refresh shortly to check your plan.",
		"支付结果仍在确认中，请稍后刷新查看套餐。",
	],
	"billing.paid": ["Paid. Your plan and this month's credits land in a moment.", "支付成功，套餐和本月积分马上到账。"],
	"billing.periodEnd": ["Renews", "续费日"],
	/** A subscription already scheduled to end — a cancellation, or the trial. */
	"billing.periodEnds": ["Ends", "结束日"],
	"billing.empty": ["Nothing yet.", "还没有记录。"],
	"billing.emptyBody": [
		"Every grant and every charge is recorded here, newest first.",
		"每一笔发放和每一笔扣费都会记在这里，最新的在前。",
	],
	"billing.noSubscription": ["No subscription on file", "没有订阅记录"],
	// Every status a subscription row can hold.
	"billing.status.active": ["Active", "生效中"],
	"billing.status.past_due": ["Payment failed", "扣款失败"],
	"billing.status.paused": ["Paused", "已暂停"],
	"billing.status.canceled": ["Cancelled", "已取消"],

	// -- errors and states
	// -- the assistant
	//
	// Two vocabularies meet here. The chrome is the panel's, so it reads like the
	// rest of the workbench; the tool labels are the *product's* five stages, in
	// the same words the sidebar uses, because a customer watching the assistant
	// work should be able to point at which stage it is in.
	// A lane that returns the reasoning's signature but not its words — which
	// is every one of them today — leaves nothing to expand, so the row itself
	// is the whole message.

	// One label per tool. The name the model calls is never shown: `list_gaps`
	// is a function, "Reading the gaps" is what happened.

	"state.loading": ["Loading", "加载中"],
	"state.loadingSlow": ["Still loading. The page will appear when ready.", "仍在加载，完成后会自动显示。"],
	"state.loadError": ["This content couldn't load", "暂时没能加载这部分内容"],
	"state.loadErrorBody": [
		"We couldn't retrieve the content. Please try again in a moment.",
		"这次未能获取到内容，可以稍后再试一次。",
	],
	"state.loadErrorHint": ["You can also visit another page and come back later.", "也可以先看看其他页面，稍后再回来。"],
	"state.sessionErrorHint": [
		"If this continues, give it a little time before trying again.",
		"如果仍然无法加载，可以过一会儿再试。",
	],
	"state.retrying": ["Trying again…", "正在重试…"],
	// The way out of an empty screen, as a verb. The reading's own name reads as
	// a status under a title that says there is nothing yet — "Published", under
	// "No reports yet", is a sentence about the report.
	"empty.goPrompts": ["Collect samples", "去采样"],
	"empty.goGaps": ["See content gaps", "去看内容缺口"],
	"empty.goPublished": ["See published pages", "去看已发布页面"],
	"state.crashBody": [
		"This screen stopped before it finished drawing. Trying again redraws this part; nothing you did was lost.",
		"这个界面没有画完就中断了。重试只会重新绘制这一块，你做过的操作不会丢。",
	],
	"state.error": ["Something went wrong", "出错了"],
	"state.retry": ["Try again", "重试"],
	"errors.unauthorized": ["Sign in to continue", "请先登录"],
	"errors.not_found": ["Not found", "未找到"],
	"errors.forbidden": ["Not allowed", "无权访问"],
	// Sign-in, where "sign in to continue" is the one sentence that says
	// nothing: it is the page they are on and the thing they just tried.
	"errors.rejected.bad_credentials": ["Email or password is incorrect", "邮箱或密码不正确"],
	"errors.forbidden.origin": ["Refused: unexpected origin", "已拒绝：来源不符"],
	"errors.rate_limited": ["Too many requests. Wait a moment.", "请求过于频繁，请稍候。"],
	"errors.conflict": ["That already exists", "已经存在"],
	"errors.bad_request": ["That request was not valid", "请求无效"],
	"errors.onboarding_topics_empty": ["Pick at least one topic", "请至少选择一个主题"],
	"errors.brand_engines_empty": ["Pick at least one engine", "请至少选择一个引擎"],
	"errors.brand_address_unusable": ["{address} is not a usable address", "{address} 不是可用的地址"],
	"errors.capability_input": ["That request's input is not valid", "请求输入无效"],
	"errors.email": ["Enter a valid email address", "请输入有效的邮箱地址"],
	"errors.locale": ["Choose a supported language", "请选择受支持的语言"],
	"errors.model_price_unavailable": ["That model has no published price right now", "该模型暂无公布的价格"],
	"errors.model_request": ["That model request is not valid", "模型请求无效"],
	"errors.profile_name": ["Enter a valid name", "请输入有效的名称"],
	"errors.unknown_capability": ["That capability is not available", "该能力不可用"],
	"errors.ingest_too_many_rows": ["A report can carry at most 1,000 rows", "单次报告最多 1000 行"],
	"errors.citation_domain_required": ["Enter a domain or URL", "请输入域名或网址"],
	"errors.citation_category_unknown": ["Choose a category from the list", "请从列表中选择分类"],
	"errors.citation_tags_empty": ["Add at least one tag", "请至少添加一个标签"],
	"errors.citation_url_required": ["Enter a full page URL", "请输入完整的页面网址"],
	"errors.insight_action_unknown": ["That action is not available here", "此处不支持该操作"],
	"errors.entity_kinds_empty": ["Choose at least one kind", "请至少选择一种类型"],
	"errors.insight_notes_too_long": ["Notes are limited to 500 characters", "备注最多 500 个字符"],
	"errors.competitor_name_invalid": ["Enter a name of up to 60 characters", "请输入不超过 60 个字符的名称"],
	"errors.topic_name_invalid": ["Enter a topic of up to 60 characters", "请输入不超过 60 个字符的主题"],
	"errors.prompt_shared": ["That prompt is shared and cannot be re-filed", "该提示词为共享提示词，不能重新归类"],
	"errors.conflict.question_tracked": ["That question is already tracked", "该问题已在跟踪中"],
	"errors.question_invalid": ["Enter a valid question and choose its intent", "请输入有效的问题并选择意图"],
	"errors.conflict.question_exists": ["That question already exists", "该问题已存在"],
	"errors.question_length": ["A question needs 8 to 300 characters", "问题需为 8 到 300 个字符"],
	"errors.generate_unknown_recipe": ["That kind of page cannot be generated", "无法生成该类型的页面"],
	"errors.generate_unknown_template": ["That writing template no longer exists", "该写作模板已不存在"],
	"errors.generate_template_mismatch": [
		"That writing template is for a different kind of page",
		"该写作模板适用于其他类型的页面",
	],
	"errors.request_id_required": ["The request is missing its id; reload and try again", "请求缺少标识，请刷新后重试"],
	"errors.page_revision_stale": [
		"The article changed since you opened it; reload and try again",
		"文章在你打开后已被修改，请刷新后重试",
	],
	"errors.page_title_empty": ["The title cannot be empty", "标题不能为空"],
	"errors.page_feedback_length": ["Revision feedback needs 1 to 4,000 characters", "修改意见需为 1 到 4000 个字符"],
	"errors.page_revision_draft_only": ["Only a draft can be sent back for revision", "只有草稿可以退回修改"],
	"errors.page_no_recipe": [
		"This page was not generated, so it cannot be regenerated",
		"该页面不是生成的，无法重新生成",
	],
	"errors.member_brand_role_unknown": ["Choose a brand role from the list", "请从列表中选择品牌角色"],
	"errors.member_brand_duplicate": ["A brand can be granted only once", "每个品牌只能授予一次"],
	"errors.member_role_unknown": ["Choose a role from the list", "请从列表中选择角色"],
	"errors.member_brands_required": ["A member needs at least one brand", "成员至少需要一个品牌"],
	"errors.billing_plan_unavailable": ["A subscription cannot move to that plan", "订阅无法切换到该方案"],
	"errors.billing_upgrade_via_checkout": ["Upgrades go through checkout", "升级请通过结账完成"],
	"errors.distribution_url_invalid": ["Enter a valid URL", "请输入有效的网址"],
	"errors.distribution_platform_invalid": ["Choose a platform from the list", "请从列表中选择平台"],
	"errors.distribution_capability_invalid": [
		"That platform does not support this kind of post",
		"该平台不支持此类发布",
	],
	"errors.distribution_source_changed": [
		"The source article changed; refresh before saving",
		"源文章已被修改，请刷新后再保存",
	],
	"errors.distribution_draft_invalid": ["The distribution draft is not valid", "分发草稿无效"],
	"errors.model_output_invalid": ["The model returned a response that could not be used", "模型返回的结果无法使用"],
	"errors.submission_name_invalid": ["Enter a name of 1 to 120 characters", "请输入 1 到 120 个字符的名称"],
	"errors.submission_website_invalid": ["Enter a usable website address", "请输入可访问的网站地址"],
	"errors.sample_no_questions": ["Add a question first", "请先添加问题"],
	"errors.sample_prompts_invalid": [
		"Choose enabled prompts of this brand in this language",
		"请选择该品牌、该语言下已启用的提示词",
	],
	"errors.task_not_pausable": [
		"This task has already ended, or it cannot stop in the middle",
		"该任务已经结束，或不能从中途停下",
	],
	"errors.task_not_paused": ["This task is not paused", "该任务未处于暂停状态"],
	"errors.task_not_retriable": [
		"This task has not ended, so there is nothing to run again",
		"该任务还没有结束，没有可以重跑的内容",
	],
	"errors.task_run_over": [
		"The agent run this task belonged to has finished; start a new one instead",
		"这个任务所属的智能体运行已经结束，请重新发起一次",
	],
	"errors.paused": ["This task was paused", "该任务已暂停"],
	"errors.sample_engine_missing": ["Choose a sampling engine in Settings", "请先在设置中选择采样引擎"],
	"errors.sample_no_complete_answers": [
		"No complete answers were collected; the incomplete ones were kept as evidence",
		"没有采集到完整回答；不完整的回答已保留为证据",
	],
	"errors.sample_set_without_brand": ["This question set has no brand to sample for", "该问题集没有对应品牌，无法采样"],
	"errors.sample_payer_missing": ["This batch has no account to charge", "该批次没有付费账户"],
	"errors.sample_engine_unknown": ["That engine is not available", "该引擎不可用"],
	"errors.brand_sources_required": [
		"Add the brand's website or public pages about it under Identity before collecting facts",
		"采集事实前请先在品牌信息中填写官网或介绍品牌的公开页面",
	],
	"errors.brand_datapoint_invalid": ["The fact “{key}” is not valid", "事实“{key}”无效"],
	"errors.brand_domain_unusable": ["{domain} is not a usable address", "{domain} 不是可用的地址"],
	"errors.page_blocks_empty": ["A page needs at least one block", "页面至少需要一个内容块"],
	"errors.page_answer_empty": ["The answer block is empty", "答案块为空"],
	"errors.page_answer_too_long": [
		"The answer block is {words} words; keep it to {max} so it stays quotable",
		"答案块有 {words} 个词，请控制在 {max} 个以内，便于被引用",
	],
	"errors.page_table_no_columns": ["A table needs columns", "表格需要列"],
	"errors.page_table_row_width": [
		"Table row {row} has {cells} cells but there are {columns} columns",
		"表格第 {row} 行有 {cells} 个单元格，但共有 {columns} 列",
	],
	"errors.page_faq_incomplete": ["Every FAQ item needs a question and an answer", "每个常见问题都需要问题和答案"],
	"errors.page_source_url": ["A source in block {index} has an unusable URL", "第 {index} 个内容块中有来源网址不可用"],
	"errors.page_section_incomplete": ["A section needs a heading and a body", "章节需要标题和正文"],
	"errors.page_chart_empty": ["A chart needs at least one point", "图表至少需要一个数据点"],
	"errors.page_stat_incomplete": ["A stat needs a label and a value", "数据项需要标签和数值"],
	"errors.page_stat_source_url": ["A stat's source needs a usable URL", "数据项的来源需要可用的网址"],
	"errors.page_answer_count": [
		"A generated page needs exactly one answer block; this one has {found}",
		"生成的页面需要恰好一个答案块，当前有 {found} 个",
	],
	"errors.page_answer_first": ["The answer block must come first", "答案块必须放在最前面"],
	"errors.page_sources_too_few": ["A generated page needs at least two sources", "生成的页面至少需要两个来源"],
	"errors.page_blocks_unusable": ["The page content could not be read", "无法读取页面内容"],
	"errors.page_kind_unsupported": [
		"Structured data is not available for this kind of page",
		"此类页面暂不支持结构化数据",
	],
	"errors.page_entity_missing": ["A brand page must name its brand", "品牌页面必须指明对应品牌"],
	"errors.page_ranked_empty": ["A ranked page must list at least one item", "排行页面至少需要一个条目"],
	"errors.page_dataset_meta_missing": ["A dataset page must carry dataset metadata", "数据集页面必须包含数据集元数据"],
	"errors.page_jsonld_invalid": ["The page's structured data is not valid", "页面结构化数据无效"],
	"errors.page_jsonld_incomplete": ["The {node} structured data is missing {field}", "{node} 结构化数据缺少 {field}"],
	"errors.plan_invalid": ["The content plan is not valid", "内容计划无效"],
	"errors.plan_command_invalid": ["That plan change is not valid", "该计划操作无效"],
	"errors.plan_action_invalid": ["That action is not valid", "该动作无效"],
	"errors.plan_action_missing": ["Choose an action first", "请先选择动作"],
	"errors.plan_page_missing": ["Choose an article first", "请先选择文章"],
	"errors.plan_outcome_required": ["Record the outcome before completing an action", "完成动作前请先记录结果"],
	"errors.plan_action_not_generative": ["This action does not generate an article", "该动作不生成文章"],
	"errors.plan_locale_mismatch": ["Use the plan's content language", "请使用计划的内容语言"],
	"errors.plan_page_link_required": ["Link the article to update first", "请先关联要更新的文章"],
	"errors.generate_unpublishable_after_check": [
		"Removing unsupported sentences left a page that cannot be published",
		"删除无依据的句子后，页面已无法发布",
	],
	"errors.ledger_amount_not_positive": ["The amount must be positive", "金额必须为正数"],
	"errors.factcheck_too_few_facts": [
		"This brand has no facts to check answers against",
		"该品牌还没有事实，无法核对回答",
	],
	"errors.generate_second_brand_required": ["A comparison needs a second brand", "对比页面需要第二个品牌"],
	"errors.page_revision_unavailable": ["The page is no longer available for revision", "该页面已无法修改"],
	"errors.page_changed_during_revision": [
		"The draft changed while the revision was running",
		"修改进行期间草稿已被更改",
	],
	"errors.page_jsonld_missing": ["A page without structured data cannot be published", "缺少结构化数据的页面无法发布"],
	"errors.member_owner_not_invited": ["An owner cannot be invited", "所有者不能通过邀请加入"],
	"errors.member_owner_not_settable": ["The owner cannot be set from this screen", "所有者不能在此页面设置"],
	"errors.opportunity_too_few_answers": [
		"There are not enough answers in this window to advise on",
		"该时间段内回答不足，无法给出建议",
	],
	"errors.webhook_signature": ["The webhook signature is not valid", "回调签名无效"],
	"errors.webhook_body": ["The webhook body could not be read", "无法解析回调内容"],
	"errors.webhook_event": ["The webhook event is incomplete", "回调事件不完整"],
	"errors.webhook_payment": ["The payment event has no payment id", "付款事件缺少付款编号"],
	"errors.webhook_subscription": [
		"The subscription event does not match a known subscription",
		"订阅事件无法对应已知订阅",
	],
	"errors.webhook_period": ["The paid period is missing or invalid", "付费周期缺失或无效"],
	"errors.billing_interval_unsupported": ["That billing interval is not supported", "不支持该计费周期"],
	"errors.billing_currency_unsupported": ["That currency is not supported", "不支持该币种"],
	"errors.billing_trial_monthly": ["The trial month is sold by the month only.", "体验月只按月出售。"],
	"errors.billing_plan_by_contract": [
		"This plan is arranged by contract, not checkout",
		"该方案通过合同办理，不支持在线结账",
	],
	"errors.billing_use_existing_subscription": [
		"Change plans through the existing subscription",
		"请通过现有订阅更换方案",
	],
	"errors.billing_checkout_open": [
		"Finish or cancel the open checkout before choosing another price",
		"请先完成或取消当前结账，再选择其他价格",
	],
	"errors.billing_checkout_reconciling": [
		"A subscription checkout is being reconciled; try again shortly",
		"订阅结账正在核对中，请稍后再试",
	],
	"errors.generate_locale_unsupported": ["{locale} is not a supported language", "{locale} 不是受支持的语言"],
	"errors.generate_industry_locale": ["This industry does not publish in {locale} yet", "该行业暂不发布 {locale} 内容"],
	"errors.generate_comparison_self": ["A brand cannot be compared with itself", "品牌不能与自身对比"],
	"errors.generate_too_few_shared": [
		"{first} and {second} share {shared} recorded facts; a comparison needs {need}",
		"{first} 与 {second} 共有 {shared} 条已记录事实，对比需要 {need} 条",
	],
	"errors.generate_no_rival": [
		"No rival is on record for this prompt to compare against",
		"此提示词没有可对比的竞品记录",
	],
	"errors.generate_no_alternatives": [
		"No sourced alternatives are available for a useful comparison",
		"没有可引用的替代品，无法生成有意义的对比",
	],
	// Bad requests that name themselves: the `auth_` and `brand_` codes the
	// server sends where the person can fix what was refused.
	"errors.auth_email_invalid": ["That does not look like an email address", "这不像是一个邮箱地址"],
	"errors.auth_password_short": ["A password needs at least 8 characters", "密码至少需要 8 个字符"],
	"errors.auth_password_long": ["That password is too long", "密码太长了"],
	"errors.auth_code_shape": ["The code is six digits", "验证码是 6 位数字"],
	"errors.auth_code_wrong": ["That code is not right", "验证码不正确"],
	"errors.auth_code_mismatch": ["This code was sent to a different address", "这个验证码是发给另一个邮箱的"],
	"errors.auth_link_spent": ["This link has expired or has already been used", "这个链接已失效或已被使用"],
	"errors.auth_link_mismatch": ["This link was sent to a different address", "这个链接是发给另一个邮箱的"],
	"errors.auth_provider_no_email": [
		"That account has no verified email address at the provider",
		"这个账号在该平台没有已验证的邮箱地址",
	],
	"errors.brand_website_invalid": ["That does not look like a website address", "这不像是一个网址"],
	"errors.brand_category_unknown": ["That category is not in the list", "该类目不在列表中"],
	"errors.brand_categories_too_many": ["A brand can have at most five categories", "一个品牌最多选 5 个类目"],
	"errors.brand_name_length": ["A brand name between 2 and 80 characters is required", "品牌名称需要 2 到 80 个字符"],
	"errors.brand_opportunities_need_answers": [
		"There are no answers from the last month to advise on yet. Run a sample first.",
		"近一个月还没有可分析的回答，请先采样一次。",
	],
	"errors.internal": ["Something went wrong on our side", "我们这边出错了"],
	"errors.upstream": ["An upstream service is unavailable", "上游服务不可用"],
	"errors.misconfigured": ["This deployment is missing configuration", "本部署缺少配置"],
	"errors.notFound.entity": ["That product is not in your account", "这个产品不在你的账户下"],
	"errors.notFound.org": ["That workspace is not yours", "这个工作区不属于你"],
	"errors.notFound.page": ["That page is not in your account", "这个页面不在你的账户下"],
	"errors.notFound.gap": ["That gap is gone", "这个缺口已经不在了"],
	"errors.notFound.batch": ["That sampling batch is not in your account", "这个采样批次不在你的账户下"],
	"errors.notFound.template": ["That template is no longer listed", "该模板已不再提供"],
	"errors.notFound.provider": ["That sign-in provider is not supported", "不支持该登录方式"],
	"errors.notFound.model_call": ["That model call is not on record", "没有这条模型调用记录"],
	"errors.notFound.claim": ["That verification is gone", "这次验证已经不在了"],
	"errors.notFound.sitemap": ["That sitemap does not exist", "该站点地图不存在"],
	"errors.notFound.datapoint": ["That fact is not on the brand", "这条事实不在该品牌下"],
	"errors.notFound.category": ["That category does not exist", "该分类不存在"],
	"errors.notFound.answer": ["That answer is gone", "这条回答已经不在了"],
	"errors.notFound.agent_draft": ["This agent has no draft to run", "该智能体没有可运行的草稿"],
	"errors.conflict.brand_slug_exists": ["A brand with that address already exists", "已有品牌使用这个地址"],
	"errors.conflict.claim_changed": [
		"The verification changed while it was being checked. Start it again.",
		"验证在检查过程中发生了变化，请重新开始。",
	],
	"errors.conflict.gap_already_generated": ["A page for this gap is already on its way", "这个缺口已经在生成页面了"],
	"errors.conflict.generation_active": ["A generation for this gap is still running", "这个缺口的生成任务仍在进行"],
	// Dodo charges the card itself, so paygate cannot move a live subscription
	// to another price. The way across is to cancel and check out again.
	"errors.conflict.plan_change_unsupported": [
		"This subscription's payment method cannot change plan. Cancel it, then subscribe to the plan you want.",
		"这个订阅的支付方式无法直接换套餐。请先取消，再订阅你想要的套餐。",
	],
	"errors.conflict.factcheck_evidence_changed": [
		"The fact-check evidence changed. Review it again before publishing.",
		"事实核查证据已变化，发布前请重新审核。",
	],
	"errors.conflict.agent_draft_changed": [
		"This draft changed elsewhere. Reload it before saving again.",
		"草稿已在别处被修改，请刷新后再保存。",
	],
	"errors.conflict.page_owned_elsewhere": ["Another account already publishes this page", "这个页面已由另一个账户发布"],
	"errors.conflict.model_request_changed": [
		"This model request already has different input",
		"此模型请求已绑定其他输入",
	],
	"errors.conflict.model_request_unsettled": [
		"This model call needs accounting reconciliation before retrying",
		"此模型调用需先核对状态与费用，暂不能重试",
	],
	"errors.conflict.model_usage_exceeds_reservation": [
		"Reported usage exceeds the reserved limit and needs reconciliation",
		"返回用量超过预留上限，需要核对",
	],
	"errors.conflict.model_usage_unmeasured": ["The model did not return reliable usage", "模型未返回可靠用量"],
	"agents.estimate": ["Usage-based estimate", "用量预估"],
	"agents.estimateLoading": ["Reading usage history…", "正在读取用量历史…"],
	"agents.estimateUnavailable": [
		"An estimate is unavailable. The run ceiling still applies.",
		"暂无法预估，运行积分上限仍然有效。",
	],
	"agents.estimateUnknown": ["Insufficient history", "历史数据不足"],
	"agents.estimateCredits": ["About {n} credits", "约 {n} 积分"],
	"agents.estimateLine": [
		"Up to {n} calls; {samples} historical operations",
		"最多 {n} 次执行；参考 {samples} 次历史操作",
	],
	"agents.estimateBasis": [
		"Reference: this brand’s last 20 completed operations per capability within 90 days, including all branches and bounded batches. Inputs and current prices can change actual costs. This is not a fixed quote or the run ceiling.",
		"参考该品牌 90 天内每项能力最近 20 次已完成操作，计入所有分支与有上限的批量。输入与当前价格会影响实际费用；这不是固定报价，也不是运行积分上限。",
	],
	"errors.notFound.agent": ["Agent not found", "智能体不存在"],
	"errors.notFound.agent_version": ["Version not found", "版本不存在"],
	"errors.notFound.agent_run": ["Run not found", "运行不存在"],
	"errors.notFound.agent_step": ["Step not found", "步骤不存在"],
	"errors.conflict.agent_brand_busy": ["This brand already has an active run", "此品牌已有进行中的运行"],
	"errors.conflict.agent_request_changed": [
		"This request key already has different inputs",
		"此请求标识已绑定其他输入",
	],
	"errors.conflict.agent_run_closed": [
		"This run or waiting step can no longer accept input",
		"此运行或等待步骤已不能接受输入",
	],
	"errors.agent_ondit_timeout": [
		"The Ondit step stopped reporting progress and was given up on. Its task may still be on the queue.",
		"Ondit 步骤长时间没有任何进展，已放弃等待；它的任务可能仍在队列里。",
	],
	"errors.agent_stall_limit": [
		"Enter a wait of 60 seconds to 6 hours, or leave it empty to use the operation's own.",
		"请填写 60 秒到 6 小时之间的等待时间，留空则使用该操作自带的时间。",
	],
	"errors.agent_ondit_too_long": [
		"The Ondit step was still going after six hours. A run is not where work this long belongs — give it its own schedule.",
		"Ondit 步骤运行超过六小时。这么长的工作不该待在一次运行里，请为它单独安排排程。",
	],
	"errors.agent_name": ["Enter a name up to 160 characters", "请输入不超过 160 字符的名称"],
	"errors.agent_definition": ["The definition could not be read", "无法读取该定义"],
	"errors.agent_execution_unsupported": [
		"This workflow uses a node or setting that the Agent runtime does not support yet.",
		"此工作流使用了 Agent 运行引擎尚未支持的节点或配置。",
	],
	"errors.agent_definition_invalid": [
		"The definition is invalid; validate it in the studio",
		"定义无效，请在工作室校验",
	],
	"errors.agent_executor_unavailable": [
		"This version contains steps the current executor does not support yet",
		"此版本包含当前执行器尚未支持的步骤",
	],
	"errors.agent_run_input": ["The run settings are invalid", "运行设置无效"],
	"errors.agent_schedule_input": [
		"The schedule settings are invalid. Reload the workflow and try again.",
		"定时设置无效，请刷新工作流后重试。",
	],
	"errors.agent_schedule_cron": [
		"A schedule takes five cron fields — minute, hour, day of month, month, weekday — without W, ?, @ or seconds.",
		"定时任务需要五段 Cron：分、时、日、月、周，不支持 W、?、@ 或秒。",
	],
	"errors.agent_schedule_timezone": ["Choose a timezone from the list", "请从列表中选择时区"],
	"errors.agent_schedule_never": [
		"This schedule never comes round. Check the day and month.",
		"此定时任务永远不会触发，请检查日期与月份。",
	],
	"errors.agent_model_empty": ["The model returned an empty response", "模型返回空响应"],
	"errors.agent_template_native_unsupported": [
		"This legacy workflow uses a block that cannot yet be copied to Agent",
		"该旧工作流包含尚不能复制到 Agent 的节点",
	],
	"errors.notFound.agent_chunk": ["Document chunk not found", "文档分块不存在"],
	"errors.notFound.agent_document": ["Document not found", "文档不存在"],
	"errors.notFound.agent_folder": ["Folder not found", "文件夹不存在"],
	"errors.notFound.agent_knowledge": ["Knowledge base not found", "知识库不存在"],
	"errors.notFound.agent_knowledge_connector": ["Source not found", "来源不存在"],
	"errors.notFound.agent_tag": ["Knowledge tag not found", "知识库标签不存在"],
	"errors.agent_table_required_empty": [
		"Some rows leave this column empty. Fill them in before making it required.",
		"有些行的这一列是空的，请先填写再设为必填。",
	],
	"errors.agent_table_retype_lossy": [
		"Some values in this column cannot be read as the new type. Change or clear them first.",
		"这一列有些值无法按新类型读取，请先修改或清空这些值。",
	],
	"errors.agent_table_input": [
		"Check the table name, column definitions and cell values",
		"请检查数据表名称、列定义与单元格取值",
	],
	"errors.agent_table_limit": [
		"This table is at one of its limits — tables, columns, rows, batch size or cell size",
		"已达到数据表上限：表数量、列数、行数、批量条数或单元格大小",
	],
	"errors.notFound.agent_table": ["Table not found", "数据表不存在"],
	"errors.notFound.agent_table_row": ["Table row not found", "数据行不存在"],
	"errors.forbidden.agent_table_locked": ["This table is locked against that change", "该数据表已锁定，无法进行此修改"],
	"errors.conflict.agent_table_unique": [
		"Another row already has this value in a unique column",
		"唯一列中已存在相同取值的行",
	],
	"errors.notFound.agent_upload": ["Upload not found", "上传记录不存在"],
	"errors.conflict.agent_embeddings_pending": ["Vector indexing is still in progress", "向量索引仍在处理中"],
	"errors.conflict.agent_knowledge_operation_changed": [
		"This request was already used with different input",
		"该请求已用于其他输入",
	],
	"errors.conflict.agent_knowledge_tag_conflict": ["A tag with this name already exists", "同名标签已存在"],
	"errors.conflict.agent_run_stopped": ["This workflow run has stopped", "该工作流运行已停止"],
	"errors.conflict.agent_upload_state": [
		"This upload cannot be changed in its current state",
		"当前上传状态不允许此操作",
	],
	"errors.forbidden.agent_knowledge": ["You cannot access this knowledge base", "无权访问该知识库"],
	"errors.forbidden.agent_upload": ["You cannot access this upload", "无权访问该上传记录"],
	"errors.agent_document_format": ["This document format is not supported", "不支持该文档格式"],
	"errors.agent_embeddings_dimension": ["The embedding dimensions do not match", "向量维度不匹配"],
	"errors.agent_embeddings_unconfigured": ["The embedding service has not been configured", "尚未配置向量模型服务"],
	"errors.agent_folder_input": ["Check the folder name and location", "请检查文件夹名称和位置"],
	"errors.notFound.file": ["File not found", "文件不存在"],
	"errors.notFound.agent_file": ["File not found", "文件不存在"],
	"errors.conflict.agent_file_changed": [
		"This file changed since you opened it. Reload it before saving again.",
		"该文件在你打开后已被修改，请刷新后再保存。",
	],
	"errors.agent_file_input": ["Check the file name and location", "请检查文件名称和位置"],
	"errors.agent_file_quota": ["This brand has used all of its file storage", "该品牌的文件存储空间已用完"],
	"errors.agent_file_too_large": ["That file is larger than the upload limit", "该文件超过上传大小上限"],
	"errors.agent_environment_name": [
		"A variable name may use letters, digits and underscores, and cannot start with a digit",
		"变量名只能包含字母、数字和下划线，且不能以数字开头",
	],
	"errors.agent_environment_value": ["Check the value: it is empty or too long", "请检查取值：为空或过长"],
	"errors.agent_environment_limit": ["This brand has as many variables as it may hold", "该品牌的变量数量已达上限"],
	"errors.agent_environment_unknown": [
		"This brand has no variable by that name. Add it on the workspace's variables screen, or correct the reference.",
		"该品牌没有同名变量。请在工作区变量页面添加，或更正引用。",
	],
	"errors.agent_environment_scope": [
		"A variable may only fill an API block's request. Move the reference to the block that makes the call.",
		"变量只能用于 API 块的请求，请将引用移到发起调用的块中。",
	],
	"errors.agent_help_input": ["Check the subject, message and request type", "请检查主题、内容和请求类型"],
	"errors.agent_help_attachment": [
		"Attach at most three images, under 1 MB in total",
		"最多可附加三张图片，且总大小不超过 1 MB",
	],
	"errors.agent_knowledge_capacity": ["The knowledge base has reached its indexing limit", "知识库已达到索引容量上限"],
	"errors.agent_knowledge_index_interrupted": [
		"Indexing stopped before it finished. Retry to index this document again.",
		"索引未完成就中断了，请重试以重新索引该文档。",
	],
	"errors.agent_knowledge_input": ["Check the knowledge base inputs", "请检查知识库输入"],
	"errors.agent_knowledge_search_mode": ["This search mode is not supported", "不支持该检索模式"],
	"errors.agent_knowledge_connector_type": ["This source type cannot be synced here", "此来源类型暂不支持同步"],
	"errors.agent_knowledge_connector_access": [
		"Sources are shared with the whole brand; per-person access is not available",
		"来源由整个品牌共享，不支持按成员单独授权",
	],
	"errors.agent_knowledge_connector_credential": [
		"Choose a connected account that matches this source",
		"请为该来源选择匹配的已连接账号",
	],
	"errors.agent_knowledge_connector_config": ["Check the source settings", "请检查来源设置"],
	"errors.agent_knowledge_connector_paused": ["Resume the source before syncing it", "请先恢复该来源再同步"],
	"errors.agent_model_refusal": ["The model service declined this request", "模型服务拒绝了本次请求"],
	"errors.agent_credential_expired": [
		"This connection has expired. Connect the account again.",
		"该连接已过期，请重新连接账号。",
	],
	"errors.agent_credential_name": ["Check the connection name", "请检查连接名称"],
	"errors.agent_credential_provider": ["This integration cannot be connected here", "此集成暂不支持在此连接"],
	"errors.agent_credential_secret": ["Check the key you pasted", "请检查粘贴的密钥"],
	"errors.agent_credential_type": ["This connection type is not supported", "不支持该连接类型"],
	"errors.agent_credential_workspace": ["That connection belongs to another brand", "该连接属于其他品牌"],
	"errors.agent_oauth_account": ["The provider did not say which account was connected", "服务商未返回所连接的账号"],
	"errors.agent_oauth_draft": ["Start the connection again", "请重新发起连接"],
	"errors.agent_oauth_exchange": ["The provider refused to complete the connection", "服务商拒绝完成本次连接"],
	"errors.agent_oauth_tenant": [
		"Enter the store or environment address this connection is for",
		"请填写这次连接对应的店铺或环境地址",
	],
	"errors.agent_oauth_state": [
		"That connection link is no longer valid. Start again.",
		"该连接链接已失效，请重新发起。",
	],
	"errors.agent_oauth_unconfigured": [
		"This integration has not been configured on this deployment",
		"本部署尚未配置此集成",
	],
	"errors.notFound.agent_credential": ["That connected account is unavailable", "该已连接账号不可用"],
	"errors.notFound.agent_oauth_provider": ["This integration cannot be connected here", "此集成暂不支持在此连接"],
	"errors.notFound.agent_integration_operation": ["This block cannot do that yet", "该模块暂不支持此操作"],
	"errors.forbidden.agent_integration_scope": [
		"That connected account belongs to another brand",
		"该已连接账号属于其他品牌",
	],
	"errors.conflict.agent_integration_in_flight": [
		"An earlier attempt never reported back. Check the service before running this step again.",
		"上一次尝试没有返回结果。请先到该服务确认，再重新运行此步骤。",
	],
	"errors.conflict.agent_integration_call_changed": [
		"Use a new run to change this step's inputs",
		"更改此步骤的输入请重新发起运行",
	],
	"errors.agent_integration_credential": ["Choose an account connected for this service", "请选择为该服务连接的账号"],
	"errors.agent_integration_input": ["Check this step's inputs", "请检查此步骤的输入"],
	"errors.agent_integration_request_too_large": [
		"This step is sending more than the service accepts",
		"此步骤发送的内容超过该服务的上限",
	],
	"errors.agent_integration_response_too_large": [
		"The service returned more than this step can read. Narrow it and try again.",
		"该服务返回的内容超过此步骤可读取的上限，请缩小范围后重试。",
	],
	"errors.agent_integration_response": [
		"The service answered in a shape this step cannot read",
		"该服务返回的内容格式无法解析",
	],
	"errors.agent_integration_timeout": ["The service did not answer in time", "该服务未在规定时间内响应"],
	"errors.agent_integration_unavailable": ["The service could not be reached", "无法连接该服务"],
	"errors.agent_integration_refused": ["The service refused this request", "该服务拒绝了本次请求"],
	"errors.agent_integration_denied": [
		"The connected account no longer has access. Reconnect it.",
		"已连接账号的权限已失效，请重新连接。",
	],
	"errors.agent_integration_rate_limited": ["The service is rate limiting this account", "该服务正在对此账号限流"],
	"errors.agent_integration_host": ["This step could not build a valid request", "此步骤无法构造有效请求"],
	"errors.agent_integration_budget": [
		"This step asked the service too many times for one run",
		"此步骤在一次运行中调用该服务的次数过多",
	],
	"errors.agent_selector_unsupported": [
		"Ondit cannot connect to this service yet, so this list cannot be loaded",
		"Ondit 暂不支持连接该服务，无法加载此列表",
	],
	"errors.agent_selector_scope": ["This list can only be loaded inside a brand", "此列表只能在品牌内加载"],
	"errors.agent_selector_context": ["Check the fields this list depends on", "请检查此列表所依赖的字段"],
	"errors.agent_selector_impersonation": [
		"Acting as another Google Workspace user is not supported. Clear that field.",
		"不支持以其他 Google Workspace 用户的身份访问，请清空该字段。",
	],
	"errors.agent_selector_grant": [
		"This connected account was not granted access to this list",
		"该已连接账号未获授权访问此列表",
	],
	"errors.agent_settings_input": ["That is not a setting this workspace keeps", "该项不是此工作区保存的设置"],
	"errors.notFound.agent_chat": ["This chat page is unavailable", "该对话页面不可用"],
	"errors.conflict.agent_chat_identifier": ["That chat address is already taken", "该对话地址已被占用"],
	"errors.agent_chat_identifier": [
		"Use lowercase letters, numbers and hyphens, up to 100 characters",
		"请使用小写字母、数字和连字符，不超过 100 个字符",
	],
	"errors.agent_chat_auth_mode": ["This chat access mode is not supported", "不支持该对话访问方式"],
	"errors.agent_chat_password_weak": ["Set a password of at least 15 characters", "请设置至少 15 个字符的密码"],
	"errors.agent_chat_emails": [
		"Add at least one valid email address to the list",
		"请在名单中添加至少一个有效邮箱地址",
	],
	"errors.agent_chat_outputs": ["Check which block outputs the chat may show", "请检查对话可展示的模块输出"],
	"errors.agent_chat_not_deployed": [
		"Deploy this workflow before publishing it as a chat",
		"请先部署该工作流，再将其发布为对话页面",
	],
	"errors.agent_chat_message": ["That message is empty or too long", "该消息为空或过长"],
	"errors.agent_chat_attachments": ["This chat page does not accept file attachments", "该对话页面不支持附件上传"],
	"errors.agent_chat_conversation_full": [
		"This conversation has reached its message limit. Start a new one.",
		"该会话已达到消息数量上限，请开始新会话。",
	],
	"errors.agent_chat_run_failed": ["The workflow behind this chat did not finish", "该对话背后的工作流未能完成"],
	"errors.notFound.agent_mcp_server": ["That MCP server is unavailable", "该 MCP 服务器不可用"],
	"errors.agent_skill_input": ["Check the skills you are saving", "请检查要保存的技能"],
	"errors.agent_skill_name": [
		"Use lowercase letters, digits and hyphens, up to 64 characters (e.g. my-skill)",
		"名称只能包含小写字母、数字和连字符，最多 64 个字符（如 my-skill）",
	],
	"errors.agent_skill_missing": [
		"A skill on an Agent node was deleted or belongs to another brand. Remove it from the node and run again.",
		"Agent 节点上的某个技能已删除或属于其他品牌。请从节点中移除该技能后重新运行。",
	],
	"errors.agent_skill_description": ["Enter a description of up to 1,024 characters", "请输入不超过 1024 个字符的描述"],
	"errors.agent_skill_content": [
		"Enter the skill's content, up to 50,000 characters",
		"请输入技能内容，最多 50000 个字符",
	],
	"errors.conflict.agent_skill_name": ["A skill with this name already exists", "同名技能已存在"],
	"errors.notFound.agent_skill": ["That skill no longer exists", "该技能已不存在"],
	"errors.conflict.agent_mcp_name": ["An MCP server with this name already exists", "同名 MCP 服务器已存在"],
	"errors.agent_mcp_name": ["Enter a name up to 100 characters", "请输入不超过 100 个字符的名称"],
	"errors.agent_mcp_url": [
		"Enter an https address on the default port, with no username or password",
		"请输入使用默认端口的 https 地址，且不含用户名或密码",
	],
	"errors.agent_mcp_address_blocked": [
		"That address is on a private network and cannot be reached from here",
		"该地址属于内网，无法从此处访问",
	],
	"errors.agent_mcp_unreachable": ["That MCP server did not answer", "该 MCP 服务器无响应"],
	"errors.agent_mcp_unauthorized": ["That MCP server refused these credentials", "该 MCP 服务器拒绝了此凭据"],
	"errors.agent_mcp_protocol": ["That server did not answer as an MCP server", "该服务器未按 MCP 协议响应"],
	"errors.agent_mcp_response_too_large": [
		"That MCP server sent more than we will read",
		"该 MCP 服务器返回的内容超过可读取上限",
	],
	"errors.agent_mcp_headers": ["Check the request headers for this server", "请检查该服务器的请求头"],
	"errors.agent_mcp_auth_mode": ["This MCP authentication mode is not supported", "不支持该 MCP 认证方式"],
	"errors.agent_mcp_parameters": ["Check the MCP server settings", "请检查 MCP 服务器设置"],
	"errors.agent_model_output": ["The model output did not match the required schema", "模型输出不符合要求的结构"],
	"errors.notFound.agent_schedule": ["This schedule is unavailable", "该定时计划不可用"],
	"errors.agent_batch_limit": ["The list exceeds the batch limit", "列表超过批量上限"],
	"errors.agent_child_depth": ["The child workflow exceeds the nesting limit", "子工作流超过嵌套上限"],
	"errors.agent_parameters": ["Fill in the required inputs using their declared types", "请按字段要求填写输入"],
	"errors.agent_wait_limit": ["The wait exceeds its configured limit", "等待时间超过配置上限"],
	"errors.agent_human_timeout": ["Nobody answered this approval in time", "无人在时限内处理该审批"],
	"errors.agent_tool_rounds": [
		"The agent used all of its tool rounds without finishing",
		"该智能体用尽工具调用轮次仍未完成",
	],
	"errors.agent_tool_unsupported": ["That tool is not available in this runtime", "此运行环境不支持该工具"],
	"errors.agent_http_url": [
		"Enter a full http:// or https:// address with no username or password",
		"请填写完整的 http:// 或 https:// 地址，且不含用户名或密码",
	],
	"errors.agent_http_blocked": [
		"That address is on a private or internal network and cannot be called",
		"该地址位于私有或内部网络，无法访问",
	],
	"errors.agent_http_unreachable": ["That address could not be reached", "无法连接该地址"],
	"errors.agent_http_parameters": [
		"Check the method, headers and timeout of this request",
		"请检查该请求的方法、请求头与超时设置",
	],
	"errors.agent_http_timeout": ["The request took too long and was stopped", "请求超时已中止"],
	"errors.agent_http_redirects": ["The request was redirected too many times", "该请求重定向次数过多"],
	"errors.agent_http_response_limit": ["The response is larger than the limit", "响应内容超过大小上限"],
	"errors.agent_http_request_limit": ["The request body is larger than the limit", "请求体超过大小上限"],
	"errors.agent_http_run_limit": ["This run has made all the requests it is allowed", "本次运行的请求次数已达上限"],
	"errors.agent_assignee": ["Choose a member with editing access to this brand", "请选择对此品牌有编辑权限的成员"],
	"errors.agent_binding": ["A required input value is unavailable", "所需输入值不可用"],
	"errors.agent_comparison": ["The condition values cannot be compared", "条件值无法比较"],
	"errors.notFound.operation": ["That operation is no longer available", "该操作已不存在"],
	"errors.conflict.operation_input_changed": [
		"Use a new request key when changing the input",
		"更改输入时请使用新的请求标识",
	],
	"errors.conflict.operation_skipped": [
		"This operation was skipped. Check its status before trying again.",
		"此操作已跳过，请检查状态后再重试。",
	],
	"errors.notFound.job": ["That job is gone", "这个任务已经不在了"],
	"errors.notFound.question": ["That question is gone", "这个问题已经不在了"],
	"errors.notFound.thread": ["That conversation is gone", "这个对话已经不在了"],
	"errors.notFound.plan": ["That plan does not exist", "该套餐不存在"],
	"errors.notFound.dataset": ["That dataset does not exist", "该数据集不存在"],
	"errors.notFound.badge": ["That badge does not exist", "该徽章不存在"],
	"errors.notFound.subscription": ["No subscription", "没有订阅"],
	"errors.notFound.question_set": ["No question set", "没有问题集"],
	"errors.gateway_notice": [
		"The engine refused the call. It has been skipped and will be retried next week.",
		"引擎拒绝了这次调用。已跳过，下周会再试。",
	],
	"errors.gated": ["Blocked", "已阻止"],
	"errors.gated.agent_credit_limit": [
		"The next call exceeds this run’s credit ceiling",
		"下一次调用将超过本次运行的积分上限",
	],
	"errors.gated.factcheck_required": [
		"Review the current fact-check evidence before publishing.",
		"请检查当前事实证据后再发布。",
	],
	"errors.gated.agent_tool_unavailable": ["This tool is not available yet", "此工具能力暂未开放"],
	"errors.gated.agent_run_stopped": ["This agent run has stopped", "该智能体运行已停止"],
	"errors.gated.content_plan_stopped": ["The content plan or action has stopped", "内容计划或行动已停止"],
	"errors.gated.content_permission_changed": ["The requester no longer has editing access", "发起人已无编辑权限"],
	"errors.gated.org_paused": ["Tracking is paused for this workspace", "该工作区的追踪已暂停"],
	"errors.gated.brand_paused": ["Tracking is paused for this brand", "该品牌的追踪已暂停"],
	"errors.gated.spend_cap": ["Daily spend cap reached", "已达到每日花费上限"],
	"errors.gated.credits": ["Not enough credits", "积分不足"],
	"errors.gated.task_paused": ["The task it was waiting on is paused", "它等待的任务已暂停"],
	"errors.gated.platform_balance": ["Engines are unavailable right now; skipped", "引擎暂时不可用，已跳过"],
	"errors.gated.platform_cap": ["Platform daily cap reached", "平台每日上限已到"],
	"errors.gated.sample_quota": ["Your plan's question limit is reached", "已达到套餐的问题数上限"],
	"errors.gated.page_quota": [
		"Every page this month's plan covers has been generated. It opens again on the first.",
		"本月套餐可生成的页面已经用完，下月 1 日恢复。",
	],
	"errors.gated.entity_quota": ["Your plan's brand limit is reached", "已达到套餐的品牌数上限"],
	"errors.gated.market_price": [
		"This engine's model is trading above your price ceiling",
		"该引擎的模型报价高于你设置的价格上限",
	],
	"errors.gated.no_supply": ["No provider is online for this engine", "该引擎当前没有供给在线"],
	"errors.gated.already_yours": ["You already own this listing", "这个条目已经属于你"],
	"errors.gated.claimed_by_another": ["Someone else has verified this listing", "已有其他账号完成了该条目的验证"],
	"errors.gated.not_listed": ["This entry is not published yet", "该条目尚未上线"],
	"errors.gated.no_domain_on_record": [
		"This entry has no website on record, so there is nothing to verify against",
		"该条目没有登记网站，无法进行归属验证",
	],

	// -- claiming a listing
	"claim.title": ["Claim {name}", "认领 {name}"],
	"claim.intro": [
		"Prove you control {domain} and this listing becomes yours. Either method works — do whichever you can reach.",
		"证明你能控制 {domain}，这个条目就归你。两种方式任选其一，哪个方便用哪个。",
	],
	"claim.dns.title": ["Add a DNS record", "添加一条 DNS 记录"],
	"claim.dns.body": [
		"The stronger proof. Changes can take a few minutes to an hour to reach us.",
		"更可靠的方式。DNS 变更通常需要几分钟到一小时才能被我们查到。",
	],
	"claim.dns.type": ["Type", "类型"],
	"claim.dns.host": ["Host", "主机记录"],
	"claim.dns.value": ["Value", "记录值"],
	"claim.meta.title": ["Or add a meta tag", "或者加一个 meta 标签"],
	"claim.meta.body": [
		"Paste this into the <head> of https://{domain}/ — it takes effect as soon as the page is live.",
		"把这段贴进 https://{domain}/ 的 <head> 里，页面上线后即刻生效。",
	],
	"claim.check": ["Check now", "现在验证"],
	"claim.checking": ["Checking…", "验证中…"],
	"claim.propagation": [
		"We look for the DNS record first, then the meta tag. If neither is there yet, try again in a few minutes.",
		"我们先查 DNS 记录，再查 meta 标签。如果都还没查到，过几分钟再试一次。",
	],
	"claim.verified": ["Verified", "验证通过"],
	"claim.verified.body": [
		"{name} is yours. Tracking starts with the next weekly run.",
		"{name} 已归属于你。下一次周度采样开始追踪。",
	],
	"claim.reason.no_proof_found": [
		"Neither the DNS record nor the meta tag is there yet",
		"DNS 记录和 meta 标签都还没查到",
	],
	"claim.reason.site_unreachable": [
		"We could not reach the site, so we checked DNS only",
		"网站访问不通，我们只查了 DNS",
	],

	// -- answer engine insights
	//
	// Nine readings of one set of answers. The metric definitions are the
	// load-bearing strings here: every figure on these screens is one somebody
	// may quote in a meeting, and a number whose definition lives in the
	// documentation gets quoted as something else. Each `.hint` is the sentence
	// the mark beside the heading shows, and it says what is counted and over
	// what — never what the number is good for.
	// The sentence the mark beside each stage's title shows: what its readings
	// have in common, and what the scope bar over them is doing.
	"stage.detect.lede": [
		"One set of answers, read seven ways: how often this brand is named, who it is named beside, and which pages the answers leaned on. The scope carries across these readings.",
		"同一批回答的七种读法：这个品牌被提及的频率、和谁一起被提及、以及答案引用了哪些页面。这些读法使用相同的统计口径。",
	],
	"stage.analyse.lede": [
		"The same answers, read for what to do about them: who was cited instead of you, the prompts where they won, how the brand is evaluated, what the engines got wrong, and what to improve.",
		"同一批回答，读的是接下来该做什么：谁被引用而不是你、你在哪些提示词上输了、品牌被如何评价、引擎说错了什么，以及据此给出的改进建议。",
	],
	"stage.generate.lede": [
		"What gets written, and what it gets written from. Every page is assembled from this brand's own sourced facts and nothing else.",
		"要写什么，以及从什么写。每个页面都只由这个品牌自己有出处的事实拼成，不用别的。",
	],
	"stage.publish.lede": [
		"What is live, whether an engine ever came to read it, and the badge that links back.",
		"哪些已经上线、引擎有没有来读过，以及回链用的徽章。",
	],
	"stage.reflow.lede": [
		"Whether a published page changed the answer. A conclusion appears only once the samples can carry one.",
		"已发布的页面有没有改变答案。只有样本足够支撑结论时才会给出结论。",
	],

	// Every reading in the loop, by the slug `LOOP` gives it. One flat set
	// rather than one per stage: the slugs are unique across all five, and it
	// is the same word the assistant uses to name a screen.
	"reading.visibility": ["Visibility", "可见度"],
	"reading.prompts": ["Prompts", "提示词"],
	"reading.fanouts": ["Query fan-outs", "查询分支"],
	"reading.platforms": ["Platforms", "平台"],
	"reading.regions": ["Regions", "区域"],
	"reading.personas": ["Personas", "客户角色"],
	"reading.sentiment": ["Brand favourability", "品牌好感度"],
	"reading.citations": ["Citations", "引用"],
	"reading.competitors": ["Competitors", "竞品"],
	"reading.gaps": ["Content gaps", "内容缺口"],
	"reading.factcheck": ["FactCheck", "事实核查"],
	"reading.opportunities": ["Improvement plan", "优化建议"],
	"reading.briefs": ["Briefs", "写作依据"],
	"reading.drafts": ["Drafts", "草稿"],
	"reading.published": ["Published", "已发布"],
	"reading.crawlers": ["AI crawlers", "AI 抓取"],
	"reading.badge": ["Badge", "徽章"],
	"reading.reflows": ["Reflow reports", "回流报告"],
	"reading.since": ["Publication impact", "发布效果"],
	"reading.traffic": ["Referral traffic", "回流流量"],
	"reading.journeys": ["Visitor paths", "访问路径"],
	"reading.goals": ["Goals", "转化目标"],
	"reading.attribution": ["Attribution", "转化归因"],
	"reading.vitals": ["Page speed", "页面速度"],
	"reading.live": ["Install check", "安装检查"],

	// -- stage five, the traffic half: whether an answer sent anybody back
	//
	// Every figure here is GA4's word for it. A customer who had GA before this
	// screen reads the same vocabulary, and the two numbers mean the same thing
	// even though different code counted them.
	"traffic.lede": [
		"Of everyone who arrived, how many an assistant sent — and which pages they landed on. The only figure in the loop you can state in your own terms.",
		"来访的人里有多少是助手送来的，他们落在哪些页面上。这是整个循环里唯一能用你自己的业务口径说出来的数字。",
	],
	"traffic.sessions": ["Sessions", "会话"],
	"traffic.users": ["Users", "用户"],
	"traffic.engagementRate": ["Engagement rate", "互动率"],
	"traffic.conversions": ["Conversions", "转化"],
	"traffic.share": ["AI share", "AI 占比"],
	"traffic.shareFoot": ["of {all} referred sessions", "在 {all} 次来访会话中"],
	"traffic.avgTime": ["{time} per session", "每次会话 {time}"],
	"traffic.daily": ["By day", "按天"],
	"traffic.dailyHint": [
		"The same shape as the crawler chart on the publishing stage: an engine came to read the page, and then somebody arrived from it.",
		"和发布阶段的抓取图形状一致：引擎来读了页面，然后有人从那里来了。",
	],
	"traffic.byEngine": ["By assistant", "按助手"],
	"traffic.byEngineHint": [
		"Assistants this product samples are named as they are everywhere else here. The rest send real traffic and are counted, but there is no citation figure to reconcile them against.",
		"本产品采样的助手，名称与其他页面一致。其余助手同样会带来真实流量并被统计，但没有对应的引用数字可以互相印证。",
	],
	"traffic.engine": ["Assistant", "助手"],
	"traffic.byLanding": ["Landing pages", "落地页"],
	"traffic.byLandingHint": [
		"Which page an answer pointed at. A page here that you generated is the loop closing.",
		"回答把人指向了哪个页面。如果这里出现的是你生成的页面，循环就闭合了。",
	],
	"traffic.landing": ["Page", "页面"],
	"traffic.byPlace": ["Regions", "区域"],
	"traffic.byPlaceHint": [
		"Read from the CDN in front of your site. A site without one reports no region rather than a guessed one.",
		"由站点前面的 CDN 提供。没有 CDN 的站点不会报告区域，而不是给一个猜测值。",
	],
	"traffic.country": ["Region", "区域"],
	"traffic.byDevice": ["Devices", "设备"],
	"traffic.device": ["Device", "设备"],
	"traffic.device.desktop": ["Desktop", "桌面"],
	"traffic.device.mobile": ["Mobile", "手机"],
	"traffic.device.tablet": ["Tablet", "平板"],
	"traffic.byReferrer": ["Every referrer", "全部来源"],
	"traffic.byReferrerHint": [
		"Not only assistants. The row worth reading here is the one nobody expected, and an AI-only table cannot show it.",
		"不只是助手。这里值得看的是没人预料到的那一行，只列 AI 的表格显示不出来。",
	],
	"traffic.referrer": ["Referrer", "来源"],
	"traffic.byCampaign": ["Campaigns", "推广参数"],
	"traffic.byCampaignHint": ["From utm_campaign on the landing URL.", "来自落地地址上的 utm_campaign 参数。"],
	"traffic.campaign": ["Campaign", "推广"],
	"traffic.direct": ["Direct", "直接访问"],
	"traffic.empty": ["Nothing in this window.", "该时间范围内没有数据。"],
	// Assistants that send traffic but that this product does not sample.
	"traffic.engine.copilot": ["Copilot", "Copilot"],
	"traffic.engine.meta": ["Meta AI", "Meta AI"],
	"traffic.engine.you": ["You.com", "You.com"],
	"traffic.engine.poe": ["Poe", "Poe"],
	"traffic.engine.phind": ["Phind", "Phind"],
	"traffic.engine.mistral": ["Le Chat", "Le Chat"],

	"traffic.start.title": ["Start measuring visits from AI", "开始追踪 AI 带来的访问"],
	"traffic.start.body": [
		"The tracker has not received a visit yet. Complete these steps before reading the reports.",
		"跟踪代码尚未收到访问。先完成安装和验证，再查看流量报告。",
	],
	"traffic.start.install": ["Install the Ondit tracker", "安装 Ondit 跟踪代码"],
	"traffic.start.installBody": [
		"Get your code in tracking settings and add it to your website.",
		"在跟踪设置中获取代码，添加到你的网站。",
	],
	"traffic.start.verify": ["Verify an actual visit", "验证一次真实访问"],
	"traffic.start.verifyBody": [
		"Visit a page with the code, then check that it appears in Install check.",
		"打开已安装代码的页面，在「安装检查」中确认这次访问出现。",
	],
	"traffic.start.read": ["Read your referral traffic", "查看 AI 回流流量"],
	"traffic.start.readBody": [
		"See which assistants sent visitors and where they landed. Add conversion goals later if you want to measure signups or other outcomes.",
		"查看哪些助手带来访问、访客落在哪些页面。需要衡量注册等结果时，再添加转化目标。",
	],
	"traffic.start.verifyAction": ["Already installed? Verify a visit", "已安装代码？验证访问"],
	"traffic.reconnectTitle": ["Reconnect Google Analytics", "重新连接 Google Analytics"],
	"traffic.siteOnlyTitle": ["This report needs the Ondit tracker", "该报告需要 Ondit 跟踪代码"],
	"traffic.periodEmpty": ["No visits in this period", "此时段没有访问"],
	"traffic.periodEmptyBody": [
		"Try a longer period. If you expected new visits, use Install check to verify that your site is still reporting.",
		"可扩大时间范围。若预期有新访问，请到安装检查确认网站仍在上报。",
	],
	"traffic.noAi": ["No AI referrals in this period", "此时段尚无 AI 来访"],
	"traffic.noAiBody": [
		"Visits were recorded, but none had a recognized AI referrer. The referral tables below show the sources we did receive.",
		"已记录到访问，但来源中没有识别出的 AI 助手。下方来源表展示实际收到的来访。",
	],
	"traffic.breakdown": ["Break down traffic by", "选择流量维度"],
	"traffic.aiScope": ["AI-referred visits only.", "仅统计 AI 来源的访问。"],
	"traffic.allSourcesScope": ["All recorded referrers, including non-AI sources.", "所有已记录来源，包含非 AI 来访。"],
	"traffic.explorePaths": ["Explore where visitors went next", "查看访客接着去了哪里"],
	"traffic.measureOutcomes": ["Define a conversion goal", "定义一个转化目标"],
	"traffic.copyFailed": ["Copy failed. Select the code and copy it manually.", "复制失败，请选中代码后手动复制。"],
	"traffic.gaLimit": [
		"Google Analytics provides aggregate referral traffic. Visitor paths, Ondit goals, attribution, page timings and installation checks require the Ondit tracker.",
		"Google Analytics 提供回流流量汇总。访问路径、Ondit 转化目标、归因、页面性能和安装检查需要 Ondit 跟踪代码。",
	],
	"traffic.refresh": ["Refresh", "刷新"],
	"traffic.installStep": ["1. Add the code to your site", "1. 将代码添加到网站"],
	"traffic.verifyStep": ["2. Check that visits arrive", "2. 确认访问上报"],
	"traffic.verifyBody": [
		"After publishing the code, open a page on your site and check its visit record. Generating or copying the code alone does not complete installation.",
		"发布代码后，打开网站页面并检查访问记录。仅生成或复制代码，还不代表安装完成。",
	],
	"traffic.privacy": ["Visitor identity and data retention", "访客识别与数据保留"],
	"live.waiting": ["Waiting for your test visit", "等待你的测试访问"],
	"live.testSteps": [
		"Open a page with the tracker in another tab, browse it, then return here. Match the page path and time to confirm your visit was received.",
		"在另一个标签页打开已安装代码的页面，浏览后返回这里。核对页面路径和时间，确认你的访问已收到。",
	],
	"live.troubleshoot": [
		"Still nothing? Check that the code is published on that page and that your browser is not blocking it. You can retrieve the code in tracking settings.",
		"仍未出现？检查代码是否已发布到该页面，以及浏览器是否拦截了跟踪脚本。可在跟踪设置中重新获取代码。",
	],
	"live.polling": [
		"Updates every 10 seconds · includes direct visits, not only AI referrals.",
		"每 10 秒自动刷新 · 包含直接访问，不限于 AI 来访。",
	],
	"journeys.emptyTitle": ["No AI visitor paths in this period", "此时段没有 AI 访问路径"],
	"journeys.emptyBody": [
		"Paths appear after an AI-referred visit. Start with referral traffic to see whether any AI visitors arrived in this period.",
		"AI 引荐的访问到来后才会出现路径。可先查看回流流量，确认此时段是否有 AI 来访。",
	],
	"goals.gaNotice": [
		"These goals require the Ondit tracker; they do not configure Google Analytics key events.",
		"这些目标需要 Ondit 跟踪代码，不会修改 Google Analytics 的关键事件。",
	],
	"goals.installNotice": [
		"You can define goals now. Counting begins once the tracker receives matching visits or events.",
		"可以先定义目标。跟踪代码收到匹配的访问或事件后，才会开始计数。",
	],
	"goals.firstBody": [
		"Add a meaningful outcome, such as reaching /signup/success after registration, or sending a signup event.",
		"添加一个有业务意义的结果，例如注册后到达 /signup/success，或上报 signup 事件。",
	],
	"goals.optional": [
		"Goals are optional for traffic reports. Add one when you want to measure conversions and attribution.",
		"查看流量不必设置目标。需要衡量转化及来源归因时，再添加目标。",
	],
	"goals.invalidPath": [
		"Enter a page path starting with /, such as /signup/success. Do not use a full URL or / on its own.",
		"请输入以 / 开头的页面路径，例如 /signup/success。不要填写完整网址，也不能仅填写 /。",
	],
	"goals.viewAttribution": ["See which assistants drove conversions", "查看哪些助手带来转化"],
	"goals.checkRules": ["Review goal rules", "检查目标规则"],
	"attribution.noConversions": ["No AI conversions to attribute in this period", "此时段没有可归因的 AI 转化"],
	"attribution.noConversionsBody": [
		"A goal must match a visit or event, and an AI referral must be available for attribution. Review the rules or choose a longer period.",
		"访问或事件需匹配目标，并存在可归因的 AI 来源。可检查目标规则，或扩大时间范围。",
	],
	"vitals.emptyTitle": ["Waiting for page timing samples", "等待页面性能样本"],
	"traffic.source": ["Measured by", "数据来源"],
	"traffic.sourceHint": ["One source per day, never both.", "同一天只用一种来源，不会两种同时生效。"],
	"traffic.source.site": ["Ondit tracker", "Ondit 跟踪代码"],
	"traffic.source.ga4": ["Google Analytics", "Google Analytics"],
	"traffic.source.none": ["Not set up", "尚未设置"],
	"traffic.source.ga4Floor": [
		"GA withholds small rows without saying so, so these are a floor",
		"GA 会在不提示的情况下隐去小数值的行，所以这些数字是下限",
	],
	"traffic.source.switched": ["before {day} this range was measured by {source}", "{day} 之前这段区间由 {source} 统计"],
	"traffic.source.window": ["{from} to {to}", "{from} 至 {to}"],
	"settings.section.tracking": ["Tracking", "跟踪"],
	"traffic.installNeeded": [
		"No visits received from the Ondit tracker yet. Check the installation in settings.",
		"尚未收到 Ondit 跟踪代码上报的访问，请前往设置检查安装。",
	],
	"traffic.noSource": [
		"Ondit tracking is the default. Complete the installation in settings to start collecting visits.",
		"默认使用 Ondit 跟踪，请前往设置完成安装，开始收集访问数据。",
	],
	"traffic.noSourceAction": ["Open tracking settings", "前往跟踪设置"],
	"traffic.needsSite": [
		"Google Analytics returns totals, not individual visits, so this reading only exists for days the Ondit tracker measured.",
		"Google Analytics 只返回汇总值、不提供单次访问明细，所以这个读数只在由 Ondit 跟踪代码统计的日期存在。",
	],
	"traffic.setup": ["Source", "数据来源"],
	"traffic.setupHint": [
		"Use the Ondit tracker by default, or connect a Google Analytics property.",
		"默认使用 Ondit 跟踪代码，也可接入 Google Analytics 媒体资源。",
	],
	"traffic.gaProperty": ["Property ID", "媒体资源 ID"],
	"traffic.gaAccount": ["Google account", "Google 账号"],
	"traffic.gaAuthorize": ["Connect a Google account", "连接 Google 账号"],
	"traffic.gaUse": ["Use this property", "使用此媒体资源"],
	"traffic.gaOAuthHint": [
		"Connect a Google account that can read this property, then use the property. The brand switches to GA from today.",
		"连接一个能读取该媒体资源的 Google 账号，再使用此媒体资源。该品牌从今天起使用 GA 数据。",
	],
	"traffic.gaReauthorize": [
		"Google authorization is required before syncing this property. Reconnect to resume and restore its history.",
		"此媒体资源需要 Google 授权。重新连接后将恢复同步并补回历史数据。",
	],
	"traffic.gaAuthorizationFailed": [
		"Google authorization did not complete. Check that your account can read this property, then try again.",
		"Google 授权未完成。请确认该账号有权读取此媒体资源，然后重试。",
	],
	"traffic.gaUnconfigured": [
		"Google OAuth is not configured on this deployment. Contact your administrator.",
		"当前部署尚未配置 Google OAuth，请联系管理员。",
	],
	"errors.traffic_ga_oauth_required": [
		"Reconnect Google to read this property.",
		"请重新授权 Google 以读取此媒体资源。",
	],
	"errors.traffic_ga_oauth_unconfigured": ["Google OAuth is not configured.", "尚未配置 Google OAuth。"],
	"errors.traffic_ga_oauth_denied": ["Google authorization was cancelled.", "Google 授权已取消。"],
	"traffic.gaSynced": ["Last pulled {when}.", "上次拉取于{when}。"],
	"traffic.snippetHint": [
		"Paste this into every page you want measured, before the closing head tag.",
		"把这段代码贴到每个需要统计的页面，放在 head 结束标签之前。",
	],
	"traffic.mintToken": ["Create snippet", "生成代码片段"],
	"traffic.connectGa": ["Connect", "连接"],
	"traffic.cookie": ["Remember visitors", "识别回访访客"],
	"traffic.cookieHint": [
		"Writes a first-party cookie on your own domain, so a returning visitor is the same person next month. Your site's consent notice covers it. Off by default, and off means no cookie at all.",
		"在你自己的域名下写入第一方 Cookie，这样下个月回访的人仍会被识别为同一个人。需要由你站点的同意提示覆盖。默认关闭，关闭时完全不写 Cookie。",
	],
	"traffic.retention": ["Keep addresses for", "IP 地址保留"],
	"traffic.retentionDays": ["{days} days", "{days} 天"],
	"traffic.retentionHint": [
		"Only the address is removed after this; the visit itself stays, so history is not lost.",
		"到期后只删除 IP 地址，访问记录本身保留，历史数据不会丢失。",
	],
	"traffic.switchTitle": ["Change the source?", "更换数据来源？"],
	"traffic.switchBody": [
		"{source} measures from today. Days already measured keep whatever measured them, and the two are never added together.",
		"{source} 从今天起开始统计。已经统计过的日期仍归原来的来源，两者永远不会相加。",
	],
	"traffic.switchConfirm": ["Change source", "更换来源"],
	"traffic.saved": ["Saved.", "已保存。"],

	"hint.trafficSessions": [
		"A visit, ending after 30 minutes of inactivity — GA4's rule, so the two figures are comparable.",
		"一次访问，30 分钟无活动后结束 —— 这是 GA4 的规则，所以两边的数字可以互相比较。",
	],
	"hint.trafficShare": [
		"Sessions an assistant sent, over every referred session. Both counts are on this screen so you can check it.",
		"助手送来的会话数，除以全部来访会话数。两个数字都在本页上，可以自行核对。",
	],
	"hint.trafficUsers": [
		"Distinct visitors. Without the cookie on, the same person next month counts as a new one.",
		"独立访客。未开启 Cookie 时，同一个人下个月会被算作新访客。",
	],
	"hint.trafficEngagement": [
		"Sessions over ten seconds, or with two page views, or with a conversion — GA4's definition. Bounce rate is one minus this.",
		"超过十秒、或浏览两个页面、或产生转化的会话 —— 这是 GA4 的定义。跳出率就是 1 减去它。",
	],
	"hint.trafficConversions": [
		"Sessions that met one of your goals. Your own paths and events, never GA's key events.",
		"达成了你设定的某个目标的会话。只按你自己的路径和事件，不采用 GA 的关键事件。",
	],

	"attribution.lede": [
		"Which assistant gets the credit when somebody converts. The only screen here that connects an answer to something your business counts.",
		"有人转化时，功劳归给哪个助手。这是本产品唯一把回答和你的业务数字连起来的页面。",
	],
	"attribution.credits": ["Credited assistants", "归因助手"],
	"attribution.first": ["First touch", "首次触达"],
	"attribution.last": ["Last touch", "末次触达"],
	"attribution.firstHint": [
		"Credited to the assistant that first brought them, looked for up to ninety days back — an introduction three months ago is still the introduction.",
		"归给最早把他们带来的那个助手，最多回溯九十天 —— 三个月前的引荐仍然是引荐。",
	],
	"attribution.lastHint": [
		"Credited to the assistant on the visit that converted.",
		"归给产生转化的那次访问所属的助手。",
	],
	"attribution.rate": ["Conversion rate", "转化率"],
	"attribution.empty": [
		"Nothing has converted yet, or nothing counts as a conversion.",
		"还没有转化，或者尚未定义什么算作转化。",
	],
	"attribution.emptyAction": ["Set a goal", "设置转化目标"],

	"journeys.lede": [
		"Where a referral went after the page it landed on. One hop: either a page sent them deeper into your site, or it ended the visit.",
		"从落地页出发，来访者接着去了哪里。只看一步：要么这个页面把人带向站内更深处，要么访问就此结束。",
	],
	"journeys.hops": ["Landing page, then", "落地页之后"],
	"journeys.hopsHint": [
		"Shows the landing page and the next page in AI-referred sessions. A visit ending here does not explain why the visitor left.",
		"展示 AI 来访的落地页和下一页。访问到此结束，不代表已经知道访客离开的原因。",
	],
	"journeys.landed": ["Landed on", "落地于"],
	"journeys.then": ["Then", "接着"],
	"journeys.ended": ["The visit ended here", "访问到此结束"],

	"goals.lede": [
		"What counts as a conversion for this brand — your own paths and events, never GA's key events. Attribution is empty without one.",
		"该品牌把什么算作一次转化 —— 只用你自己的路径和事件，不采用 GA 的关键事件。没有目标，归因页就是空的。",
	],
	"goals.saving": ["Adding…", "添加中…"],
	"goals.add": ["Add a goal", "新增目标"],
	"goals.addHint": [
		"A path matches by prefix, so /pricing also covers /pricing/teams. An event matches the name your site passes to ondit.track().",
		"路径按前缀匹配，所以 /pricing 也会覆盖 /pricing/teams。事件按你站点传给 ondit.track() 的名称精确匹配。",
	],
	"goals.empty": ["No goals yet.", "还没有设置目标。"],
	"goals.name": ["Name", "名称"],
	"goals.namePlaceholder": ["Signed up", "完成注册"],
	"goals.match": ["Matches", "匹配"],
	"goals.kind.path": ["Path", "路径"],
	"goals.kind.event": ["Event", "事件"],
	"goals.pathHint": [
		"Matches a page path prefix: /signup/success also includes /signup/success/team. Only future matching visits count.",
		"按页面路径前缀匹配：/signup/success 也包含 /signup/success/team。仅统计创建目标后的匹配访问。",
	],
	"goals.eventHint": [
		"Call ondit.track('name') on your site when it happens.",
		"事件发生时，在你的站点上调用 ondit.track('名称')。",
	],
	"goals.added": ["Goal added.", "目标已新增。"],
	"goals.removed": ["Goal removed.", "目标已删除。"],
	"goals.removeTitle": ["Remove this goal?", "删除该目标？"],
	"goals.removeBody": [
		"{name} stops counting. The visits it already matched keep their conversion.",
		"{name} 将不再计数。此前已匹配的访问仍保留其转化记录。",
	],

	"vitals.lede": [
		"Timing samples collected from visitors on your site, including non-AI visits. These measure browser experience, not crawler performance.",
		"来自网站访客的页面性能样本，包含非 AI 来访。衡量浏览器中的访问体验，不代表抓取工具的性能。",
	],
	"vitals.pages": ["By page", "按页面"],
	"vitals.pagesHint": [
		"The 75th percentile for each metric. Views counts samples with LCP data; a dash means that metric has no samples.",
		"每项指标取第 75 百分位。浏览数为带有 LCP 数据的样本数；破折号表示该指标尚无样本。",
	],
	"vitals.path": ["Page", "页面"],
	"vitals.views": ["Views", "浏览"],
	"vitals.empty": [
		"No page has reported timings yet. They are sent when a page is closed, not when it loads.",
		"还没有页面上报耗时数据。这些数据在页面关闭时发送，而不是在加载时。",
	],

	"live.lede": [
		"The last ten hits. Not a dashboard — this is how you check the snippet works in the minute after pasting it in.",
		"最近十条访问记录。这不是实时看板 —— 它是你贴上代码后，用来在一分钟内确认是否生效的。",
	],
	"live.recent": ["Recent hits", "最近访问"],
	"live.recentHint": [
		"Refreshes every ten seconds. Open your own site in another tab and your visit should appear here.",
		"每十秒刷新一次。在另一个标签页打开你自己的站点，你的这次访问应该会出现在这里。",
	],
	"live.empty": [
		"Nothing yet. If the snippet is on the page, a visit to it should show up within a few seconds.",
		"暂时没有数据。如果代码已经在页面上，访问该页面后几秒内应该会出现。",
	],
	"live.when": ["When", "时间"],
	"live.where": ["Where from", "来自"],

	"errors.traffic_payload": ["That hit could not be read.", "无法解析该上报数据。"],
	"errors.traffic_goal_kind": ["A goal matches a path or an event.", "目标只能匹配路径或事件。"],
	"errors.traffic_goal_empty": ["A goal needs a name and something to match.", "目标需要名称和匹配内容。"],
	"errors.traffic_goal_too_broad": [
		"A goal of / would mark every visit as a conversion, which makes attribution meaningless.",
		"把目标设为 / 会让每一次访问都算作转化，归因就没有意义了。",
	],
	"errors.traffic_source_unknown": ["That is not a source this product reads.", "本产品不支持该数据来源。"],
	"errors.traffic_ga_property_required": [
		"A Google Analytics property ID is needed.",
		"需要填写 Google Analytics 媒体资源 ID。",
	],
	"errors.traffic_ga_property_invalid": [
		"A property ID is the number GA shows in its admin, with no prefix.",
		"媒体资源 ID 是 GA 管理界面里显示的那串数字，不含前缀。",
	],
	"errors.traffic_ga_property_unreadable": [
		"That property cannot be read yet. Add the service account as a Viewer on it in Google Analytics first.",
		"暂时读不到该媒体资源。请先在 Google Analytics 中把服务账号添加为它的“查看者”。",
	],
	"errors.traffic_ga_property_claimed": [
		"That property is already connected to another brand. A property belongs to one brand.",
		"该媒体资源已连接到另一个品牌。一个媒体资源只能属于一个品牌。",
	],
	"errors.notFound.goal": ["That goal is gone.", "该目标不存在。"],

	// -- the brief: what a page would be written from, before anything is spent
	"briefs.lede": [
		"Every gap waiting, and the facts a page for it would be allowed to state. Assembled from your own data with no model call, so you can read the refusals before paying for them.",
		"每一个待处理的缺口，以及为它写页面时允许陈述的事实。全部由你自己的数据拼成、不调用模型，所以花钱之前就能看到会被拒绝的原因。",
	],
	"briefs.empty": ["No gaps are waiting in this scope.", "该口径下没有待处理的缺口。"],
	"briefs.emptyBody": [
		"A brief is assembled from a gap somebody accepted. Accept one to write from.",
		"文档摘要是从被采纳的缺口生成的。先采纳一个缺口，这里就有内容可写。",
	],
	"briefs.view": ["Review brief", "查看依据"],
	"briefs.foot": [
		"Every gap in this list is on the gaps screen too, with the answers behind it:",
		"这里的每个缺口在缺口页也有一行，可以看到它背后的回答：",
	],
	"briefs.fact": ["Fact", "事实"],
	"briefs.value": ["Value", "取值"],
	"briefs.source": ["Source", "出处"],
	"briefs.hasSource": ["Sourced", "有出处"],
	"briefs.noSource": ["No source", "无出处"],
	"briefs.noFacts": ["There are no facts on record for this brand yet.", "这个品牌还没有任何在册事实。"],
	"briefs.noFactsBody": [
		"Every sentence on a generated page has to come from one, so this brief has nothing to write from.",
		"生成页面上的每句话都必须来自某条事实，所以这份摘要现在没有可写的内容。",
	],
	// The floor, said as a count rather than as a rule: a page needs two, and
	// this is how many there are.
	"briefs.sources": ["{n} distinct sources behind these facts.", "这些事实背后有 {n} 个不同的出处。"],
	"briefs.recipe.vs_generate": ["A comparison page", "对比页"],
	"briefs.recipe.alternatives_generate": ["An alternatives page", "替代方案页"],
	"briefs.recipe.entity_enrich": ["The brand's own page", "品牌自己的页面"],
	"briefs.recipe.faq_generate": ["An FAQ page", "问答页"],
	"briefs.recipe.best_generate": ["A ranking page", "榜单页"],

	// -- the published pages a reflow has not concluded on yet
	"reflowPages.lede": [
		"Every published page and what has happened since. These are counts; whether the difference means anything is what a reflow report says, and one appears {days} days after a page goes live, only if both windows clear the sample floor.",
		"每个已发布页面，以及发布之后发生了什么。这里是计数；差异是否说明问题由回流报告回答——页面上线 {days} 天后才会有一份，且两侧样本都要达到下限。",
	],
	"reflowPages.empty": ["Nothing published in this language yet.", "这个语言下还没有发布过页面。"],
	"reflowPages.emptyBody": [
		"A page has to be live before there is a before and an after to compare.",
		"页面上线之后，才有上线前和上线后可以对比。",
	],
	"reflowPages.live": ["Live", "已上线"],
	"reflowPages.days": ["{n}d", "{n} 天"],
	"reflowPages.before": ["Cited before", "上线前被引"],
	"reflowPages.since": ["Cited since", "上线后被引"],
	"reflowPages.conclusion": ["Conclusion", "结论"],
	"reflowPages.waiting": ["{n} days to go", "还差 {n} 天"],
	"reflowPages.due": ["Measurement due", "待测量"],

	"insights.empty": ["Nothing has been sampled in this range yet.", "该时间范围内还没有采样数据。"],
	"insights.emptyBody": [
		"Every reading on this stage is computed over collected answers. Collect samples, or widen the period and the engines above.",
		"这个阶段的每个读数都是基于已采集的回答算出来的。可以先采样，或把上方的时间范围和引擎放宽。",
	],
	// The denominator, under every headline. A 100% on four answers and a 100%
	// on four hundred are the same figure and not the same finding.
	"insights.metricView": ["Metric to chart", "图表指标"],
	"insights.metricTrend": ["{metric} trend", "{metric}趋势"],
	"insights.voiceDistribution": ["Share of voice distribution", "声量分布"],
	"insights.brandComparison": ["Brand comparison", "品牌对比"],
	"insights.topicView": ["Topic view", "主题视图"],
	"insights.tableView": ["Table", "表格"],
	"insights.denominator": [
		"{n} answers in this window, against {before} in the one before it.",
		"本周期共 {n} 条回答，上一周期为 {before} 条。",
	],
	"insights.rank": ["Rank", "排名"],
	"insights.brand": ["Brand", "品牌"],
	"insights.expand": ["Show all", "展开"],
	"insights.collapse": ["Show fewer", "收起"],
	"insights.noData": ["No data yet", "暂无数据"],
	"insights.flat": ["unchanged", "持平"],
	// Percentage points, as the unit a movement between two shares is measured in.
	"insights.pp": ["{n}pp", "{n} 个百分点"],
	"insights.other": ["Everyone else", "其他"],
	"insights.seriesAlt": ["This metric over the window, one line per brand", "本周期内该指标走势，每个品牌一条线"],
	"insights.topic": ["Topic", "主题"],
	"insights.noTopic": ["Not filed", "未归类"],
	"insights.prompts": ["Prompts", "提示词数"],
	"insights.nPrompts": ["{n} prompts", "{n} 个提示词"],
	"insights.runs": ["Answers", "回答数"],
	"insights.byTopic": ["By topic", "按主题划分"],
	"insights.byTopicHint": [
		"Where this brand stands subject by subject, against the same brands it is measured against everywhere else.",
		"逐个主题看这个品牌的位置，对比的是与其他页面一致的那组品牌。",
	],
	"insights.matrix": ["Topic priority matrix", "主题优先级矩阵"],
	"insights.matrixHint": [
		"Two halves of being cited: named in the answer, and linked from it. The area of each topic is the answers behind it.",
		"被引用的两个侧面：在回答里被提及，以及被回答链接。每个主题的面积代表其背后的回答数量。",
	],
	// The four quadrants, which are the point of the chart: each one is a
	// different piece of work, and naming them is what turns a scatter into an
	// instruction.
	"insights.quadrant.absent": ["Not in the answer", "不在回答中"],
	"insights.quadrant.unlinked": ["Named, not linked", "被提及，未被引用"],
	"insights.quadrant.uncredited": ["Linked, not named", "被引用，未被提及"],
	"insights.quadrant.strong": ["Working", "优势阵地"],
	"insights.promptSummary": [
		"{prompts} prompts over {topics} topics and {types} prompt types.",
		"共 {prompts} 个提示词，覆盖 {topics} 个主题、{types} 种提示词类型。",
	],
	"insights.nAnswers": ["{n} answers", "{n} 条回答"],
	"insights.editPrompts": ["Edit prompts", "编辑提示词"],
	"insights.groupByTopic": ["Group by topic", "按主题分组"],
	"insights.columns": ["Columns", "自定义列"],
	"insights.askAccess": ["Ask about access", "申请访问权限"],

	// -- the five metrics
	"metric.visibility": ["Visibility", "可见度"],
	"metric.visibility.hint": [
		"Of the answers we collected in this window, the share that named this brand. Answers, not mentions: a brand named three times in one answer is one visible answer.",
		"本周期采集到的回答中，提及该品牌的比例。统计的是回答数而非提及次数：一条回答里被提及三次，仍然只算一条。",
	],
	"metric.share_of_voice": ["Share of voice", "声量占比"],
	"metric.share_of_voice.hint": [
		"Of every brand named in those answers, the share of namings that were this one.",
		"这些回答提及的所有品牌中，属于该品牌的提及占比。",
	],
	"metric.average_position": ["Average position", "平均位置"],
	"metric.average_position.hint": [
		"Where in the answer this brand lands when it is named, counting from one. Only defined where it was named at all — the average rank of a brand nobody mentioned is not zero, it is nothing.",
		"该品牌被提及时，在回答中出现的平均次序（从 1 开始）。仅在被提及时才有值——从未被提及的品牌不是 0 名，而是没有名次。",
	],
	"metric.citation_share": ["Citation share", "引用占比"],
	"metric.citation_share.hint": [
		"The share of questions where a domain this brand owns was cited. Within one question, a domain counts as cited when it appeared in more than half of that question's answers; the figure is the share of questions where that held.",
		"该品牌自有域名被引用的问题占比。在同一个问题内，域名在超过一半的回答中出现才算被引用；这个数字是满足该条件的问题所占的比例。",
	],
	"metric.sentiment": ["Brand favourability", "品牌好感度"],
	"metric.sentiment.hint": [
		"Positive mentions less negative ones, over the mentions the reading pass has actually read. Never over all of them — a brand whose answers are still queued would otherwise read as one nobody had a good word for.",
		"正面提及减去负面提及，除以解读环节已读过的提及数。分母不含未解读的部分——否则刚开始采集的品牌会显示成人人差评。",
	],

	// -- the insight filter bar
	"filter.vsPrevious": ["vs. previous period", "对比上一周期"],
	"filter.topics": ["Topics", "主题"],
	"filter.allTopics": ["All topics", "全部主题"],
	"filter.nTopics": ["{n} selected", "已选 {n} 个"],
	"filter.clear": ["Clear", "清除"],
	"filter.intent": ["Prompt type", "提示词类型"],
	"filter.allIntents": ["All types", "全部类型"],
	"filter.category": ["Citation category", "引用分类"],
	"filter.allCategories": ["All categories", "全部分类"],
	"filter.tag": ["Citation tag", "引用标签"],
	"filter.allTags": ["All tags", "全部标签"],
	"filter.views": ["Views", "视图"],
	"filter.noViews": ["No saved views yet", "还没有保存的视图"],
	"filter.saveView": ["Save this view", "保存当前视图"],
	"filter.viewName": ["Name this view", "为视图命名"],
	"filter.viewSaved": ["View saved", "视图已保存"],

	// -- query fan-outs
	"fanouts.noCoverage": ["No searches reported in this window", "本周期没有查询分支数据"],
	"fanouts.noCoverageBody": [
		"Not every assistant reports the searches behind its answer, and none of them promise to. A blank here means it was not reported, not that nothing was searched for.",
		"并非每个助手都会返回它在回答前做过的检索，也没有任何一家承诺提供。这里为空表示没有回传数据，而不是它没有检索。",
	],
	"fanouts.coverage": [
		"{reported} of {total} prompts reported their searches.",
		"{total} 个提示词中，有 {reported} 个回传了检索记录。",
	],
	"fanouts.byPlatform": ["Searches per answer, by platform", "各平台每次执行的查询数"],
	"fanouts.byPlatformHint": [
		"How many searches each assistant runs before answering. Coverage differs by platform, so this compares those that report it.",
		"每个助手在回答前平均检索多少次。各平台的回传情况不同，这里只比较有回传的平台。",
	],
	"fanouts.viewQueries": ["View searches ({n})", "查看检索词（{n}）"],
	"fanouts.byPrompt": ["Searches by prompt", "按提示词划分的查询分支"],
	"fanouts.byPromptHint": [
		"The assistant's own rewording of the question — which is what a page actually has to match. “Searched” is how often it looked anything up at all: a prompt it answers from memory will not move when you publish a page for it.",
		"助手对问题的重写方式——这才是页面真正需要匹配的说法。“触发检索”是它有多大比例真的去查了：凭记忆回答的提示词，即使为它发布页面也不会有变化。",
	],
	"fanouts.queries": ["Searches", "查询数"],
	"fanouts.perRun": ["Per answer", "每次执行"],
	"fanouts.rate": ["Searched", "触发检索"],

	// -- platforms
	"platforms.matrix": ["Matrix view", "矩阵视图"],
	"platforms.matrixHint": [
		"Every brand on every platform, one metric at a time. One metric, because a table whose cells came from two of them is a table whose cells mean different things.",
		"每个品牌在每个平台上的表现，一次看一个指标。只看一个：如果格子里混着两个指标，同一列的含义就不一样了。",
	],
	"platforms.metric": ["Metric", "指标"],

	// -- sentiment
	"sentiment.lede": [
		"How AI evaluates your brand, the evidence behind negative answers, and what to improve.",
		"AI 如何评价你的品牌、负面回答背后的线索，以及可以采取的改进措施。",
	],
	"sentiment.negative.title": ["Reasons behind negative evaluations and next steps", "负面评价的原因及改进建议"],
	"sentiment.negative.hint": [
		"Investigate the latest 20 negative answers in this scope. Summaries and themes are leads, not verified causes; a negative opinion is not necessarily a factual error.",
		"展示当前口径下最近 20 条负面回答。摘要和主题是原因线索，不是已证实的原因；负面评价也不一定是事实错误。",
	],
	"sentiment.negative.empty": [
		"No negative evaluations were found among the answers read in this scope.",
		"当前口径下，已解读的回答中未发现负面评价。",
	],
	"sentiment.negative.pending": [
		"Reasons and suggestions will appear once negative evaluations have been identified. Unread answers are not positive evaluations.",
		"识别出负面评价后，这里会显示原因线索与改进建议。未解读的回答不代表正面评价。",
	],
	"sentiment.negative.all": ["View all negative answers", "查看全部负面回答"],
	"sentiment.negative.answer": ["Negative evaluation", "负面评价"],
	"sentiment.negative.reason": ["Reason to investigate", "原因线索"],
	"sentiment.negative.action": ["Suggested next steps", "改进建议"],
	"sentiment.negative.noSummary": [
		"No evaluation summary was recorded. Open the answer to inspect its wording.",
		"尚无评价摘要，请打开原始回答核对具体表述。",
	],
	"sentiment.negative.themes": ["Topics in this answer: {themes}", "该回答涉及的主题：{themes}"],
	"sentiment.negative.noReason": [
		"No specific cause has been established. Check the original wording and its cited sources first.",
		"尚未确认具体原因。先核对原始表述及其引用来源。",
	],
	"sentiment.negative.mismatch": [
		"This answer also has factual discrepancies awaiting review; check whether they explain the negative evaluation.",
		"这条回答还有待处理的事实差异，请核对它们是否与负面评价有关。",
	],
	"sentiment.negative.claim": ["Answer claim: {claim}", "回答中的说法：{claim}"],
	"sentiment.negative.truth": ["Recorded fact: {truth}", "已记录的事实：{truth}"],
	"sentiment.negative.correct": [
		"Verify the recorded facts and their sources. If the answer is wrong, prepare a sourced clarification; do not treat an opinion as an error.",
		"核对已记录的事实及其来源。确认回答有误后，补充有出处的澄清内容；不要把主观评价当作错误纠正。",
	],
	"sentiment.negative.context": [
		"Check the evaluation against the product's current facts. For a real limitation, explain its scope and alternatives; for a misunderstanding, add sourced documentation or an FAQ.",
		"将评价与产品当前事实核对。若确有局限，说明适用范围和替代方案；若属于误解，补充有出处的说明或常见问题解答。",
	],
	"sentiment.negative.verify": ["Review factual discrepancies", "核对事实差异"],
	"sentiment.negative.facts": ["Review brand facts", "核对品牌事实"],
	"sentiment.negative.brief": ["Prepare supporting content", "查看写作依据"],

	"sentiment.empty": ["This brand has not been named in any answer yet.", "还没有任何回答提及这个品牌。"],
	"sentiment.emptyBody": [
		"Sentiment is read from the answers that mention you, so there is nothing to read until one does.",
		"倾向是从提到你的回答里读出来的，在有回答提到你之前没有可读的内容。",
	],
	"sentiment.building": ["Reading brand evaluations", "正在解读品牌评价"],
	"sentiment.buildingBody": [
		"The reading pass rides on the fact-check call, so this fills in over the next few runs rather than all at once.",
		"品牌评价会在后续运行中逐步解读。尚未解读的回答不计入好感度。",
	],
	"sentiment.read": ["{read} of {total} mentions read ({percent}%).", "已解读 {read} / {total} 条提及（{percent}%）。"],

	// -- citations
	"citations.kind.competitor": ["Competitor", "竞品"],
	"citations.kind.editorial": ["Editorial", "媒体报道"],
	"citations.share": ["Share", "占比"],
	// The gap between this column and the citation count is the finding: a link
	// the answer spent that did not put the brand's name in the text.
	"citations.withMention": ["With a mention", "同时被提及"],
	"citations.sources": ["Citation sources", "引用来源"],
	"citations.domainsView": ["Domains", "域名"],
	"citations.pagesView": ["Pages", "页面"],
	"citations.topDomains": ["Most cited domains", "热门引用域名"],
	"citations.topDomainsHint": [
		"Which sites the answers leaned on. The site is who to talk to; the page below is what to write.",
		"回答主要依赖哪些站点。站点决定去找谁谈，下面的页面决定写什么。",
	],
	"citations.topPages": ["Most cited pages", "热门引用页面"],
	"citations.topPagesHint": [
		"The same links at the page grain, so the two tables add up to the same total.",
		"同一批链接按页面粒度统计，两张表的总数一致。",
	],
	"citations.monitored": ["Watched pages", "已监控页面"],
	"citations.monitoredHint": [
		"Is the page published last week being cited yet? A page nothing has cited stays in this table with a zero — that row is the reason the table exists.",
		"上周发布的页面开始被引用了吗？还没有被引用的页面会以 0 留在表里——那一行正是这张表存在的理由。",
	],
	"citations.watch": ["Watch", "加入监控"],
	"citations.noneWatched": ["Nothing is being watched yet.", "还没有监控任何页面。"],
	"citations.noneWatchedBody": [
		"Watch a cited page to be told when it stops being cited.",
		"把一个被引用的页面加入监控，它不再被引用时你会收到提醒。",
	],
	"citations.firstCited": ["First cited", "首次被引用"],
	"citations.notYet": ["Not yet", "尚未"],

	"settings.section.audiences": ["Audiences", "受众"],
	"settings.section.audiencesHint": [
		"Target countries, buyer profiles and the prompts they use.",
		"配置目标国家、客户角色及对应提示词。",
	],
	"errors.notFound.audience": ["This audience no longer exists or is unavailable.", "此受众不存在或不可用。"],
	"audience.selectCountry": ["Select a country or territory", "选择国家或地区"],
	"audience.moreAttributes": ["Additional audience details", "更多受众资料"],
	"audience.configure": ["Configure audiences", "配置受众"],
	"audience.regions.help": [
		"Compare answers collected from verified country exits. Content language is a separate setting.",
		"按经验证的国家出口比较回答，内容语言单独设置。",
	],
	"audience.personas.help": [
		"Compare answers sampled for your buyer roles. Profiles are frozen with each sample.",
		"比较面向不同客户角色采集的回答，每次采样保留当时的角色资料。",
	],
	"audience.regionPending": [
		"Regional sampling is not available yet. You can configure target countries now; measurement needs a regional data connection.",
		"地区采样尚未接通。可以先配置目标国家，接通地区数据后才能开始监测。",
	],
	"audience.comparison": ["Performance comparison", "表现对比"],
	"audience.comparisonHelp": [
		"Each segment uses its own answers as the metric scope. The previous value covers the immediately preceding period; its answer count is shown alongside.",
		"各组指标基于该组回答计算。上期取紧邻的前一个等长周期，数值旁显示回答数。",
	],
	"audience.regions.empty": [
		"Choose the countries you want to measure in audience settings.",
		"在受众设置中添加希望监测的国家。",
	],
	"audience.personas.empty": [
		"Define your buyer roles, then collect their first answers.",
		"先定义客户角色，再为每个角色采集回答。",
	],
	"audience.samples": ["Answers", "回答数"],
	"audience.previous": ["Previous period", "上期"],
	"audience.actions": ["Actions", "操作"],
	"audience.answers": ["View answers", "查看回答"],
	"audience.notSampled": ["Not sampled", "尚未采样"],
	"audience.regions.unassigned": ["Answers without verified region data", "未记录经验证地区的回答"],
	"audience.personas.unassigned": ["Answers without a persona", "未指定客户角色的回答"],
	"audience.trend": ["Trend", "趋势"],
	"audience.trendEmptyTitle": ["No answers in this period", "当前范围内没有回答"],
	"audience.trendEmpty": [
		"Collect samples, or widen the period and the engines above.",
		"可以先采样，或把上方的时间范围和引擎放宽。",
	],
	"audience.sample": ["Sample this persona", "采样此角色"],
	"audience.cost": [
		"Uses your enabled platforms and charges for actual model usage.",
		"使用已启用的平台，按模型实际用量计费。",
	],
	"audience.skipped": ["Sampling did not start — view task", "采样未开始，查看任务"],
	"audience.task": ["View sampling task", "查看采样任务"],
	"audience.saving": ["Submitting…", "正在提交…"],
	"audience.error": ["Unable to complete the request. Please retry.", "请求未完成，请重试。"],
	"audience.add.region": ["Add country", "添加国家"],
	"audience.add.persona": ["Add persona", "添加客户角色"],
	"audience.edit": ["Edit", "编辑"],
	"audience.archived": ["Archived", "已归档"],
	"audience.archive": ["Archive this audience (keeps past results)", "归档此受众（保留历史结果）"],
	"audience.scope": ["Prompt scope", "提示词范围"],
	"audience.allPrompts": ["All enabled prompts", "所有已启用提示词"],
	"audience.topics": ["Topics", "主题"],
	"audience.prompts": ["Prompts", "提示词"],
	"audience.view": ["View performance", "查看分析"],
	"audience.noResults": ["No matches", "没有匹配项"],
	"audience.name": ["Name", "名称"],
	"audience.country": ["Country or territory", "国家或地区"],
	"audience.promptLanguage": ["Prompt language", "提示词语言"],
	"audience.selectTopics": ["Select topics", "选择主题"],
	"audience.selectPrompts": ["Select prompts", "选择提示词"],
	"audience.assignmentHelp": [
		"Includes prompts in selected topics or explicitly selected prompts. Leave both empty to use all enabled prompts. Edits apply to future samples.",
		"包含所选主题下的提示词，以及单独选中的提示词。两项均留空时使用全部已启用提示词。修改只影响后续采样。",
	],
	"audience.field.job_title": ["Job title", "职位"],
	"audience.field.seniority": ["Seniority", "职级"],
	"audience.field.industry": ["Industry", "行业"],
	"audience.field.company_size": ["Company size", "公司规模"],
	"audience.field.age_range": ["Age range", "年龄段"],
	"audience.field.motivations": ["Motivations", "购买动机"],
	"audience.field.pain_points": ["Pain points", "痛点"],
	"regions.title": ["Regions", "区域"],
	"regions.body": [
		"Regional sampling is not implemented yet. Your plan is not blocking access. Content language does not identify the location from which an answer was collected.",
		"区域采样尚未实现，并非会员权限不足。内容语言不代表答案实际采集的地理位置。",
	],
	"regions.axis": ["Region", "区域"],
	"personas.title": ["Personas", "客户角色"],
	"personas.body": [
		"Persona comparisons are not implemented yet. Your plan is not blocking access. Existing answers were not collected under controlled buyer roles.",
		"客户角色对比尚未实现，并非会员权限不足。现有答案没有按照受控的买家身份分别采集。",
	],
	"coverage.next": [
		"You can add market or buyer context to your prompts now. Those results describe the questions asked; they should not be read as a controlled regional or persona comparison.",
		"目前可以在提示词中补充目标市场或买家背景。这些结果对应具体问题，不能当作受控的区域或客户角色对比。",
	],
	"personas.axis": ["Persona", "客户角色"],

	// -- the filing a customer does about what the tabs read
	"insightSettings.rules": ["Citation categories", "引用分类"],
	"insightSettings.rulesHint": [
		"A cited URL is filed automatically. The automatic answer is wrong about exactly the domains you care about — your docs on another apex, a partner, a rival's blog on a shared platform — and one misfiled row is enough to stop trusting the other forty.",
		"被引用的 URL 会自动归类。自动归类恰恰会在你最在意的域名上出错——另一个主域下的文档站、合作伙伴、竞品发在公共平台上的博客——只要有一行归错，其余四十行也就不可信了。",
	],
	"insightSettings.subpaths": ["Include subpaths", "包含子路径"],
	"insightSettings.noRules": ["Nothing has been refiled yet.", "还没有覆盖任何自动归类。"],
	"insightSettings.tags": ["Citation tags", "引用标签"],
	"insightSettings.tagsHint": [
		"A category says what kind of site this is; a tag says which of your campaigns it belongs to. One URL has exactly one of the first and any number of the second.",
		"分类说明这是什么类型的站点，标签说明它属于你的哪一个动作。一个 URL 只有一个分类，但可以有任意多个标签。",
	],
	"insightSettings.tagsPlaceholder": ["launch, q3", "launch, q3"],
	"insightSettings.noTags": ["No tags yet.", "还没有标签。"],
	"insightSettings.names": ["Name matching", "名称匹配"],
	"insightSettings.namesHint": [
		"The names an answer can use for this brand, and how many answers in the last 90 days contained each one. An alias that never matches is one somebody guessed.",
		"回答中可能用来指代这个品牌的名称，以及最近 90 天里包含每个名称的回答数。一个从未命中的别名，多半是当初猜的。",
	],
	"insightSettings.name": ["Name", "名称"],
	"insightSettings.mentions": ["Answers", "回答数"],
	"insightSettings.primary": ["Primary", "主名称"],
	"insightSettings.neverSeen": ["Never seen", "从未出现"],
	"insightSettings.unmapped": ["Unmapped names", "未映射的名称"],
	"insightSettings.unmappedHint": [
		"An answer that both linked to a domain we cannot resolve and wrote that domain's own name as a word. Every row here is visibility this brand may not be getting credit for.",
		"某条回答既链接了我们无法识别的域名，又在正文里写出了该域名对应的名称。这里的每一行，都可能是没有被计入的可见度。",
	],
	"insightSettings.noUnmapped": ["Nothing unmapped.", "没有未映射的名称。"],
	"insightSettings.lastSeen": ["Last seen", "最近出现"],
	"insightSettings.classify": ["Classify name", "归类名称"],
	"insightSettings.mapIt": ["It's us", "是我们"],
	"insightSettings.trackIt": ["Track as rival", "作为竞品跟踪"],
	"insightSettings.dismissIt": ["Neither", "都不是"],
	"insightSettings.resolution": ["Entity resolution", "实体解析"],
	"insightSettings.resolutionHint": [
		"What the reading pass should treat as a brand in an answer, and anything it should know that the facts do not say.",
		"解读环节应该把回答里的什么东西当作一个品牌，以及事实表里没写、但它应该知道的内容。",
	],
	"insightSettings.kind.companies": ["Companies", "公司"],
	"insightSettings.kind.products": ["Products", "产品"],
	"insightSettings.kind.projects": ["Open-source projects", "开源项目"],
	"insightSettings.kind.models": ["Models", "模型"],
	"insightSettings.notesPlaceholder": [
		"Anything the reader should know — an old name, a product we do not sell any more.",
		"解读时需要知道的信息——比如曾用名，或者已经下架的产品。",
	],
	"insightSettings.competitors": ["Tracked competitors", "跟踪的竞品"],
	"insightSettings.competitorsHint": [
		"Rivals are derived from who is actually cited instead of you, which is usually a surprise and always the more useful answer. These two lists are the corrections: one adds a rival the answers have never put beside you, the other drops a domain nobody competes with. Tracking wins where they collide.",
		"竞品是根据「实际被引用、而不是你」推导出来的，结论常出人意料，但更有用。这两个列表是对它的修正：一个补上回答从未与你并列过的竞品，另一个去掉根本不构成竞争的域名。同时出现在两边时，以跟踪为准。",
	],
	"insightSettings.track": ["Track", "跟踪"],
	"insightSettings.tracked": ["Tracked", "已跟踪"],
	"insightSettings.noTracked": ["Only the rivals the answers name.", "只有回答中出现过的竞品。"],
	"insightSettings.excluded": ["Excluded", "已排除"],
	"insightSettings.noExcluded": ["Nothing excluded.", "没有排除任何域名。"],

	"common.add": ["Add", "添加"],
	// -- brand voice
	//
	// Pairs, never a bare ban: the screen has to say why, because "banned words"
	// is what everyone expects and deleting a word out of a sentence leaves prose
	// nobody wrote.
	"voice.title": ["How this brand sounds", "这个品牌的写法"],
	"voice.hint": [
		"The facts decide what a page may say. This decides how it reads.",
		"事实决定页面能说什么，这里决定它读起来是什么样。",
	],
	"voice.tonePlaceholder": ["Plain and technical. Short sentences.", "平实、偏技术。句子短。"],
	"voice.toneHint": [
		"One line, carried into every brief for this brand. Long enough to be a paragraph and it becomes the loudest instruction in the prompt.",
		"一句话，会进入这个品牌的每一份写作依据。写成一段，它就会盖过提示词里其他所有规则。",
	],
	"voice.terms": ["Your words", "你的用词"],
	"voice.termsHint": [
		"A word to write and the word to never write instead of it. Both halves are needed: a word with no replacement cannot be taken out of a sentence without leaving prose nobody wrote.",
		"要写的词，以及绝不要写的那个词。两半都得填：只说禁用哪个词、不说该写什么，从句子里抠掉它剩下的就是没人写过的句子。",
	],
	"voice.write": ["Write", "写作"],
	"voice.never": ["Never", "不要写"],
	"voice.empty": [
		"No custom terms yet. Add a pair to specify the wording to use and what it replaces.",
		"还没有自定义用词。添加一组，指定推荐写法及需要替换的词。",
	],
	"voice.completePair": [
		"Complete both fields in each pair, or remove the empty pair before saving.",
		"请补全每组的两项内容，或删除空白组后保存。",
	],
	"voice.add": ["Add a pair", "加一组"],
	"voice.house": ["Already taken out of every page", "每个页面都已经去掉的"],
	"voice.houseHint": [
		"The tells that survive a polish pass. Applied to every brand; you do not have to restate them.",
		"润色之后仍然会留下的 AI 腔。对所有品牌生效，你不用再写一遍。",
	],

	// -- embeddable fragment
	"embed.title": ["Use this on your own site", "放到你自己的站上"],
	"embed.hint": [
		"The same page as a fragment to paste. It cites this page in the markup and in a line of text, because the same paragraphs on two domains with nothing linking them makes both look like the copy.",
		"同一个页面，做成一段可以直接贴的代码。它在结构化数据和正文里都标注了来源——同样的段落出现在两个域名上又没有互相指向，两边都会被当成抄的那一份。",
	],
	"embed.show": ["Show the fragment", "生成片段"],
	"embed.html": ["HTML", "HTML"],
	"embed.jsonld": ["FAQ structured data", "FAQ 结构化数据"],
	"embed.noFaq": [
		"This page has no FAQ block, so there is no structured data to paste.",
		"这个页面没有 FAQ 区块，没有可贴的结构化数据。",
	],
	"embed.copy": ["Copy", "复制"],
	"embed.copied": ["Copied", "已复制"],
	"embed.copyFailed": [
		"Could not reach the clipboard. Select the text instead.",
		"无法访问剪贴板，请直接选中文本复制。",
	],
	"embed.draftWarning": [
		"This page is not published yet, so the source link in the fragment does not answer.",
		"这个页面还没发布，片段里的来源链接目前打不开。",
	],

	"filter.removeView": ["Remove saved view", "移除已保存视图"],
	"common.removeConfirm": [
		"Remove this item? It will be removed after you confirm.",
		"确定删除此项吗？确认后将移除此项内容。",
	],
	"common.remove": ["Remove", "移除"],
	"common.close": ["Close", "关闭"],
	"common.more": ["More actions", "更多操作"],
	"common.save": ["Save", "保存"],
	"common.saved": ["Saved", "已保存"],
	"common.created": ["Added", "添加时间"],
	"common.yes": ["Yes", "是"],
	"common.no": ["No", "否"],

	"common.copy": ["Copy", "复制"],
	"common.copied": ["Copied", "已复制"],
	"common.cancel": ["Cancel", "取消"],

	// -- column headers shared across tables
	"table.domain": ["Domain", "域名"],
	"table.page": ["Page", "页面"],
	"table.moreActions": ["More actions", "更多操作"],
	"table.title": ["Title", "标题"],
	"table.status": ["Status", "状态"],
	"table.updated": ["Updated", "更新时间"],
	"table.engine": ["Engine", "引擎"],
	"table.date": ["Date", "日期"],
	"table.time": ["Time", "时间"],
	"table.more": ["Show more", "显示更多"],
	"table.reason": ["Reason", "事由"],
	"table.change": ["Change", "变动"],
	"table.search": ["Search", "搜索"],
	"table.export": ["CSV", "CSV"],
	"table.showing": ["{shown} of {total}", "{shown} / {total}"],
	"table.rows": ["{total} rows", "{total} 行"],
	"table.clear": ["Clear the search", "清空搜索"],
	"table.noMatch": ["Nothing matches that search.", "没有匹配的结果。"],
	"settings.datapoints": ["Facts on your listing", "条目上的事实数据"],
	"settings.datapointsHint": [
		"Every fact needs a source we can link to. Facts without one are not published.",
		"每条数据都要有可以链接的来源。没有来源的数据不会发布。",
	],
	"settings.value": ["Value", "值"],
	"settings.valueNumber": ["Number", "数值"],
	"settings.valueList": ["Comma separated", "用逗号分隔"],
	"settings.unit": ["Unit", "单位"],
	"settings.source": ["Source URL", "来源链接"],
	"billing.usage": ["Model usage", "模型用量"],
	"billing.usageEmpty": ["Nothing used yet", "还没有用量"],
	"billing.usageEmptyBody": [
		"Model calls appear here as the loop makes them, one row per model.",
		"回路每做一次模型调用，这里就会出现一行，按模型分开统计。",
	],
	"billing.model": ["Model", "模型"],
	"billing.calls": ["Calls", "调用次数"],
	"billing.tokens": ["Tokens", "Token 数"],
	"billing.spend": ["Spend", "费用"],
	"billing.usageTotal": ["Last {days} days", "最近 {days} 天"],

	// -- signing in
	// The same two lines as the directory's hero. One proposition, said once.
	// The demonstrations the sign-in panel plays. The figures are illustrative and
	// the panel says so; what they illustrate — the denominator, the fact table,
	// the pair of domains — is the mechanism, and that part is exact.
	"auth.demo.sample": ["Example", "示例"],
	"auth.demo.ask": ["Which CRM is best for a 10-person sales team?", "十个人的销售团队，用哪个 CRM 好？"],
	"auth.demo.absent": ["not named", "未被提及"],
	"auth.demo.visibility": ["visibility, last 30 days", "可见度，近 30 天"],
	"auth.demo.you": ["Your brand", "你的品牌"],
	"auth.demo.rivalA": ["Competitor A", "竞品 A"],
	"auth.demo.rest": ["Everyone else", "其余品牌"],
	"auth.demo.line1": ["Deploys on-premise.", "支持私有化部署。"],
	"auth.demo.source1": ["docs", "文档"],
	"auth.demo.line2": ["SOC 2 Type II.", "通过 SOC 2 Type II。"],
	"auth.demo.source2": ["audit report", "审计报告"],
	"auth.demo.line4": ["The fastest tool there is.", "市面上最快的工具。"],
	"auth.demo.cut": ["no source — cut", "无出处，已删"],
	"auth.demo.listing": ["Your listing", "你的品牌页"],
	"auth.demo.yourSite": ["your site", "你的官网"],
	"auth.demo.embed": ["The same blocks", "同样的内容块"],
	"auth.demo.sameBlocks": ["HTML + FAQPage", "HTML + FAQPage"],
	"auth.demo.cites": ["cite each other", "互相引用"],
	"auth.demo.citations": ["citations, six weeks on", "六周后新增引用"],
	"auth.engines": ["Measured on 11 assistants, in the US and in China.", "覆盖 11 个助手，中美两边都测。"],
	"login.headline": ["Get your brand recommended by AI", "让 AI 推荐你的品牌"],
	"login.pitch": ["Let agents optimise your AI visibility", "让智能体替你优化 AI 可见度"],

	// -- the deck in the panel beside the form
	//
	// The loop in four cards. The two that carry numbers carry `stage.sample`
	// with them: nothing here was measured, and a demo that reads as a customer's
	// result is a claim we have not earned.
	"stage.detect.name": ["Detect", "监测"],
	"stage.detect.title": ["We ask the engines what your buyers ask", "我们替你的买家向引擎发问"],
	"stage.detect.question": ["Which tools help a small team get cited by AI?", "小团队想被 AI 引用，该用什么工具？"],
	"stage.detect.cite": ["Cited · ondit.ai", "已引用 · ondit.ai"],
	"stage.analyse.name": ["Analyse", "分析"],
	"stage.analyse.title": ["Share of voice, engine by engine", "逐个引擎看声量占比"],
	"stage.publish.name": ["Publish", "发布"],
	"stage.publish.title": ["Every line checked against the facts", "每一句都对照你的事实表"],
	"stage.publish.pricing": ["Pricing", "定价"],
	"stage.publish.pricingValue": ["$19 / month", "19 美元 / 月"],
	"stage.publish.regions": ["Regions", "地区"],
	"stage.publish.regionsValue": ["EU · US", "欧盟 · 美国"],
	"stage.publish.sources": ["Sources", "来源"],
	"stage.publish.sourcesValue": ["3 cited", "3 条"],
	"stage.reflow.name": ["Reflow", "回流"],
	"stage.reflow.title": ["Citations coming back, week by week", "引用逐周回流"],
	"stage.reflow.label": ["citations this week", "本周引用"],
	"stage.sample": ["Sample figures.", "示例数据。"],
	"login.signIn": ["Sign in", "登录"],
	"login.signUp": ["Create an account", "注册"],
	"login.email": ["Email", "邮箱"],
	"login.password": ["Password", "密码"],
	"login.passwordHint": ["At least 8 characters.", "至少 8 个字符。"],
	"login.continue": ["Continue", "继续"],
	"login.createAccount": ["Create account", "创建账号"],
	// -- buying credits
	"topup.title": ["Top up credits", "积分充值"],
	"topup.body": [
		"Credits you buy never expire and are spent after the month's allowance.",
		"充值的积分永不失效，并在当月赠送积分用完后才开始扣。",
	],
	"topup.short": ["Not enough credits for that. Buy a pack and try again.", "积分不够完成这个操作。充值后再试一次。"],
	"topup.rate": ["$1 buys {credits} credits.", "1 美元 = {credits} 积分。"],
	"topup.balance": ["{n} credits left", "剩余 {n} 积分"],
	"topup.expiring": ["{n} expire {date}", "其中 {n} 将于 {date} 到期"],
	"topup.expired": ["Your plan has lapsed. Renew it to buy credits.", "你的订阅已过期，续订后才能充值。"],
	"topup.needsPlan": [
		"Credits are for accounts on a plan. Pick one first.",
		"积分充值面向已订阅的账户，请先选择套餐。",
	],
	"topup.credits": ["credits", "积分"],
	"topup.neverExpire": ["Bought credits never expire.", "充值积分永不失效。"],
	"topup.later": ["Not now", "稍后再说"],
	"topup.open": ["Top up", "充值"],
	"topup.paid": ["Paid. {credits} credits land in a moment.", "支付成功，{credits} 积分马上到账。"],
	"topup.checkoutUnavailable": [
		"The checkout could not be opened. Try again in a moment.",
		"无法打开支付页面，请稍后再试。",
	],

	// -- the plan lapsing
	"banner.expired": [
		"Your plan has lapsed. Everything is still here to read; renew to keep working on it.",
		"你的订阅已过期。所有内容仍可查看；续订后才能继续操作。",
	],
	"banner.renew": ["Renew", "续订"],
	"banner.renewAsk": ["Ask an owner or admin of the account to renew it.", "请账户的所有者或管理员续订。"],

	// -- the balance running out
	"banner.lowCredits": [
		"Only {n} credits left. Top up before the next reading or generation is refused.",
		"仅剩 {n} 积分。请及时充值，否则下一次测量或生成会被拒绝。",
	],
	"banner.topUpAsk": ["Ask an owner or admin of the account to top up.", "请账户的所有者或管理员充值。"],
	"billing.expiring": ["{n} expire {date}", "{n} 积分于 {date} 到期"],
	"billing.permanent": ["{n} never expire", "{n} 积分永不失效"],
	"billing.expiredTitle": ["Your plan has lapsed", "订阅已过期"],
	"billing.expiredBody": [
		"Measurement stopped when the period ended and the plan's credits went with it. Pick a plan below to start again — your brands, questions and pages are all still here.",
		"周期结束时测量已停止，套餐积分也随之到期。在下面重新选择套餐即可继续——你的品牌、问题和页面都还在。",
	],
	// Cancelling stops the renewal; it does not take back the period already
	// paid for, and it is offered even when the plan has lapsed.
	"billing.cancelShort": ["Cancel", "取消订阅"],
	"billing.cancel": ["Cancel the subscription", "取消订阅"],
	"billing.cancelKeep": ["Keep it", "继续订阅"],
	"billing.cancelHint": ["Do not want to renew?", "不想续订了？"],
	"billing.cancelTitle": ["Cancel this subscription?", "要取消这个订阅吗？"],
	"billing.cancelBody": [
		"The charge stops. You keep the plan, its brands and its credits until {date}, and everything already published stays published.",
		"扣款将停止。在 {date} 之前，套餐、品牌和积分都还在，已经发布的页面也会一直保留。",
	],
	"billing.trialEnds": [
		"The trial month runs until {date} and does not renew. Move up to a plan to keep measuring.",
		"体验月可用到 {date}，不会自动续费。升级套餐才能继续测量。",
	],
	"billing.cancelScheduled": [
		"This subscription ends on {date} and will not renew.",
		"订阅将于 {date} 结束，不再续订。",
	],
	"billing.cancelDone": ["Cancelled. The plan runs until {date}.", "已取消，套餐可用到 {date}。"],
	"plan.downgrade": ["Move down", "降级"],
	"plan.scheduled": ["Scheduled", "已安排"],
	"errors.gated.plan_overflow": ["Your account is bigger than that plan", "你的账户超出了该套餐的限制"],
	"downgrade.title": ["Move down to {plan}", "降级到 {plan}"],
	"downgrade.when": [
		"Nothing changes today. You keep {current} until {date}; from then on you are billed for {plan}.",
		"今天不会有任何变化。在 {date} 之前你仍享有 {current}；之后按 {plan} 计费。",
	],
	"downgrade.fits": ["Everything in your account fits the smaller plan.", "你账户里的内容都在小套餐的限制内。"],
	"downgrade.overTitle": ["Trim these first", "请先处理以下超出项"],
	"downgrade.overBody": [
		"The smaller plan cannot hold everything in your account. Nothing is removed for you — decide what goes, then come back.",
		"小套餐装不下你账户里的全部内容。我们不会替你删除任何东西——请自行决定保留什么，然后再回来。",
	],
	"downgrade.over.brands": ["{have} brands, the plan covers {max}", "{have} 个品牌，套餐只支持 {max} 个"],
	"downgrade.over.seats": ["{have} seats, the plan covers {max}", "{have} 个席位，套餐只支持 {max} 个"],
	"downgrade.over.questions": [
		"{name} tracks {have} questions, the plan covers {max}",
		"{name} 追踪了 {have} 个问题，套餐只支持 {max} 个",
	],
	"downgrade.over.engines": [
		"{name} is measured on {have} engines, the plan covers {max}",
		"{name} 在 {have} 个引擎上测量，套餐只支持 {max} 个",
	],
	"downgrade.fix.brands": ["Delete a brand from its settings", "在品牌设置中删除品牌"],
	"downgrade.fix.seats": ["Remove members from the account", "从账户中移除成员"],
	"downgrade.fix.questions": ["Open its questions", "打开它的问题集"],
	"downgrade.fix.engines": ["Open its settings", "打开它的设置"],
	"downgrade.confirm": ["Schedule the move", "确认降级"],
	"downgrade.cancel": ["Keep my plan", "保留当前套餐"],
	"downgrade.scheduled": ["Moving to {plan} on {date}.", "将于 {date} 降级到 {plan}。"],
	"downgrade.undo": ["Cancel the move", "取消降级"],
	"downgrade.done": ["Scheduled. Nothing changes until {date}.", "已安排。{date} 之前不会有变化。"],
	"downgrade.undone": ["The move is cancelled; your plan stays as it is.", "降级已取消，套餐保持不变。"],
	"plan.enginesNarrowed": [
		"You chose {n} engines. A plan that covers fewer measures the first ones on your list, in the order you picked them.",
		"你选了 {n} 个引擎。覆盖不到这么多的套餐，会按你选择的顺序测量靠前的那几个。",
	],
	"plan.enginesFewer": ["Covers {n} of the {m} engines you chose", "只覆盖你所选 {m} 个引擎中的 {n} 个"],

	// -- engines
	"market.us": ["Global", "全球"],
	"market.cn": ["China", "中国"],
	// The Chinese assistants carry their own name first in Chinese, which is
	// what their users call them, and only the Latin one in English.
	"engine.chatgpt": ["ChatGPT", "ChatGPT"],
	"engine.perplexity": ["Perplexity", "Perplexity"],
	"engine.gemini": ["Gemini", "Gemini"],
	"engine.claude": ["Claude", "Claude"],
	"engine.grok": ["Grok", "Grok"],
	"engine.deepseek": ["DeepSeek", "DeepSeek"],
	"engine.qwen": ["Qwen", "通义千问 Qwen"],
	"engine.kimi": ["Kimi", "Kimi"],
	"engine.doubao": ["Doubao", "豆包 Doubao"],
	"engine.glm": ["GLM", "智谱清言 GLM"],
	"engine.hunyuan": ["Hunyuan", "腾讯元宝 Hunyuan"],
	"settings.engines": ["Answer engines", "回答引擎"],
	"settings.enginesHint": [
		"Which assistants this brand is measured on. Your plan covers {max}; choose more by moving up a plan.",
		"这个品牌在哪些 AI 助手上被测量。当前套餐可选 {max} 个；升级套餐可选更多。",
	],
	"settings.enginesDefault": ["No choice yet: the plan's default set is used.", "还没有选择：使用套餐的默认引擎。"],
	"errors.gated.engine_quota": ["More engines than your plan covers", "所选引擎超过了套餐上限"],
	"errors.gated.subscription_expired": ["Your plan has lapsed. Renew to continue.", "订阅已过期，续订后才能继续。"],
	"errors.notFound.credit_pack": ["That credit pack is not for sale", "该积分包暂不出售"],
	"errors.gated.question_quota": [
		"This brand already tracks every question your plan covers. Turn one off, or move up a plan.",
		"这个品牌追踪的问题数已达套餐上限。可以停用一个，或升级套餐。",
	],
	"errors.gated.trial_used": [
		"This account has already had its trial month. Pick a plan to keep going.",
		"这个账户已经用过体验月了。选择一个套餐即可继续。",
	],
	"errors.gated.agent_plan": ["The Agent is part of the Pro plan.", "Agent 是专业版的功能。"],
	"errors.gated.advanced_plan": ["This reading is part of the Pro plan.", "这个读数是专业版的功能。"],
	"errors.gated.seat_quota": [
		"Every seat on your plan is taken. Withdraw an invitation or move up a plan.",
		"套餐的席位已经用完。可以撤回一封邀请，或升级套餐。",
	],
	"errors.forbidden.brand_role": ["You can read this brand but not change it", "你可以查看这个品牌，但不能修改"],
	"errors.forbidden.own_role": ["You cannot change your own role", "你不能修改自己的角色"],
	"errors.forbidden.invite_email": ["That invitation was sent to a different address", "这封邀请是发给另一个邮箱的"],
	"errors.notFound.member": ["That person is not in this account", "这个人不在这个账户里"],
	"errors.notFound.invite": ["That invitation is gone", "这封邀请已经不在了"],
	"errors.conflict.last_owner": [
		"An account needs an owner. Make somebody else the owner first.",
		"账户必须有所有者，请先把所有者交给别人。",
	],
	"errors.conflict.already_a_member": ["They are already in this account", "他们已经在这个账户里了"],
	"errors.forbidden.role": ["Only an owner or admin can do that", "只有所有者或管理员可以执行此操作"],
	"errors.forbidden.org_role": ["Only an owner or admin can change the account", "只有所有者或管理员可以修改账户"],
	"errors.forbidden.admin": ["Only an operator can do that", "只有运营人员可以执行此操作"],

	// -- deleting a brand
	"settings.danger": ["Danger zone", "危险操作"],
	"settings.delete": ["Delete this brand", "删除该品牌"],
	"settings.deleteHint": [
		"Removes the brand from your account with everything measured and written for it. This cannot be undone.",
		"将该品牌及其所有测量数据和生成页面从你的账户中移除。此操作无法撤销。",
	],
	"settings.deleteTitle": ["Delete {name}?", "删除 {name}？"],
	"settings.deleteBody": [
		"Its questions, samples, gaps, reports and pages go with it, and nothing can bring them back. A listing you claimed from the directory stays in the directory, unowned.",
		"它的问题、采样、缺口、报告和页面都会一起删除，且无法恢复。从目录认领的条目会保留在目录中，不再归属于你。",
	],
	"settings.deleteConfirm": ["Delete permanently", "永久删除"],
	"settings.deleteCancel": ["Keep it", "保留"],
	"settings.deleted": ["Deleted", "已删除"],

	// -- the engines screen of the wizard
	"onboard.panel.engines": ["Which assistants should we ask about you?", "我们该去问哪些 AI 助手？"],
	"onboard.engines.title": ["Pick your answer engines", "选择回答引擎"],
	"onboard.engines.body": [
		"Every engine you pick is asked every one of your questions, every week. Pick the ones your customers actually use.",
		"你选的每个引擎每周都会被问到你的全部问题。选你的客户真正在用的那些。",
	],
	"onboard.engines.limits": [
		"Starter covers 1 engine, Standard 3, Pro 4, and Enterprise every one — the number you pick here decides which plans fit.",
		"入门版可选 1 个，标准版 3 个，专业版 4 个，企业版不限——这里选的数量决定了哪些套餐适用。",
	],
	"onboard.engines.chosen": ["{n} chosen", "已选 {n} 个"],
	"onboard.engines.plan": [
		"Your {plan} plan covers {max} engines — {n} chosen.",
		"当前 {plan} 套餐可选 {max} 个引擎，已选 {n} 个。",
	],
	"onboard.topics.toEngines": ["Choose engines", "去选引擎"],
	"login.working": ["Working…", "处理中…"],
	"login.or": ["or", "或"],
	"login.withGoogle": ["Continue with Google", "使用 Google 继续"],
	"login.withGithub": ["Continue with GitHub", "使用 GitHub 继续"],
	"login.haveAccount": ["Already have an account?", "已经有账号了？"],
	"login.noAccount": ["No account yet?", "还没有账号？"],
	"login.forgot": ["Forgot your password?", "忘记密码？"],
	"login.codeSent": ["We sent a six-digit code to your address.", "我们向你的邮箱发送了一个 6 位验证码。"],
	"login.unverified": [
		"This address has not been confirmed yet. We have sent a new code.",
		"这个邮箱还没有确认。我们已经重新发送了验证码。",
	],
	"login.code": ["Code", "验证码"],
	"login.codeDigit": ["Digit {n}", "第 {n} 位"],
	"login.codeHint": ["Six digits, in the mail we just sent.", "刚发的邮件里的 6 位数字。"],
	"login.codeExpired": ["That code has expired. Send a new one.", "验证码已失效，请重新发送。"],
	"login.resend": ["Send a new code", "重新发送验证码"],
	"errors.forbidden.code_expired": ["That code has expired", "验证码已失效"],

	// -- onboarding
	"onboard.continue": ["Continue", "继续"],
	"onboard.back": ["Back", "返回"],
	"onboard.cancel": ["Cancel", "取消"],
	"onboard.progress": ["Step {n} of {of}", "第 {n} 步，共 {of} 步"],
	"onboard.panel.company": ["A minute of questions, then your workspace.", "回答几个问题，然后进入你的工作区。"],
	"onboard.panel.website": ["We read your homepage so you do not have to type it.", "我们读你的首页，你不用自己打字。"],
	"onboard.panel.brand": ["Correct anything we got wrong.", "哪里不对就改哪里。"],
	"onboard.panel.region": ["Where are the people who should be recommended you?", "该被推荐给谁？他们在哪儿？"],
	"onboard.panel.topics": ["Choose the topics you want to track.", "选择你想监测的主题。"],
	"onboard.panel.plan": ["Measurement starts when a plan does.", "测量从订阅开始。"],

	"onboard.company.title": ["Tell us about your company", "介绍一下你的公司"],
	"onboard.company.body": ["This helps us set the right defaults.", "这帮助我们设置合适的默认值。"],
	"onboard.company.size": ["Company size", "公司规模"],
	"onboard.company.employees": ["{n} employees", "{n} 人"],
	"onboard.company.agency": ["I am an agency working for clients", "我是代理机构，替客户运营"],

	"onboard.website.title": ["Do you have a website?", "你有自己的网站吗？"],
	"onboard.website.body": [
		"Paste the address and we will read it. No site yet? Describe the brand instead.",
		"贴上地址，我们来读。还没有网站？那就直接描述品牌。",
	],
	"onboard.website.label": ["Website", "网站"],
	"onboard.website.none": ["I do not have a website yet", "我还没有网站"],
	"onboard.website.analyzing": ["Reading {domain}…", "正在读取 {domain}…"],
	"onboard.website.analyzingShort": ["Reading…", "读取中…"],
	"onboard.website.claimedElsewhere": [
		"{name} is already listed and owned by another account.",
		"{name} 已经被另一个账号认领了。",
	],

	"onboard.website.claimedHelp": [
		"If that is your team, ask its administrator to invite you instead.",
		"如果那是你的团队，请让管理员邀请你加入。",
	],

	"onboard.website.addAnyway": ["Add it anyway", "仍然添加"],

	"onboard.brand.title": ["Describe your brand", "描述你的品牌"],
	"onboard.brand.body": [
		"What it is, who it is for. This is what the questions are written from.",
		"它是什么、给谁用。问题就是从这段话里写出来的。",
	],
	"onboard.brand.confirmTitle": ["Is this right?", "是这样吗？"],
	"onboard.brand.confirmBody": [
		"Read from your homepage. Fix anything that is off.",
		"从你的首页读出来的，不准的地方改一下。",
	],
	"onboard.brand.name": ["Brand name", "品牌名称"],
	"onboard.brand.description": ["What it does", "它是做什么的"],
	"onboard.brand.descriptionPlaceholder": [
		"A vector database for teams building RAG…",
		"一个面向 RAG 团队的向量数据库…",
	],
	"onboard.brand.descriptionHint": [
		"The more specific, the better the questions. What do you offer, and to whom?",
		"越具体，问题越准。你提供什么，给谁？",
	],
	"onboard.brand.sources": ["Public pages about it (optional)", "介绍它的公开页面（可选）"],
	"onboard.brand.sourcesHint": [
		"One address per line, up to five: a marketplace store, a business registry entry, a certificate lookup. Facts are read from these pages and cite them.",
		"每行一个地址，最多五个：电商店铺、企业信用信息公示、认证证书查询等。事实会从这些页面读取，并以它们为来源。",
	],
	"onboard.brand.sourcesPlaceholder": ["https://detail.1688.com/offer/…", "https://detail.1688.com/offer/…"],
	"onboard.brand.categories": ["Categories", "类目"],
	"onboard.brand.categoriesHint": [
		"What you sell. Up to five, as broad or as narrow as fits.",
		"你卖什么。最多 5 个，粗细按实际来。",
	],

	"onboard.region.title": ["Where is your audience?", "你的受众在哪里？"],
	"onboard.region.body": [
		"Questions are asked the way a buyer there would ask them.",
		"问题会按那里的买家的问法来问。",
	],
	"onboard.region.region": ["Region", "地区"],
	"onboard.region.language": ["Language", "语言"],
	"onboard.region.languageHint": [
		"The language the questions are written in, and the language of this panel.",
		"问题使用的语言，也是这个面板的语言。",
	],

	"onboard.topics.title": ["Which topics should we ask about?", "我们该问哪些主题？"],
	"onboard.topics.body": [
		"Pick up to {max} topics. Specific questions will be generated after payment.",
		"最多选择 {max} 个主题，具体问题将在支付后自动生成。",
	],
	"onboard.topics.suggesting": ["Thinking of topics…", "正在想主题…"],
	"onboard.topics.custom": ["Add your own", "自己加一个"],
	"onboard.topics.rule": ["{chosen} of {max} chosen", "已选 {chosen} / {max}"],
	"onboard.topics.perTopic": ["Questions per topic", "每个主题问几个问题"],
	"onboard.topics.perTopicHint": [
		"{n} questions in all. Every one of them is put to every engine you track, every week.",
		"一共 {n} 个问题。每个问题每周都会问给你追踪的每个引擎。",
	],

	"onboard.plan.active": [
		"Your plan is {plan}. Measurement starts from the workspace.",
		"当前套餐：{plan}。测量从工作区开始。",
	],
	"onboard.plan.open": ["Open my workspace", "打开我的工作区"],

	"onboard.plan.title": ["Pick a plan", "选择套餐"],
	"onboard.plan.body": [
		"Free is a listing. Paid measures every week and writes the pages.",
		"免费只是一个条目；付费每周测量并写页面。",
	],
	"onboard.plan.free": ["Free", "免费"],
	"onboard.plan.gate": [
		"Asking several answer engines costs money every time, so measurement runs only on a paid plan.",
		"每次向多个回答引擎提问都要花钱，所以测量只在付费方案下运行。",
	],
	"onboard.plan.freeIncludes": ["A listing in the brands site", "在品牌库里有一个条目"],
	"onboard.plan.measured": ["Measured across every engine we track, every week", "每周在我们追踪的每个引擎上测量"],
	"onboard.topics.toPlan": ["Continue to plans", "去选方案"],
	// The same screen, at the end of the shorter order an existing account runs.
	"onboard.topics.toWorkspace": ["Open this brand's workspace", "进入这个品牌的工作区"],
	"errors.gated.subscription_required": ["Measurement needs a paid plan", "测量需要付费方案"],
	"onboard.plan.freeLine": ["Just want the listing?", "只想要一个条目？"],
	"onboard.plan.startFree": ["Continue free, without measurement", "免费继续，不测量"],
	"login.emailFirst": ["Enter your email address first.", "请先填写邮箱地址。"],
	"login.resent": ["Sent. Check your inbox.", "已发送，请查收。"],
	"login.resendFailed": ["We could not send it just now. Try again in a few minutes.", "暂时发不出去，过几分钟再试。"],
	"login.resetSent": [
		"If that address has an account, a reset link is on its way.",
		"如果该地址有账号，重置链接已经发出。",
	],
	"reset.title": ["Set a new password", "设置新密码"],
	"reset.newPassword": ["New password", "新密码"],
	"reset.save": ["Save and sign in", "保存并登录"],
	// Landing on /reset with no token in the URL: a mail client truncated the
	// link, or it was copied without its query.
	"reset.noToken": [
		"This reset link is incomplete. Ask for a new one from the sign-in page.",
		"这个重置链接不完整，请到登录页重新申请一封。",
	],
	"errors.forbidden.email_unverified": ["Confirm your email address before signing in", "登录前请先确认邮箱地址"],

	// -- banners
	"banner.paused": ["Tracking is paused.", "追踪已暂停。"],
	"banner.resume": ["Resume", "恢复"],

	// -- login
	"login.title": ["Sign in", "登录"],
	"login.denied": ["Sign-in was cancelled.", "登录已取消。"],
	// The other ways a provider round trip ends on this page: `?error=<code>`
	// from the API's callback route.
	"login.error.state": [
		"That sign-in took too long or came back to a different browser. Try again.",
		"这次登录超时，或返回到了另一个浏览器。请重试。",
	],
	"login.error.code": ["The provider sent no authorization code. Try again.", "平台没有返回授权码，请重试。"],
	"login.error.provider": ["That sign-in was started with a different provider.", "这次登录是通过另一个平台发起的。"],
	"login.error.no_email": [
		"That account has no verified email address at the provider.",
		"这个账号在该平台没有已验证的邮箱地址。",
	],

	//
	// The loop run by the clock. Two halves on one screen and the words keep
	// them apart: the top half is what it *will* do, the bottom half is what it
	// *did*. Every note the server can attach to a step has a sentence here —
	// a run whose generate stage says "run_budget" to a customer is a run that
	// tells them nothing.

	// Every note a step can carry. The server sends the key; the sentence is
	// written here, in both languages, exactly as it is for every refusal.

	// -- the drill-down drawer
	//
	// Every one of these appears over a screen that already said the number.
	// The words here name the rows, not the figure: "3 of 5" is on the cell,
	// and the drawer is what those five answers were.
	"detail.back": ["Back", "返回"],
	"detail.previous": ["Previous record", "上一条"],
	"detail.next": ["Next record", "下一条"],
	"detail.close": ["Close", "关闭"],
	"detail.empty": ["Nothing to show for this one", "这里没有可看的记录"],
	"detail.cited": ["Cited you", "引用了你"],
	"detail.notCited": ["Did not cite you", "没有引用你"],
	"detail.namedAt": ["Named {n}", "第 {n} 个被提到"],
	"detail.noText": ["The answer was not kept", "没有保存答案正文"],
	"detail.noSources": ["This answer linked to nothing", "这条回答没有引用任何来源"],
	"detail.fullAnswer": ["Read the whole answer", "查看完整答案"],
	"detail.otherAnswers": ["The other answers to this question", "这个问题的其他答案"],
	"detail.sources": ["What it linked to", "引用的来源"],
	"detail.mentions": ["Who it named", "提到了谁"],
	"detail.you": ["You", "你"],
	"detail.webQueries": ["What it searched for", "它搜索了什么"],
	"detail.truncated": [
		"The answer was cut off by its output limit. It is retained as evidence and excluded from metrics.",
		"答案因输出上限被截断，仅保留原始凭证，不计入指标。",
	],
	"detail.emptyResponse": [
		"The model returned no answer text. This response is excluded from metrics.",
		"模型未返回答案正文，此记录不计入指标。",
	],

	"detail.notice": [
		"The gateway refused this one. It is kept as evidence and never counted as an answer.",
		"网关拒绝了这一次请求。这条记录只作为凭证保留，不计入答案。",
	],
	"detail.domain": ["Domain", "域名"],
	"detail.openListing": ["Open their listing", "打开他们的条目"],
	"detail.headToHead": ["Question by question", "逐个问题对比"],
	"detail.theirs": ["Them", "他们"],
	"detail.yours": ["You", "你"],
	"detail.ofAnswers": ["of {n} answers", "共 {n} 条回答"],
	"detail.rivalPages": ["The pages of theirs that get cited", "被引用的他们的页面"],
	"detail.crawlerPaths": ["What it read", "它读了哪些页面"],
	"detail.crawlerBots": ["Who read it", "谁读了这个页面"],

	// -- members
	//
	// Two vocabularies, deliberately: an *account* role says what somebody may
	// do to the account — pay for it, decide who else is in it — and a *brand*
	// role says what they may do to one brand. An agency with six brands has
	// people on two of them, and one word for both would be the wrong word for
	// one of them.
	"account.members": ["Members", "成员"],
	"account.membersHint": [
		"Who is in this account, and which brands each of them holds.",
		"谁在这个账户里，各自负责哪些品牌。",
	],
	// The sentence a disabled control carries, as a native title. Shorter than
	// the banner at the top of the screen, which is where the explanation lives:
	// this one is read with the pointer already on the button.
	"access.viewer": ["Only an editor of this brand can do this", "只有该品牌的编辑可以执行这项操作"],
	"access.admin": ["Only the account's owner or an admin can do this", "只有账户的所有者或管理员可以执行这项操作"],
	"banner.viewer": [
		"You can read {brand} but not change it. Ask an admin of this account for editor access.",
		"你对 {brand} 只有查看权限，不能修改。需要编辑权限请找账户管理员。",
	],
	"brand.workspaces": ["Workspaces", "工作区"],
	"members.title": ["Members", "成员"],
	"members.open": ["Manage", "管理"],
	"members.invite": ["Invite", "邀请"],
	"members.send": ["Send invite", "发送邀请"],
	"members.seatsUsed": ["{used} of {max} seats used", "已用 {used} / {max} 个席位"],
	"members.seatsUnlimited": ["{used} people", "{used} 人"],
	"members.seatsPending": ["including {n} invited", "其中 {n} 个是待接受的邀请"],
	"members.seatsFull": ["Every seat is taken — see plans", "席位已满，去看套餐"],
	"members.active": ["In the account ({n})", "已加入（{n}）"],
	"members.pending": ["Invited ({n})", "待接受（{n}）"],
	"members.invited": ["Invitation sent to {email}", "邀请已发送到 {email}"],
	"members.inviteUnsent": [
		"The seat is held for {email}, but the mail did not go out. Send it again.",
		"已为 {email} 保留席位，但邮件没有发出去，请重新发送。",
	],
	"members.resend": ["Send again", "重新发送"],
	"members.resent": ["Sent again. The earlier link no longer works.", "已重新发送，之前那条链接会失效。"],
	"members.revoke": ["Withdraw", "撤回"],
	"members.revokeTitle": ["Withdraw this invitation?", "撤回这封邀请？"],
	"members.revokeBody": [
		"{email} will no longer be able to join with the link they were sent, and the seat is freed.",
		"{email} 将无法再通过收到的链接加入，席位随之释放。",
	],
	"members.expires": ["expires {date}", "{date} 过期"],
	"members.you": ["you", "你"],
	"members.lastSeen": ["last here {when}", "最近登录：{when}"],
	"members.joined": ["joined {date}", "{date} 加入"],
	"members.everyBrand": ["Every brand, including new ones", "所有品牌，包括之后新增的"],
	"members.noBrands": ["No brand yet", "还没有品牌"],
	"members.access": ["Brands", "品牌"],
	"members.accessLede": [
		"Pick the brands this person works on. An editor changes things; a viewer reads.",
		"选择这个人负责的品牌。编辑可以修改，查看者只能读。",
	],
	"members.adminLede": [
		"An admin holds every brand, including the ones added later, and can change the plan.",
		"管理员拥有所有品牌（含之后新增的），并且可以更改套餐。",
	],
	"members.saveAccess": ["Save", "保存"],
	"members.remove": ["Remove", "移除"],
	"members.removeTitle": ["Remove this person?", "移除这个人？"],
	"members.removeBody": [
		"{who} loses access to every brand in this account, and the seat goes back to the plan.",
		"{who} 将失去这个账户下所有品牌的访问权限，席位会退回套餐。",
	],
	"members.transfer": ["Make owner", "设为所有者"],
	"members.transferTitle": ["Hand the account over?", "把账户交给对方？"],
	"members.transferBody": [
		"{who} becomes the owner of this account and takes over its plan and billing. You stay on as an admin.",
		"{who} 将成为这个账户的所有者，套餐与账单也随之转交。你会保留管理员身份。",
	],
	"members.transferred": ["{who} owns this account now.", "账户已转交给 {who}。"],
	"members.roleLabel": ["Account role", "账户角色"],
	"members.brandRoleLabel": ["Brand role", "品牌角色"],
	"members.role.owner": ["Owner", "所有者"],
	"members.role.admin": ["Admin", "管理员"],
	"members.role.member": ["Member", "成员"],
	"members.brandRole.editor": ["Editor", "编辑"],
	"members.brandRole.viewer": ["Viewer", "查看者"],

	// -- where an invitation link lands
	"invite.noToken": [
		"This invitation link is incomplete. Ask your account administrator for the full link.",
		"邀请链接不完整，请向账户管理员索取完整链接。",
	],
	"invite.title": ["An invitation", "一封邀请"],
	"invite.from": ["You have been invited to {org} on Ondit.", "有人邀请你加入 Ondit 上的 {org}。"],
	"invite.fromWho": ["{who} invited you to {org} on Ondit.", "{who} 邀请你加入 Ondit 上的 {org}。"],
	"invite.as": ["Sent to {email}", "发送给 {email}"],
	"invite.accept": ["Accept", "接受邀请"],
	"invite.signIn": ["Sign in", "登录"],
	"invite.gone": [
		"This invitation is no longer valid. Ask whoever sent it for a new one.",
		"这封邀请已经失效，请让邀请人重新发一次。",
	],
	"invite.wrongAccount": [
		"You are signed in as {signed}, and this invitation was sent to {invited}. Sign out and sign in as that address.",
		"你现在登录的是 {signed}，而这封邀请发给的是 {invited}。请退出后用那个邮箱登录。",
	],

	// -- empty account
	"empty.title": ["No brand yet", "还没有品牌"],
	"empty.body": [
		"Add the brand you want AI answers to recommend, or claim an existing listing on the brands site.",
		"添加你希望被 AI 回答推荐的品牌，或在品牌站认领已有的条目。",
	],
};

function fill(text: string, vars?: Vars): string {
	if (!vars) return text;
	return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

/**
 * A dotted identifier — `members.invite`, `errors.conflict.seat_taken`.
 *
 * Nothing dispatches on this; it only tells a miss in both tables apart from a
 * sentence the Agent has not been translated yet. Every one of the panel's
 * keys matches it, which `messages.test.ts` checks, so a panel key that stops
 * warning would be a key somebody wrote in a shape the panel does not use.
 */
const KEY_SHAPE = /^[a-z][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)+$/;

/**
 * The first segment of every key this table actually uses — `nav`, `errors`,
 * `account`, and the rest.
 *
 * The shape alone is not enough now that the registry catalogues go through
 * the same function. A block called `incident.io`, a Stripe event named
 * `balance.available`, a metric named `aws.elb.request_count` — 117 registry
 * values are shaped exactly like a key, and each one warned that a string was
 * missing when the string was the answer. A warning that cries wolf 117 times
 * is a warning nobody reads the 118th time, which is the one that matters.
 *
 * Derived rather than listed, so a namespace added to the table is covered by
 * having been added. The trade is that the very first key of a brand-new
 * namespace does not warn — the same trade the shape test already made.
 */
const NAMESPACES: ReadonlySet<string> = new Set(Object.keys(T).map((key) => key.slice(0, key.indexOf("."))));

/**
 * The keyed table only, and `undefined` rather than the key when there is no
 * entry — for a caller that has a fallback of its own and would otherwise have
 * to compare the answer against what it asked for, warning in the console on
 * every miss along the way.
 */
export function lookup(locale: Locale, key: string, vars?: Vars): string | undefined {
	const entry = T[key];
	return entry ? fill(pick(locale, entry) || entry[0], vars) : undefined;
}

export function translate(locale: Locale, key: string, vars?: Vars): string {
	const entry = T[key];
	if (entry) return fill(pick(locale, entry) || entry[0], vars);

	// The Agent's copy: the argument is the English, so English needs no lookup
	// and a missing translation is the English rendered as written. Reading the
	// table in English too is not pointless — it is how a sentence the Agent
	// does say is told apart from a key nothing has a string for.
	const translated = locale === "zh" ? AGENT_COPY[key] : key in AGENT_COPY ? key : undefined;
	if (translated !== undefined) return fill(translated, vars);

	// The block, trigger and connector catalogues, which arrive in their own
	// chunks and may not have arrived yet. Same rule as above: the argument is
	// the English, so a miss here is not a miss — it is the sentence.
	const registry = registryCopy();
	const fromRegistry = locale === "zh" ? registry[key] : key in registry ? key : undefined;
	if (fromRegistry !== undefined) return fill(fromRegistry, vars);

	// Visible in development, harmless in production: showing the key is
	// better than showing nothing, and better than crashing. Only for something
	// shaped like a key — the Agent's untranslated English is not a mistake
	// anybody can act on until its fan-out reaches that screen.
	if (import.meta.env.DEV && KEY_SHAPE.test(key) && NAMESPACES.has(key.slice(0, key.indexOf("."))))
		console.warn(`missing string: ${key}`);
	return fill(key, vars);
}

/** The raw `ONDIT_LOCALE` cookie, or `undefined` when nobody has chosen. */
function cookieLocale(): string | undefined {
	if (typeof document === "undefined") return undefined;
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`));
	return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * One locale for both trees, in one order:
 *
 *   1. the `ONDIT_LOCALE` cookie — a choice somebody made, here or on the
 *      directory site, which is why it is shared across `.ondit.ai`;
 *   2. the signed-in account's stored `user.locale`, which `SessionGate`
 *      applies through `useAccountLocale` once `/me` answers;
 *   3. `navigator.language`;
 *   4. English.
 *
 * The cookie is ahead of the account because it is the later decision *and*
 * because it is readable before the first paint. The account's locale arrives
 * with the session, several hundred milliseconds in; putting it first would
 * mean every screen renders in one language and then swaps.
 */
export function resolveLocale(account?: string | null): Locale {
	if (typeof document === "undefined") return "en";
	const raw = cookieLocale() ?? account ?? navigator.language;
	return raw?.toLowerCase().startsWith("zh") ? "zh" : "en";
}

export function readLocale(): Locale {
	return resolveLocale();
}

export function writeLocale(locale: Locale): void {
	const host = window.location.hostname;
	// Scoped to the parent domain in production so the directory site reads the
	// same choice. One setting, two sites.
	const domain = host.endsWith(".ondit.ai") || host === "ondit.ai" ? "; domain=.ondit.ai" : "";
	// The Cookie Store API cannot set a parent-domain cookie in every browser we
	// support, and sharing this one across ondit.ai and app.ondit.ai is the
	// whole reason it exists.
	// biome-ignore lint/suspicious/noDocumentCookie: parent-domain cookie, see above
	document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; SameSite=Lax${domain}`;
}

export function I18nProvider({
	locale,
	setLocale,
	children,
}: {
	locale: Locale;
	setLocale: (locale: Locale) => void;
	children: ReactNode;
}) {
	// A registry catalogue landing has to re-render what is already mounted, and
	// it changes no prop and no state of its own — the version is the only thing
	// that tells this memo the answers changed.
	const catalogues = useRegistryCopyVersion();
	// biome-ignore lint/correctness/useExhaustiveDependencies: the version is the re-render signal; the body reads the catalogues through translate().
	const value = useMemo<I18n>(
		() => ({ locale, setLocale, t: (key, vars) => translate(locale, key, vars) }),
		[locale, setLocale, catalogues],
	);
	return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useI18n(): I18n {
	const value = useContext(Context);
	if (!value) throw new Error("useI18n outside an I18nProvider");
	return value;
}

export function useT() {
	return useI18n().t;
}

/**
 * Step 2 of the order in `resolveLocale`: adopt the account's stored locale.
 *
 * A hook rather than a resolution step because the viewer is a query — `/me`
 * is not there when the provider first renders. `SessionGate` calls it with
 * `useViewer()`'s answer, once for both modules. The dependency runs from the
 * session to this module and not back: the gate renders its own refusals
 * through `t`, and the provider sits above it.
 *
 * It writes the cookie, so it happens once and never again: a Chinese account
 * that lands here reads Chinese on the directory site too from then on, which
 * is the whole reason that cookie is scoped to the parent domain.
 */
export function useAccountLocale(account: string | null | undefined): void {
	const { setLocale } = useI18n();
	useEffect(() => {
		if (!account || cookieLocale()) return;
		setLocale(account.toLowerCase().startsWith("zh") ? "zh" : "en");
	}, [account, setLocale]);
}

/** Every key, for the parity test. */
export const STRINGS = T;

/** The Agent's copy, for the parity test; `pick`, for the pairs the server sends. */
export { AGENT_COPY, pick };
